import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Badge, Input, SectionTitle, FieldArray, FieldArrayItem, TextArea } from '@tadagram/ui';
import type {
  WorkflowNode,
  AccountSelectorConfig,
  PostToProfileConfig,
  PostToGroupConfig,
  PostToFanpageConfig,
  FanpageCommentOnPostConfig,
  FanpageReplyToCommentsConfig,
  FanpageLikeCommentsConfig,
  FanpageModerateCommentsConfig,
  FanpagePrivateReplyConfig,
  CommentOnPostConfig,
  ReplyToCommentsConfig,
  FindJoinGroupsConfig,
  FindTrendFBConfig,
  FindGroupPostsConfig,
  FindTopPostsConfig,
  AIGenerateAssetConfig,
  AIRemakeImageConfig,
  VaultAssetPreview,
  SeedingContentsManagerConfig,
  SeedingPostEntry,
  FrequencyControl,
  LikePostsConfig,
  LikePostTiktokConfig,
  SharePostTiktokConfig,
  CommentPostTiktokConfig,
  ReplyCommentTiktokConfig,
  LikeCommentTiktokConfig,
  LikePostInstagramConfig,
  RepostPostInstagramConfig,
  CommentPostInstagramConfig,
  CreatePostInstagramConfig,
  FollowInstagramConfig,
  ReplyCommentInstagramConfig,
  ShareURLConfig,
  FindTrendOmnichannelConfig,
  DownloadVideoConfig,
  AnalyzeViralContentConfig,
  AutoRepurposeVideoConfig,
  VaultUploadFileConfig,
} from './types';
import {
  MediaAssetAPI,
  AIWorkflowAPI,
  SeedingContentsAPI,
  SeedingContentItem,
  SeedingContentFolderAPI,
  SeedingContentFolderItem,
} from './api';
import { useAccounts } from './hooks/useAccounts';
import { useWorkflowStore } from './store';

interface NodeConfigPanelProps {
  node: WorkflowNode | null;
  onUpdate: (nodeId: string, config: any) => void;
}

const formatFolderCategory = (folder: SeedingContentFolderItem): string => {
  const rawCategory = typeof folder.category === 'string' ? folder.category.trim() : '';
  if (rawCategory) {
    return rawCategory;
  }

  const rawDescription = typeof folder.description === 'string' ? folder.description.trim() : '';
  if (!rawDescription) {
    return 'general';
  }

  // Support legacy folder naming style where description is prefixed by "category:...".
  const match = rawDescription.match(/^category\s*:\s*([^,;\n]+)/i);
  if (match?.[1]) {
    return match[1].trim();
  }

  return 'general';
};

// ============================================================================
// BRAND INFO SELECTOR - Reusable component for action nodes
// ============================================================================

interface BrandInfoSelectorProps {
  value: string | undefined;
  onChange: (brandInfoNodeId: string | undefined) => void;
}

const BrandInfoSelector: React.FC<BrandInfoSelectorProps> = ({ value, onChange }) => {
  const { t } = useTranslation('workflow');
  const nodes = useWorkflowStore((state) => state.nodes);

  // Find all Brand Info nodes in the workflow
  const brandInfoNodes = React.useMemo(() => {
    return nodes.filter((node) => node.type === 'brand_info');
  }, [nodes]);

  if (brandInfoNodes.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <span className="text-lg">ℹ️</span>
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">{t('noBrandInfoNodes')}</div>
            <div className="text-xs text-amber-700 mt-1">
              {t('noBrandInfoNodesHint')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold">
        {t('brandInfoLabel')} {value && <span className="text-green-600">✓</span>}
      </label>
      <select
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        value={value || ''}
        onChange={(e) => onChange(e.target.value || undefined)}
      >
        <option value="">-- {t('noBrandInfoOptional')} --</option>
        {brandInfoNodes.map((node) => {
          const brandConfig = (node.data as any).brand_info_config || {};
          const brandName = brandConfig.brand_name || t('unnamedBrand');
          const industry = brandConfig.industry;
          return (
            <option key={node.id} value={node.id}>
              {brandName} {industry && `(${industry})`}
            </option>
          );
        })}
      </select>
      {value && (
        <div className="text-xs text-gray-600 bg-green-50 border border-green-200 rounded px-2 py-1">
          🏷️ {t('brandContextHint')}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// SEEDING CONTENTS MANAGER SELECTOR - Reusable component for engagement nodes
// ============================================================================

interface SCMSelectorProps {
  value: string | undefined;
  onChange: (scmNodeId: string | undefined) => void;
}

const SeedingContentsManagerSelector: React.FC<SCMSelectorProps> = ({ value, onChange }) => {
  const { t } = useTranslation('workflow');
  const nodes = useWorkflowStore((state) => state.nodes);
  const scmNodes = React.useMemo(
    () => nodes.filter((n) => n.type === 'seeding_contents_manager'),
    [nodes],
  );

  if (scmNodes.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <span className="text-lg">ℹ️</span>
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">{t('noSCMNodes')}</div>
            <div className="text-xs text-amber-700 mt-1">
              {t('noSCMNodesHint')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold">
        {t('scmLabel')} {value && <span className="text-green-600">✓</span>}
      </label>
      <select
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        value={value || ''}
        onChange={(e) => onChange(e.target.value || undefined)}
      >
        <option value="">{t('selectSCMPlaceholder')}</option>
        {scmNodes.map((n) => {
          const scmCfg = (n.data as any).seeding_contents_manager_config || {};
          return (
            <option key={n.id} value={n.id}>
              {scmCfg.name || n.data.label || t('unnamedSCM')}
            </option>
          );
        })}
      </select>
      {value && (
        <div className="text-xs text-gray-600 bg-green-50 border border-green-200 rounded px-2 py-1">
          🔗 {t('scmRuntimeHint')}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// FIND GROUP POSTS NODE SELECTOR - Reusable component for engagement nodes
// ============================================================================

interface FGPSelectorProps {
  value: string | undefined;
  onChange: (nodeId: string | undefined) => void;
}

const FindGroupPostsSelector: React.FC<FGPSelectorProps> = ({ value, onChange }) => {
  const { t } = useTranslation('workflow');
  const nodes = useWorkflowStore((state) => state.nodes);
  const fgpNodes = React.useMemo(
    () => nodes.filter((n) => n.type === 'find_group_posts'),
    [nodes],
  );

  if (fgpNodes.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <span className="text-lg">ℹ️</span>
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">{t('noFGPNodes')}</div>
            <div className="text-xs text-amber-700 mt-1">{t('noFGPNodesHint')}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold">
        {t('fgpLabel')} {value && <span className="text-green-600">✓</span>}
      </label>
      <select
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        value={value || ''}
        onChange={(e) => onChange(e.target.value || undefined)}
      >
        <option value="">{t('selectFGPPlaceholder')}</option>
        {fgpNodes.map((n) => {
          const nodeData = n.data as { label?: string; find_group_posts_config?: { requirement?: string } };
          const req = nodeData.find_group_posts_config?.requirement;
          const label = req
            ? `${nodeData.label ?? ''} (${req})`
            : nodeData.label || t('unnamedFGP');
          return (
            <option key={n.id} value={n.id}>
              {label}
            </option>
          );
        })}
      </select>
      {value && (
        <div className="text-xs text-gray-600 bg-green-50 border border-green-200 rounded px-2 py-1">
          🔗 {t('fgpRuntimeHint')}
        </div>
      )}
    </div>
  );
};

// ─── FilterPostSelector ───────────────────────────────────────────────────────
interface FilterPostSelectorProps {
  value?: string;
  onChange: (id: string | undefined) => void;
}

const FilterPostSelector: React.FC<FilterPostSelectorProps> = ({ value, onChange }) => {
  const { t } = useTranslation('workflow');
  const nodes = useWorkflowStore((state) => state.nodes);
  const fpNodes = React.useMemo(
    () => nodes.filter((n) => n.type === 'filter_post'),
    [nodes],
  );

  if (fpNodes.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <span className="text-lg">ℹ️</span>
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">{t('noFilterPostNodes')}</div>
            <div className="text-xs text-amber-700 mt-1">{t('noFilterPostNodesHint')}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold">
        {t('filterPostLabel')} {value && <span className="text-green-600">✓</span>}
      </label>
      <select
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        value={value || ''}
        onChange={(e) => onChange(e.target.value || undefined)}
      >
        <option value="">{t('filterPostPlaceholder')}</option>
        {fpNodes.map((n) => {
          const nodeData = n.data as { label?: string; filter_post_config?: { source_type?: string; keyword?: string } };
          const cfg = nodeData.filter_post_config;
          const detail = cfg?.source_type === 'keyword' && cfg?.keyword
            ? cfg.keyword
            : cfg?.source_type === 'my_groups'
              ? 'my_groups'
              : cfg?.source_type || '';
          const label = detail ? `${nodeData.label ?? ''} (${detail})` : nodeData.label || n.id;
          return (
            <option key={n.id} value={n.id}>
              {label}
            </option>
          );
        })}
      </select>
      {value && (
        <div className="text-xs text-gray-600 bg-green-50 border border-green-200 rounded px-2 py-1">
          ⚡ {t('filterPostRuntimeHint')}
        </div>
      )}
    </div>
  );
};

// ─── FindTopPostsSelector ─────────────────────────────────────────────────────
interface FindTopPostsSelectorProps {
  value?: string;
  onChange: (id: string | undefined) => void;
}

const FindTopPostsSelector: React.FC<FindTopPostsSelectorProps> = ({ value, onChange }) => {
  const nodes = useWorkflowStore((state) => state.nodes);
  const ftpNodes = React.useMemo(
    () => nodes.filter((n) => n.type === 'find_top_posts'),
    [nodes],
  );

  if (ftpNodes.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <span className="text-lg">ℹ️</span>
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">Chưa có node Find Top Posts</div>
            <div className="text-xs text-amber-700 mt-1">Thêm một node Find Top Posts vào workflow trước.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold">
        Node Find Top Posts {value && <span className="text-green-600">✓</span>}
      </label>
      <select
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        value={value || ''}
        onChange={(e) => onChange(e.target.value || undefined)}
      >
        <option value="">-- Chọn node Find Top Posts --</option>
        {ftpNodes.map((n) => {
          const nodeData = n.data as { label?: string };
          return (
            <option key={n.id} value={n.id}>
              {nodeData.label || n.id}
            </option>
          );
        })}
      </select>
      {value && (
        <div className="text-xs text-gray-600 bg-green-50 border border-green-200 rounded px-2 py-1">
          ⚡ Sẽ lấy danh sách reactors/commenters từ node Find Top Posts khi chạy
        </div>
      )}
    </div>
  );
};

// ============================================================================
// FACEBOOK READ INSIGHT PANEL
// ============================================================================

const FacebookReadInsightPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const { t } = useTranslation('workflow');
  const rawConfig = node.data.facebook_read_insight_config || node.data.config || {};
  const config = {
    page_ids: rawConfig.page_ids || [],
  };

  const [fanpages, setFanpages] = React.useState<Array<{ id: string; page_id: string; page_name: string; picture_url?: string }>>([]);
  const [loadingPages, setLoadingPages] = React.useState(false);

  React.useEffect(() => {
    setLoadingPages(true);
    import('../../services/api').then(({ apiService }) => {
      apiService.get<{ fanpages: Array<{ id: string; page_id: string; page_name: string; picture_url?: string }> }>('/api/v1/fanpages')
        .then((resp) => { setFanpages(resp.fanpages ?? []); })
        .catch(() => { setFanpages([]); })
        .finally(() => setLoadingPages(false));
    });
  }, []);

  const updateConfig = (updates: any) => {
    onChange({
      ...node.data,
      facebook_read_insight_config: { ...config, ...updates },
    });
  };

  const togglePage = (pageId: string) => {
    const current = config.page_ids || [];
    const updated = current.includes(pageId)
      ? current.filter((id: string) => id !== pageId)
      : [...current, pageId];
    updateConfig({ page_ids: updated });
  };

  return (
    <div className="space-y-3">
      <SectionTitle>Fanpage Read Insights</SectionTitle>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <span className="text-lg">📊</span>
          <div className="flex-1">
            <div className="text-sm font-semibold text-blue-900">Read Page Insights</div>
            <div className="text-xs text-blue-700 mt-1">
              Fetches Page Impressions and Engagements for the last 30 days via Graph API.
            </div>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold mb-1">
          Target Pages <span className="text-gray-400 font-normal text-xs">(empty = all connected pages)</span>
        </label>
        {loadingPages ? (
          <div className="text-xs text-gray-500 py-2">Loading pages…</div>
        ) : fanpages.length === 0 ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            No pages connected yet. Go to Fanpage Management to connect.
          </div>
        ) : (
          <div className="space-y-1">
            {fanpages.map((fp) => {
              const selected = (config.page_ids || []).includes(fp.id);
              return (
                <label
                  key={fp.id}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded border cursor-pointer transition-colors ${selected ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200 hover:border-blue-200'}`}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => togglePage(fp.id)}
                    className="w-4 h-4 accent-blue-500"
                  />
                  {fp.picture_url && (
                    <img src={fp.picture_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                  )}
                  <span className="text-sm text-gray-800">{fp.page_name}</span>
                  <span className="text-xs text-gray-400 ml-auto">{fp.page_id}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export const NodeConfigPanel: React.FC<NodeConfigPanelProps> = ({ node, onUpdate }) => {
  const { t } = useTranslation('workflow');
  if (!node) {
    return (
      <div className="text-center text-gray-500 py-5">
        <p>{t('selectNodeToConfigure')}</p>
      </div>
    );
  }

  const handleConfigChange = (config: any) => {
    onUpdate(node.id, config);
  };

  // QUY CHUẨN: node.type là source of truth (match backend WorkflowNode.Type)
  // KHÔNG check nhiều sources, KHÔNG infer, KHÔNG fallback
  const nodeType = node.type;

  switch (nodeType) {
    // ============ DATA & SELECTORS ============
    case 'brand_info':
      return <BrandInfoPanel node={node} onChange={handleConfigChange} />;
    case 'seeding_contents_manager':
      return <SeedingContentsManagerPanel node={node} onChange={handleConfigChange} />;
    case 'account_selector':
      return <AccountSelectorPanel node={node} onChange={handleConfigChange} />;

    // ============ CONTENT CREATION ============
    case 'post_to_profile':
      return <PostToProfilePanel node={node} onChange={handleConfigChange} />;
    case 'post_to_group':
      return <PostToGroupPanel node={node} onChange={handleConfigChange} />;
    case 'post_to_fanpage':
      return <PostToFanpagePanel node={node} onChange={handleConfigChange} />;
    case 'facebook_read_insight':
      return <FacebookReadInsightPanel node={node} onChange={handleConfigChange} />;
    case 'fanpage_comment_on_post':
      return <FanpageCommentOnPostPanel node={node} onChange={handleConfigChange} />;
    case 'fanpage_reply_to_comments':
      return <FanpageReplyToCommentsPanel node={node} onChange={handleConfigChange} />;
    case 'fanpage_like_comments':
      return <FanpageLikeCommentsPanel node={node} onChange={handleConfigChange} />;
    case 'fanpage_moderate_comments':
      return <FanpageModerateCommentsPanel node={node} onChange={handleConfigChange} />;
    case 'fanpage_private_reply':
      return <FanpagePrivateReplyPanel node={node} onChange={handleConfigChange} />;
    case 'share_url':
      return <ShareURLPanel node={node} onChange={handleConfigChange} />;

    // ============ SEARCH & DISCOVERY ============
    case 'search_groups':
      return <SearchGroupsPanel node={node} onChange={handleConfigChange} />;
    case 'filter_post':
      return <FilterPostPanel node={node} onChange={handleConfigChange} />;
    case 'find_trend_fb':
      return <FindTrendFBPanel node={node} onChange={handleConfigChange} />;
    case 'find_group_posts':
      return <FindGroupPostsPanel node={node} onChange={handleConfigChange} />;
    case 'find_top_posts':
      return <FindTopPostsPanel node={node} onChange={handleConfigChange} />;

    // ============ AI PROCESSING ============
    case 'ai_generate_asset':
      return <AIGenerateAssetPanel node={node} onChange={handleConfigChange} />;
    case 'ai_remake_image':
      return <AIRemakeImagePanel node={node} onChange={handleConfigChange} />;
    case 'ai_grok_remake_image':
      return <AIRemakeImagePanel node={node} onChange={handleConfigChange} />;
    case 'ai_text_process':
      return <AITextProcessPanel node={node} onChange={handleConfigChange} />;

    // ============ JOINING & FOLLOWING ============
    case 'find_join_groups':
      return <FindJoinGroupsPanel node={node} onChange={handleConfigChange} />;
    case 'join_groups':
      return <JoinGroupsPanel node={node} onChange={handleConfigChange} />;
    case 'like_pages':
      return <LikePagesPanel node={node} onChange={handleConfigChange} />;

    // ============ ENGAGEMENT ============
    case 'comment_on_post':
      return <CommentOnPostPanel node={node} onChange={handleConfigChange} />;
    case 'like_posts':
      return <LikePostsPanel node={node} onChange={handleConfigChange} />;
    case 'comment_post_tiktok':
      return <CommentPostTiktokPanel node={node} onChange={handleConfigChange} />;
    case 'reply_comment_tiktok':
      return <ReplyCommentTiktokPanel node={node} onChange={handleConfigChange} />;
    case 'like_comment_tiktok':
      return <LikeCommentTiktokPanel node={node} onChange={handleConfigChange} />;
    case 'like_post_tiktok':
      return <LikePostTiktokPanel node={node} onChange={handleConfigChange} />;
    case 'share_post_tiktok':
      return <SharePostTiktokPanel node={node} onChange={handleConfigChange} />;
    case 'create_post_instagram':
      return <CreatePostInstagramPanel node={node} onChange={handleConfigChange} />;
    case 'like_post_instagram':
      return <LikePostInstagramPanel node={node} onChange={handleConfigChange} />;
    case 'repost_post_instagram':
      return <RepostPostInstagramPanel node={node} onChange={handleConfigChange} />;
    case 'comment_post_instagram':
      return <CommentPostInstagramPanel node={node} onChange={handleConfigChange} />;
    case 'follow_instagram':
      return <FollowInstagramPanel node={node} onChange={handleConfigChange} />;
    case 'reply_comment_instagram':
      return <ReplyCommentInstagramPanel node={node} onChange={handleConfigChange} />;
    case 'create_post_threads':
      return <CreatePostThreadsPanel node={node} onChange={handleConfigChange} />;
    case 'like_post_threads':
      return <LikePostThreadsPanel node={node} onChange={handleConfigChange} />;
    case 'share_post_threads':
      return <SharePostThreadsPanel node={node} onChange={handleConfigChange} />;
    case 'comment_post_threads':
      return <CommentPostThreadsPanel node={node} onChange={handleConfigChange} />;
    case 'follow_threads':
      return <FollowThreadsPanel node={node} onChange={handleConfigChange} />;
    case 'interact':
      return <InteractPanel node={node} onChange={handleConfigChange} />;
    case 'reply_to_comments':
      return <ReplyToCommentsPanel node={node} onChange={handleConfigChange} />;

    // ============ ACCOUNT CARE ============
    case 'view_notifications':
      return <ViewNotificationsPanel node={node} onChange={handleConfigChange} />;
    case 'accept_friend_requests':
      return <AcceptFriendRequestsPanel node={node} onChange={handleConfigChange} />;
    case 'search_add_friend':
      return <SearchAddFriendPanel node={node} onChange={handleConfigChange} />;
    case 'check_message':
      return <CheckMessagePanel node={node} onChange={handleConfigChange} />;
    case 'send_direct_message':
      return <SendDirectMessagePanel node={node} onChange={handleConfigChange} />;
    case 'post_auto_responder':
      return <PostAutoResponderPanel node={node} onChange={handleConfigChange} />;
    case 'group_random_messenger':
      return <GroupRandomMessengerPanel node={node} onChange={handleConfigChange} />;
    case 'linkedin_connect_message':
      return <LinkedinConnectMessagePanel node={node} onChange={handleConfigChange} />;
    case 'linkedin_join_group':
      return <LinkedinJoinGroupPanel node={node} onChange={handleConfigChange} />;

    // ============ WITH ZALO ============
    case 'zalo_add_friend':
      return <ZaloAddFriendPanel node={node} onChange={handleConfigChange} />;
    case 'zalo_join_group':
      return <ZaloJoinGroupPanel node={node} onChange={handleConfigChange} />;
    case 'zalo_group_members':
      return <ZaloGroupMembersPanel node={node} onChange={handleConfigChange} />;
    case 'zalo_proactive_sender':
      return <ZaloProactiveSenderPanel node={node} onChange={handleConfigChange} />;

    // ============ VIDEO PRODUCTION & TRENDS ============
    case 'find_trend_omnichannel':
      return <FindTrendOmnichannelPanel node={node} onChange={handleConfigChange} />;
    case 'download_video':
      return <DownloadVideoPanel node={node} onChange={handleConfigChange} />;
    case 'analyze_viral_content':
      return <AnalyzeViralContentPanel node={node} onChange={handleConfigChange} />;
    case 'auto_repurpose_video':
      return <AutoRepurposeVideoPanel node={node} onChange={handleConfigChange} />;
    case 'vault_upload_file':
      return <VaultUploadFilePanel node={node} onChange={handleConfigChange} />;

    // ============ CONTROL NODES ============
    case 'start':
    case 'end':
      return (
        <div className="text-center text-gray-500 py-5">
          <p>{t('noConfig')}</p>
        </div>
      );

    default:
      return (
        <div className="text-center text-gray-500 py-5">
          <p>{t('unknownNodeType', { type: nodeType })}</p>
          <p className="text-xs mt-2">{t('unknownNodeTypeHint')}</p>
        </div>
      );
  }
};

// ============================================================================
// ACCOUNT SELECTOR PANEL
// ============================================================================

const AccountSelectorPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const { t } = useTranslation('workflow');
  const rawConfig =
    node.data.account_selector_config || node.data.account_selector || node.data.config || {};

  const config = {
    platform: rawConfig.platform || 'facebook',
    selected_accounts: rawConfig.selected_accounts || [],
    selected_fanpages: rawConfig.selected_fanpages || [],
    mode: rawConfig.mode || 'specific',
    account_fanpage_map: (rawConfig.account_fanpage_map || {}) as Record<string, string>,
    masterTimeline: (() => {
      const mt = rawConfig.masterTimeline || {
        activeHours: { start: '09:00', end: '22:00' },
        weekdays: [1, 2, 3, 4, 5, 6, 0],
        baseFrequency: 3,
      };
      // Seed timeWindows from activeHours if timeWindows is empty
      if ((!mt.timeWindows || mt.timeWindows.length === 0) && mt.activeHours) {
        mt.timeWindows = [{ start: mt.activeHours.start, end: mt.activeHours.end }];
      }
      return mt;
    })(),
  };

  const { accounts, loading } = useAccounts(config.platform);
  const [fanpages, setFanpages] = React.useState<Array<{ id: string; page_name: string; page_id: string; picture_url?: string }>>([]);
  const [loadingFanpages, setLoadingFanpages] = React.useState(false);

  React.useEffect(() => {
    if (config.platform !== 'facebook') {
      setFanpages([]);
      return;
    }
    setLoadingFanpages(true);
    import('../../services/api').then(({ apiService }) => {
      apiService.get<{ fanpages: Array<{ id: string; page_id: string; page_name: string; picture_url?: string }> }>('/api/v1/fanpages')
        .then((resp) => { setFanpages(resp.fanpages ?? []); })
        .catch(() => { setFanpages([]); })
        .finally(() => setLoadingFanpages(false));
    });
  }, [config.platform]);

  // 🔥 CRITICAL FIX: Filter out ghost accounts (deleted from DB but still in workflow)
  const validSelectedAccounts = React.useMemo(() => {
    if (!accounts || accounts.length === 0) return config.selected_accounts;
    const validAccountIds = new Set(accounts.map(acc => acc.account_id));
    return config.selected_accounts.filter(id => validAccountIds.has(id));
  }, [accounts, config.selected_accounts]);

  const handlePlatformChange = (platform: string) => {
    // Keep selected_accounts - don't reset when switching platform
    // This preserves selections when user switches back
    onChange({
      ...node.data,
      account_selector_config: {
        ...config,
        platform,
        // Don't reset selected_accounts - preserve cross-platform selections
        selected_accounts: validSelectedAccounts,
      },
    });
  };

  const handleAccountToggle = (accountId: string) => {
    const newSelected = validSelectedAccounts.includes(accountId)
      ? validSelectedAccounts.filter((id) => id !== accountId)
      : [...validSelectedAccounts, accountId];

    // Clear fanpage map entry when account is deselected
    const newFanpageMap = { ...config.account_fanpage_map };
    if (!newSelected.includes(accountId)) {
      delete newFanpageMap[accountId];
    }

    onChange({
      ...node.data,
      account_selector_config: {
        ...config,
        selected_accounts: newSelected,
        account_fanpage_map: newFanpageMap,
      },
    });
  };

  const handleAccountFanpageSelect = (accountId: string, fanpageUrl: string) => {
    const newFanpageMap = { ...config.account_fanpage_map };
    if (fanpageUrl) {
      newFanpageMap[accountId] = fanpageUrl;
    } else {
      delete newFanpageMap[accountId];
    }
    onChange({
      ...node.data,
      account_selector_config: {
        ...config,
        account_fanpage_map: newFanpageMap,
      },
    });
  };

  const handleFanpageToggle = (fanpageId: string) => {
    const current = config.selected_fanpages || [];
    const next = current.includes(fanpageId)
      ? current.filter((id: string) => id !== fanpageId)
      : [...current, fanpageId];

    onChange({
      ...node.data,
      account_selector_config: {
        ...config,
        selected_fanpages: next,
      },
    });
  };

  // Filter accounts to only show current platform
  // This prevents showing Facebook accounts when Instagram is selected
  const filteredAccounts = accounts.filter(acc => acc.platform === config.platform);

  const handleMasterTimelineChange = (field: string, value: any) => {
    const updated: any = {
      ...config.masterTimeline,
      [field]: value,
    };

    // Auto-sync activeHours from timeWindows whenever timeWindows change
    if (field === 'timeWindows') {
      const windows = value as { start: string; end: string }[];
      if (windows && windows.length > 0) {
        const starts = windows.map((w) => w.start).sort();
        const ends = windows.map((w) => w.end).sort();
        updated.activeHours = {
          start: starts[0],
          end: ends[ends.length - 1],
        };
      }
    }

    onChange({
      ...node.data,
      account_selector_config: {
        ...config,
        masterTimeline: updated,
      },
    });
  };

  const handleWeekdayToggle = (day: number) => {
    const newWeekdays = config.masterTimeline.weekdays.includes(day)
      ? config.masterTimeline.weekdays.filter((d) => d !== day)
      : [...config.masterTimeline.weekdays, day];

    handleMasterTimelineChange('weekdays', newWeekdays);
  };

  const handleAddTimeWindow = () => {
    const timeWindows = config.masterTimeline.timeWindows || [];
    handleMasterTimelineChange('timeWindows', [
      ...timeWindows,
      { start: '09:00', end: '17:00' }
    ]);
  };

  const handleRemoveTimeWindow = (index: number) => {
    const timeWindows = config.masterTimeline.timeWindows || [];
    handleMasterTimelineChange('timeWindows', timeWindows.filter((_: any, i: number) => i !== index));
  };

  const handleTimeWindowChange = (index: number, field: 'start' | 'end', value: string) => {
    const timeWindows = config.masterTimeline.timeWindows || [];
    const updated = [...timeWindows];
    updated[index] = { ...updated[index], [field]: value };
    handleMasterTimelineChange('timeWindows', updated);
  };

  const weekdayLabels = [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')];

  return (
    <div className="space-y-4">
      <SectionTitle>{t('selectAccountsTitle')}</SectionTitle>

      <div>
        <label className="block text-sm mb-1">{t('platformLabel')}</label>
        <select
          className="w-full px-2 py-1 border rounded"
          value={config.platform}
          onChange={(e) => handlePlatformChange(e.target.value)}
        >
          <option value="facebook">Facebook</option>
          <option value="instagram">Instagram</option>
          <option value="tiktok">TikTok</option>
          <option value="zalo">Zalo</option>
          <option value="linkedin">LinkedIn</option>
        </select>
      </div>

      <div>
        <label className="block text-sm mb-1">
          {t('accountsSelected', { count: filteredAccounts.filter(a => validSelectedAccounts.includes(a.account_id)).length })}
        </label>
        {loading ? (
          <div className="text-gray-500 text-sm">{t('loading')}</div>
        ) : accounts.length === 0 ? (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <span className="text-lg">⚠️</span>
              <div className="flex-1">
                <div className="text-sm font-semibold text-amber-900">{t('noAccountsReminder')}</div>
                <div className="text-xs text-amber-700 mt-1">{t('noAccountsReminderHint')}</div>
              </div>
            </div>
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div className="text-gray-500 text-sm">{t('noAccountsFound', { platform: config.platform })}</div>
        ) : (
          <div style={{ maxHeight: '250px', overflowY: 'auto' }} className="space-y-1">
            {filteredAccounts.map((account) => {
              const isChecked = validSelectedAccounts.includes(account.account_id);
              const fanpageURLs: string[] = (account as any).managed_fanpage_urls || [];
              return (
                <div key={account.account_id}>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleAccountToggle(account.account_id)}
                    />
                    <span className="text-sm">{account.name}</span>
                  </label>
                  {isChecked && config.platform === 'facebook' && fanpageURLs.length > 0 && (
                    <div className="ml-6 mt-1 mb-1">
                      <select
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        value={config.account_fanpage_map[account.account_id] || ''}
                        onChange={(e) => handleAccountFanpageSelect(account.account_id, e.target.value)}
                      >
                        <option value="">— Chạy với tài khoản cá nhân —</option>
                        {fanpageURLs.map((url) => (
                          <option key={url} value={url}>{url}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {config.platform === 'facebook' && (
        <div>
          <label className="block text-sm mb-1">
            {t('fanpagesSelected', 'Fanpages selected')}: {(config.selected_fanpages || []).length}
          </label>
          {loadingFanpages ? (
            <div className="text-gray-500 text-sm">{t('loading')}</div>
          ) : fanpages.length === 0 ? (
            <div className="text-gray-500 text-sm">{t('noFanpagesConnected', 'No pages connected yet. Go to Fanpage Management to connect.')}</div>
          ) : (
            <div style={{ maxHeight: '180px', overflowY: 'auto' }} className="space-y-1">
              {fanpages.map((fp) => (
                <label key={fp.id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={(config.selected_fanpages || []).includes(fp.id)}
                    onChange={() => handleFanpageToggle(fp.id)}
                  />
                  {fp.picture_url && (
                    <img src={fp.picture_url} alt="" className="w-5 h-5 rounded-full object-cover" />
                  )}
                  <span className="text-sm">{fp.page_name}</span>
                  <span className="text-xs text-gray-400 ml-auto">{fp.page_id}</span>
                </label>
              ))}
            </div>
          )}
          <p className="text-xs text-gray-500 mt-1">
            {t('fanpageScopeHint', 'If empty, fanpage nodes can use all pages assigned to this worker.')}
          </p>
        </div>
      )}

      {/* Master Timeline Configuration */}
      <div className="border-t pt-4 mt-4">
        <SectionTitle>{t('masterTimeline')}</SectionTitle>
        <p className="text-xs text-gray-500 mb-3">
          {t('masterTimelineHint')}
        </p>

        {/* Active Hours - Multiple Time Windows */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-semibold">{t('activeHours')}</label>
            {(config.masterTimeline.timeWindows || []).length === 0 && (
              <Button
                variant="ghost-sm"
                size="xs"
                onClick={handleAddTimeWindow}
              >
                {t('addButton')}
              </Button>
            )}
          </div>

          {/* Time Windows List */}
          {(config.masterTimeline.timeWindows || []).length > 0 ? (
            <div className="space-y-2">
              {(config.masterTimeline.timeWindows || []).map((window: any, index: number) => (
                <div key={index} className="flex gap-2 items-center">
                  <input
                    type="time"
                    className="flex-1 px-2 py-1 border rounded text-sm"
                    value={window.start}
                    onChange={(e) => handleTimeWindowChange(index, 'start', e.target.value)}
                  />
                  <span className="text-sm">{t('timeTo')}</span>
                  <input
                    type="time"
                    className="flex-1 px-2 py-1 border rounded text-sm"
                    value={window.end}
                    onChange={(e) => handleTimeWindowChange(index, 'end', e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveTimeWindow(index)}
                    className="text-red-500 hover:text-red-700 px-2"
                    title={t('removeButton')}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-gray-500 italic mb-2">
              {t('noTimeWindows')}
            </div>
          )}

          <p className="text-xs text-gray-500 mt-2">{t('timeWindowHint')}</p>
        </div>

        {/* Weekdays */}
        <div className="mb-3">
          <label className="block text-sm font-semibold mb-2">{t('activeDays')}</label>
          <div className="flex gap-1">
            {weekdayLabels.map((label, index) => (
              <button
                key={index}
                type="button"
                className={`px-2 py-1 text-xs rounded border transition-colors ${config.masterTimeline.weekdays.includes(index)
                  ? 'bg-tg-success text-white border-tg-success'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                onClick={() => handleWeekdayToggle(index)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Base Frequency */}
        <div className="mb-3">
          <label className="block text-sm font-semibold mb-2">
            {t('baseFrequency')}
          </label>
          <input
            type="number"
            min="1"
            max="10"
            className="w-full px-2 py-1 border rounded"
            value={config.masterTimeline.baseFrequency}
            onChange={(e) =>
              handleMasterTimelineChange('baseFrequency', parseInt(e.target.value) || 1)
            }
          />
          <p className="text-xs text-gray-500 mt-1">
            {t('baseFrequencyHint')}
          </p>
        </div>

        {/* Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
          <p className="text-sm font-semibold text-blue-900 mb-1">{t('currentSchedule')}</p>
          {(config.masterTimeline.timeWindows || []).length > 0 ? (
            (config.masterTimeline.timeWindows as any[]).map((w: any, i: number) => (
              <p key={i} className="text-blue-700">
                • {t('activeTime', { start: w.start, end: w.end })}
              </p>
            ))
          ) : (
            <p className="text-blue-700 text-xs italic">• {t('noTimeWindows')}</p>
          )}
          <p className="text-blue-700">
            • {config.masterTimeline.weekdays.length === 7 ? t('everyDay') : t('daysPerWeek', { count: config.masterTimeline.weekdays.length })}
          </p>
          <p className="text-blue-700">
            • {t('baseDisplay', { frequency: config.masterTimeline.baseFrequency })}
          </p>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// POST TO PROFILE PANEL
// ============================================================================

// ============================================================================
// VAULT ASSET PICKER - Reusable component for image source selection
// ============================================================================

interface VaultAssetPickerProps {
  mediaType?: 'image' | 'video';
  selectedId?: string;
  onSelect: (assetId: string) => void;
}

const VaultAssetPicker: React.FC<VaultAssetPickerProps> = ({
  mediaType = 'image',
  selectedId,
  onSelect,
}) => {
  const { t } = useTranslation('workflow');
  const [assets, setAssets] = useState<VaultAssetPreview[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    MediaAssetAPI.listAssets({ media_type: mediaType, page_size: 24 })
      .then((res) => setAssets(res.assets ?? []))
      .catch(() => setErr(t('vaultLoadError')))
      .finally(() => setLoading(false));
  }, [mediaType]);

  if (loading)
    return <div className="text-xs text-gray-500 py-2">{t('vaultLoading')}</div>;
  if (err)
    return <div className="text-xs text-red-500 py-2">{err}</div>;
  if (assets.length === 0)
    return (
      <div className="text-xs text-gray-500 py-2 italic">
        {t('vaultNoAssets', { mediaType })}
      </div>
    );

  return (
    <div className="grid grid-cols-3 gap-2 max-h-52 overflow-y-auto mt-1 pr-1">
      {assets.map((asset) => (
        <button
          key={asset.id}
          type="button"
          title={asset.title || asset.asset_name}
          className={`relative rounded border-2 overflow-hidden transition-all ${selectedId === asset.id
            ? 'border-blue-500 ring-2 ring-blue-200'
            : 'border-gray-200 hover:border-gray-400'
            }`}
          style={{ aspectRatio: '1 / 1' }}
          onClick={() => onSelect(asset.id)}
        >
          {/* Gradient fallback — always rendered behind <img>, revealed on error */}
          <div
            className="absolute inset-0 flex items-center justify-center text-gray-300 text-2xl bg-gradient-to-br from-purple-50 to-indigo-50"
            aria-hidden="true"
          >
            {asset.media_type === 'video' ? '🎬' : '🖼'}
          </div>
          {/* Thumbnail: inline base64 data-URI (zero HTTP) when available, else
              absolute URL to the public /thumbnail endpoint on the marketing backend.
              onError hides img, revealing the gradient fallback above. */}
          <img
            src={MediaAssetAPI.thumbnailUrl(asset)}
            alt={asset.title || asset.asset_name}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
          {selectedId === asset.id && (
            <span className="absolute top-0.5 right-0.5 bg-blue-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] leading-none z-10">
              ✓
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

// ============================================================================
// POST TO PROFILE PANEL
// ============================================================================

const PostToProfilePanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.post_to_profile_config || node.data.postToProfileConfig || node.data.config || {};

  const config = {
    content_type: rawConfig.content_type || 'text',
    has_image: rawConfig.has_image || false,
    content_hints: rawConfig.content_hints || '',
    link_url: rawConfig.link_url || '',
    brand_info_node_id: rawConfig.brand_info_node_id,
    image_source_type: (rawConfig.image_source_type || 'none') as 'none' | 'context' | 'vault_asset' | 'vault_folder' | 'character_context',
    image_asset_id: rawConfig.image_asset_id || '',
    vault_folder_id: rawConfig.vault_folder_id || '',
    add_to_seeding_contents: rawConfig.add_to_seeding_contents || false,
    seeding_folder_ids: (rawConfig.seeding_folder_ids as string[]) || [],
    character_id: rawConfig.character_id || '',
  };

  const { t } = useTranslation('workflow');
  const [seedingFolders, setSeedingFolders] = useState<SeedingContentFolderItem[]>([]);
  const [seedingFoldersLoading, setSeedingFoldersLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    setSeedingFoldersLoading(true);
    SeedingContentFolderAPI.listAll(1, 100)
      .then((res) => { if (mounted) setSeedingFolders(res.folders || []); })
      .catch(() => { if (mounted) setSeedingFolders([]); })
      .finally(() => { if (mounted) setSeedingFoldersLoading(false); });
    return () => { mounted = false; };
  }, []);

  const updateConfig = (updates: Partial<PostToProfileConfig>) => {
    onChange({
      ...node.data,
      post_to_profile_config: {
        ...config,
        ...updates,
      },
    });
  };

  return (
    <div className="space-y-3">
      <SectionTitle>{t('postToProfile')}</SectionTitle>

      {/* Master Timeline Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <span className="text-lg">📅</span>
          <div className="flex-1">
            <div className="text-sm font-semibold text-blue-900">{t('timingBanner')}</div>
            <div className="text-xs text-blue-700 mt-1">
              {t('timingBannerHint')}
            </div>
          </div>
        </div>
      </div>

      {/* Brand Info Selector */}
      <BrandInfoSelector
        value={config.brand_info_node_id}
        onChange={(brandInfoNodeId) => updateConfig({ brand_info_node_id: brandInfoNodeId })}
      />

      <div>
        <label className="block text-sm mb-1">{t('contentType')}</label>
        <select
          className="w-full px-2 py-1 border rounded"
          value={config.content_type}
          onChange={(e) => updateConfig({ content_type: e.target.value as any })}
        >
          <option value="text">{t('textOnly')}</option>
          <option value="image">{t('imageOnly')}</option>
          <option value="both">{t('textAndImage')}</option>
        </select>
      </div>

      {/* Image Source */}
      <div>
        <label className="block text-sm font-medium mb-1.5">{t('imageAttachment')}</label>
        <select
          className="w-full px-2 py-1 border rounded text-sm"
          value={config.image_source_type}
          onChange={(e) =>
            updateConfig({
              image_source_type: e.target.value as any,
              image_asset_id: e.target.value !== 'vault_asset' ? '' : config.image_asset_id,
              vault_folder_id: e.target.value !== 'vault_folder' ? '' : config.vault_folder_id,
            })
          }
        >
          <option value="none">{t('noImage')}</option>
          <option value="context">{t('fromPreviousNode')}</option>
          <option value="character_context">{t('characterContext', 'Character Context (Image2Image)')}</option>
          <option value="vault_asset">{t('pickFromVault')}</option>
          <option value="vault_folder">{t('pickFromVaultFolder', 'Vault Folder (Sequential/Random)')}</option>
        </select>

        {config.image_source_type === 'context' && (
          <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
            {t('contextImageHint')}
          </div>
        )}

        {config.image_source_type === 'vault_asset' && (
          <div className="mt-2">
            <div className="text-xs text-gray-600 mb-1">
              {t('selectImageFromVault')}
            </div>
            <VaultAssetPicker
              mediaType="image"
              selectedId={config.image_asset_id || undefined}
              onSelect={(id) => updateConfig({ image_asset_id: id })}
            />
            {config.image_asset_id && (
              <p className="text-xs text-gray-500 mt-1 truncate">
                {t('selectedImage', { id: config.image_asset_id })}
              </p>
            )}
          </div>
        )}

        {config.image_source_type === 'vault_folder' && (
          <div className="mt-2">
            <label className="block text-xs text-gray-600 mb-1">
              {t('selectVaultFolder', 'Select Folder')}
            </label>
            {seedingFoldersLoading ? (
              <p className="text-xs text-purple-500">Loading folders...</p>
            ) : seedingFolders.length === 0 ? (
              <p className="text-xs text-purple-400">No folders found</p>
            ) : (
              <select
                className="w-full px-2 py-1 border rounded text-sm"
                value={config.vault_folder_id || ''}
                onChange={(e) => updateConfig({ vault_folder_id: e.target.value })}
              >
                <option value="" disabled>-- {t('selectVaultFolder', 'Select Folder')} --</option>
                {seedingFolders.map((folder) => (
                  <option key={folder._id} value={folder._id}>
                    {folder.name} {folder.is_default ? '(default)' : ''}
                  </option>
                ))}
              </select>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {t('vaultFolderHint', 'Assets will be posted one by one without duplication.')}
            </p>
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm mb-1">{t('contentHints')}</label>
        <textarea
          className="w-full px-2 py-1 border rounded text-sm"
          rows={3}
          placeholder={t('contentHintsPlaceholder')}
          value={config.content_hints}
          onChange={(e) => updateConfig({ content_hints: e.target.value })}
        />
      </div>

      <div>
        <label className="block text-sm mb-1">{t('linkUrl', 'Link URL')} <span className="text-gray-400 text-xs">({t('optional', 'optional')})</span></label>
        <Input
          type="url"
          placeholder="https://example.com"
          value={config.link_url || ''}
          onChange={(e) => updateConfig({ link_url: e.target.value })}
        />
      </div>

      <div>
        <label className="block text-sm mb-1">{t('characterId', 'Nhân vật đại diện (Character ID)')} <span className="text-gray-400 text-xs">({t('optional', 'optional')})</span></label>
        <Input
          type="text"
          placeholder="ObjectID nhân vật Brand Character..."
          value={config.character_id || ''}
          onChange={(e) => updateConfig({ character_id: e.target.value })}
        />
      </div>

      {/* Seeding Contents Registration */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={config.add_to_seeding_contents}
            onChange={(e) => updateConfig({ add_to_seeding_contents: e.target.checked })}
            className="w-4 h-4"
          />
          <span className="text-sm font-semibold text-purple-900">{t('addToSeedingContents')}</span>
        </label>
        {config.add_to_seeding_contents && (
          <div className="mt-2 ml-6 space-y-2">
            <p className="text-xs text-purple-700">{t('addToSeedingHint')}</p>
            <div>
              <p className="text-xs font-semibold text-purple-800 mb-1">{t('seedingTargetFolders', 'Target Folders (optional)')}</p>
              {seedingFoldersLoading ? (
                <p className="text-xs text-purple-500">Loading folders...</p>
              ) : seedingFolders.length === 0 ? (
                <p className="text-xs text-purple-400">{t('noSeedingFolders', 'No folders found — posts go to default pool')}</p>
              ) : (
                <div className="space-y-1 max-h-36 overflow-y-auto border border-purple-200 rounded p-2 bg-white">
                  {seedingFolders.map((folder) => {
                    const selected = (config.seeding_folder_ids || []).includes(folder._id);
                    return (
                      <label key={folder._id} className="flex items-center gap-2 cursor-pointer hover:bg-purple-50 rounded px-1">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => {
                            const current = config.seeding_folder_ids || [];
                            const updated = e.target.checked
                              ? [...current, folder._id]
                              : current.filter((id) => id !== folder._id);
                            updateConfig({ seeding_folder_ids: updated });
                          }}
                          className="w-3 h-3"
                        />
                        <span className="text-xs text-purple-900">{folder.name}</span>
                        <span className="text-xs text-purple-500">[{formatFolderCategory(folder)}]</span>
                        {folder.is_default && <span className="text-xs text-purple-400">(default)</span>}
                      </label>
                    );
                  })}
                </div>
              )}
              {(config.seeding_folder_ids || []).length === 0 && seedingFolders.length > 0 && (
                <p className="text-xs text-purple-400 mt-1">{t('noFolderSelected', 'None selected → uses default pool')}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// COMMENT ON POST PANEL
// ============================================================================

const CommentOnPostPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.comment_on_post_config || node.data.commentOnPostConfig || node.data.config || {};

  // Ensure all required fields exist with defaults
  const config: CommentOnPostConfig = {
    post_url: rawConfig.post_url || '',
    comment_mode: rawConfig.comment_mode || 'discussion',
    seeding_mode: rawConfig.seeding_mode || '',
    content_hints: rawConfig.content_hints || '',
    link_url: rawConfig.link_url || '',
    brand_info_node_id: rawConfig.brand_info_node_id,
    dispatch_mode: (rawConfig.dispatch_mode || 'specific') as 'specific' | 'managed' | 'group_posts' | 'filter_posts' | 'from_top_posts',
    seeding_contents_manager_node_id: rawConfig.seeding_contents_manager_node_id,
    find_group_posts_node_id: rawConfig.find_group_posts_node_id,
    filter_post_node_id: rawConfig.filter_post_node_id,
    find_top_posts_node_id: rawConfig.find_top_posts_node_id,
    tag_names: rawConfig.tag_names || [],
    hashtags: rawConfig.hashtags || [],
  };

  const updateConfig = (updates: Partial<CommentOnPostConfig>) => {
    onChange({
      ...node.data,
      comment_on_post_config: {
        ...config,
        ...updates,
      },
    });
  };

  const isManaged = config.dispatch_mode === 'managed';
  const isGroupPosts = config.dispatch_mode === 'group_posts';
  const isFilterPosts = config.dispatch_mode === 'filter_posts';
  const isFromTopPosts = config.dispatch_mode === 'from_top_posts';
  const isSeeding = config.comment_mode === 'seeding';
  const { t } = useTranslation('workflow');
  const [tagInput, setTagInput] = React.useState('');
  const [hashtagInput, setHashtagInput] = React.useState('');

  const addTag = () => {
    const name = tagInput.trim().replace(/^@/, '');
    if (name && !(config.tag_names || []).includes(name)) {
      updateConfig({ tag_names: [...(config.tag_names || []), name] });
    }
    setTagInput('');
  };
  const removeTag = (name: string) => updateConfig({ tag_names: (config.tag_names || []).filter(t => t !== name) });

  const addHashtag = () => {
    const tag = hashtagInput.trim().replace(/^#/, '');
    if (tag && !(config.hashtags || []).includes(tag)) {
      updateConfig({ hashtags: [...(config.hashtags || []), tag] });
    }
    setHashtagInput('');
  };
  const removeHashtag = (tag: string) => updateConfig({ hashtags: (config.hashtags || []).filter(h => h !== tag) });

  return (
    <div className="space-y-3">
      <SectionTitle>{t('commentOnPost')}</SectionTitle>

      {/* Master Timeline Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <span className="text-lg">📅</span>
          <div className="flex-1">
            <div className="text-sm font-semibold text-blue-900">{t('timingBanner')}</div>
            <div className="text-xs text-blue-700 mt-1">
              {t('timingBannerHint')}
            </div>
          </div>
        </div>
      </div>

      {/* Dispatch Mode */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">{t('postUrlSource')}</label>
        <div className="flex gap-3 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`dispatch-mode-${node.id}`}
              value="specific"
              checked={!isManaged && !isGroupPosts && !isFilterPosts && !isFromTopPosts}
              onChange={() => updateConfig({ dispatch_mode: 'specific', seeding_contents_manager_node_id: undefined, find_group_posts_node_id: undefined, filter_post_node_id: undefined, find_top_posts_node_id: undefined })}
            />
            <span className="text-sm">{t('specificUrl')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`dispatch-mode-${node.id}`}
              value="managed"
              checked={isManaged}
              onChange={() => updateConfig({ dispatch_mode: 'managed', post_url: '', find_group_posts_node_id: undefined, filter_post_node_id: undefined, find_top_posts_node_id: undefined })}
            />
            <span className="text-sm">{t('managedMode')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`dispatch-mode-${node.id}`}
              value="group_posts"
              checked={isGroupPosts}
              onChange={() => updateConfig({ dispatch_mode: 'group_posts', post_url: '', seeding_contents_manager_node_id: undefined, filter_post_node_id: undefined, find_top_posts_node_id: undefined })}
            />
            <span className="text-sm">{t('groupPostsMode')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`dispatch-mode-${node.id}`}
              value="filter_posts"
              checked={isFilterPosts}
              onChange={() => updateConfig({ dispatch_mode: 'filter_posts', post_url: '', seeding_contents_manager_node_id: undefined, find_group_posts_node_id: undefined, find_top_posts_node_id: undefined })}
            />
            <span className="text-sm">{t('filterPostsMode')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`dispatch-mode-${node.id}`}
              value="from_top_posts"
              checked={isFromTopPosts}
              onChange={() => updateConfig({ dispatch_mode: 'from_top_posts', post_url: '', seeding_contents_manager_node_id: undefined, find_group_posts_node_id: undefined, filter_post_node_id: undefined })}
            />
            <span className="text-sm">{t('fromTopPostsMode')}</span>
          </label>
        </div>
        {isManaged && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
            {t('managedModeHint')}
          </div>
        )}
        {isGroupPosts && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
            {t('groupPostsModeHint')}
          </div>
        )}
        {isFilterPosts && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
            {t('filterPostsModeHint')}
          </div>
        )}
        {isFromTopPosts && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
            {t('fromTopPostsModeHint')}
          </div>
        )}
      </div>

      {/* Brand Info Selector */}
      <BrandInfoSelector
        value={config.brand_info_node_id}
        onChange={(brandInfoNodeId) => updateConfig({ brand_info_node_id: brandInfoNodeId })}
      />

      {/* SCM Selector (managed mode) */}
      {isManaged && (
        <SeedingContentsManagerSelector
          value={config.seeding_contents_manager_node_id}
          onChange={(id) => updateConfig({ seeding_contents_manager_node_id: id })}
        />
      )}

      {/* Find Group Posts Selector (group_posts mode) */}
      {isGroupPosts && (
        <FindGroupPostsSelector
          value={config.find_group_posts_node_id}
          onChange={(id) => updateConfig({ find_group_posts_node_id: id })}
        />
      )}

      {/* Filter Post Selector (filter_posts mode) */}
      {isFilterPosts && (
        <FilterPostSelector
          value={config.filter_post_node_id}
          onChange={(id) => updateConfig({ filter_post_node_id: id })}
        />
      )}

      {/* Find Top Posts Selector (from_top_posts mode) */}
      {isFromTopPosts && (
        <FindTopPostsSelector
          value={config.find_top_posts_node_id}
          onChange={(id) => updateConfig({ find_top_posts_node_id: id })}
        />
      )}

      {/* Post URL (specific mode only) */}
      {!isManaged && !isGroupPosts && !isFilterPosts && !isFromTopPosts && (
        <Input
          label="Post URL"
          type="url"
          placeholder="https://facebook.com/groups/xxx/posts/yyy"
          value={config.post_url}
          onChange={(e) => updateConfig({ post_url: e.target.value })}
        />
      )}

      {/* Comment Mode */}
      <div>
        <label className="block text-sm font-semibold mb-1">{t('commentMode')}</label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${!isSeeding
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
              }`}
            onClick={() => updateConfig({ comment_mode: 'discussion', seeding_mode: '' })}
          >
            {t('discussionMode')}
          </button>
          <button
            type="button"
            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${isSeeding
              ? 'bg-orange-500 text-white border-orange-500'
              : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'
              }`}
            onClick={() => updateConfig({ comment_mode: 'seeding' })}
          >
            {t('seedingMode')}
          </button>
        </div>
        {!isSeeding && (
          <div className="mt-2 text-xs text-gray-500 bg-gray-50 rounded px-2 py-1">
            {t('discussionHint')}
          </div>
        )}
        {isSeeding && (
          <div className="mt-2 text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded px-2 py-1">
            {t('seedingHint')}
          </div>
        )}
      </div>

      {/* Seeding Mode — only when comment_mode = seeding */}
      {isSeeding && (
        <div>
          <label className="block text-sm font-semibold mb-1">
            {t('seedingIntent')} <span className="text-gray-400 font-normal">{t('emptyForRandom')}</span>
          </label>
          <select
            className="w-full px-2 py-2 border rounded text-sm"
            value={config.seeding_mode || ''}
            onChange={(e) => updateConfig({ seeding_mode: e.target.value as any })}
          >
            <option value="">{t('seedingRandom')}</option>
            <option value="ask_info">{t('seedingAskInfo')}</option>
            <option value="interest">{t('seedingInterest')}</option>
            <option value="inbox_cta">{t('seedingInboxCTA')}</option>
            <option value="social_proof">{t('seedingSocialProof')}</option>
            <option value="close">{t('seedingClose')}</option>
          </select>
        </div>
      )}

      <div>
        <label className="block text-sm mb-1">
          {isSeeding ? t('contentGuidanceSeeding') : t('contentGuidanceDiscussion')}
        </label>
        <textarea
          className="w-full px-2 py-1 border rounded text-sm"
          rows={3}
          placeholder={
            isSeeding
              ? t('contentGuidanceSeedingPlaceholder')
              : t('contentGuidanceDiscussionPlaceholder')
          }
          value={config.content_hints}
          onChange={(e) => updateConfig({ content_hints: e.target.value })}
        />
      </div>

      <div>
        <label className="block text-sm mb-1">{t('linkUrl', 'Link URL')} <span className="text-gray-400 text-xs">({t('optional', 'optional')})</span></label>
        <Input
          type="url"
          placeholder="https://example.com"
          value={config.link_url || ''}
          onChange={(e) => updateConfig({ link_url: e.target.value })}
        />
      </div>

      {/* Tag Names */}
      <div>
        <label className="block text-sm font-semibold mb-1">Add Tag Name <span className="text-gray-400 font-normal text-xs">(tag @người dùng vào cuối comment)</span></label>
        <div className="flex gap-2 mb-2">
          <input
            className="flex-1 px-2 py-1 border rounded text-sm"
            placeholder="@username hoặc tên"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
          />
          <button
            type="button"
            onClick={addTag}
            className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
          >
            Add
          </button>
        </div>
        {(config.tag_names || []).length > 0 && (
          <div className="flex flex-wrap gap-1">
            {(config.tag_names || []).map((name) => (
              <span key={name} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">
                @{name}
                <button type="button" onClick={() => removeTag(name)} className="hover:text-red-600 font-bold">×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Hashtags */}
      <div>
        <label className="block text-sm font-semibold mb-1">Add Hashtag <span className="text-gray-400 font-normal text-xs">(gắn #hashtag vào cuối comment)</span></label>
        <div className="flex gap-2 mb-2">
          <input
            className="flex-1 px-2 py-1 border rounded text-sm"
            placeholder="#hashtag"
            value={hashtagInput}
            onChange={(e) => setHashtagInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addHashtag(); } }}
          />
          <button
            type="button"
            onClick={addHashtag}
            className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
          >
            Add
          </button>
        </div>
        {(config.hashtags || []).length > 0 && (
          <div className="flex flex-wrap gap-1">
            {(config.hashtags || []).map((tag) => (
              <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs">
                #{tag}
                <button type="button" onClick={() => removeHashtag(tag)} className="hover:text-red-600 font-bold">×</button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


// ============================================================================
// FIND & JOIN GROUPS PANEL
// ============================================================================

const FindJoinGroupsPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const d: any = node.data || {};
  const config: FindJoinGroupsConfig = d.find_join_groups_config || d.find_groups_config || d.search_groups || d.config || {
    keywords: [],
    max_groups_per_week: 5,
    time_windows: [{ start: '09:00', end: '17:00' }],
    filters: {
      min_members: 1000,
      activity_level: 'active',
    },
  };

  // ensure filters object exists to avoid runtime reads on undefined
  const filters = config.filters || { min_members: 0, activity_level: 'any' };

  const [keywordInput, setKeywordInput] = React.useState('');

  const updateConfig = (updates: Partial<FindJoinGroupsConfig>) => {
    const next: any = {
      ...config,
      ...updates,
      filters: {
        ...(config.filters || { min_members: 1000, activity_level: 'active' }),
        ...((updates as any).filters || {}),
      },
    } as any;

    // Cast to any to avoid strict typing conflicts with legacy/partial payloads
    onChange({
      ...d,
      find_join_groups_config: next,
    } as any);
  };

  const addKeyword = () => {
    const kw = keywordInput.trim();
    if (kw && !(config.keywords || []).includes(kw)) {
      updateConfig({ keywords: [...(config.keywords || []), kw] });
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword: string) => {
    updateConfig({ keywords: (config.keywords || []).filter((k) => k !== keyword) });
  };

  const { t } = useTranslation('workflow');

  return (
    <div className="space-y-3">
      <SectionTitle>{t('findJoinGroups')}</SectionTitle>

      <div>
        <label className="block text-sm mb-1">{t('searchKeywords')}</label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            className="flex-1 px-2 py-1 border rounded text-sm"
            placeholder={t('enterKeyword')}
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
          />
          <Button size="sm" variant="secondary" onClick={addKeyword}>
            {t('add')}
          </Button>
        </div>
        <div className="flex flex-wrap gap-1">
          {(config.keywords || []).map((keyword) => (
            <Badge key={keyword} variant="primary">
              {keyword}
              <button
                className="ml-1"
                onClick={() => removeKeyword(keyword)}
                style={{ cursor: 'pointer' }}
              >
                ×
              </button>
            </Badge>
          ))}
        </div>
      </div>

      <Input
        label={t('maxGroupsPerWeek')}
        type="number"
        min={1}
        max={20}
        value={config.max_groups_per_week || 5}
        onChange={(e) => updateConfig({ max_groups_per_week: parseInt(e.target.value) || 1 })}
      />

      <Input
        label={t('minimumMembers')}
        type="number"
        min={0}
        step={100}
        value={filters.min_members || 0}
        onChange={(e) =>
          updateConfig({
            filters: { ...(config.filters || {}), min_members: parseInt(e.target.value) || 0 },
          } as any)
        }
      />

      <div>
        <label className="block text-sm mb-1">{t('activityLevel')}</label>
        <select
          className="w-full px-2 py-1 border rounded"
          value={filters.activity_level}
          onChange={(e) =>
            updateConfig({
              filters: { ...(config.filters || {}), activity_level: e.target.value as any },
            } as any)
          }
        >
          <option value="any">{t('anyActivity')}</option>
          <option value="active">{t('activeGroups')}</option>
          <option value="very_active">{t('veryActiveGroups')}</option>
        </select>
      </div>
    </div>
  );
};

// ============================================================================
// BRAND INFO PANEL
// ============================================================================

const BrandInfoPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.brand_info_config || node.data.config || {};
  const config = {
    brand_name: rawConfig.brand_name || '',
    industry: rawConfig.industry || '',
    category: rawConfig.category || '',
    product_names: rawConfig.product_names || [],
    description: rawConfig.description || '',
    core_topics: rawConfig.core_topics || [],
    target_demographics: {
      age_groups: rawConfig.target_demographics?.age_groups || [],
      locations: rawConfig.target_demographics?.locations || [],
      interests: rawConfig.target_demographics?.interests || [],
    },
    mention_style: rawConfig.mention_style || 'organic',
    tone_of_voice: rawConfig.tone_of_voice || '',
  };

  const updateConfig = (updates: any) => {
    onChange({
      ...node.data,
      brand_info_config: { ...config, ...updates },
    });
  };

  const { t } = useTranslation('workflow');

  return (
    <div className="space-y-3">
      <SectionTitle>{t('brandInfoTitle')}</SectionTitle>

      <Input
        label={t('brandNameLabel')}
        value={config.brand_name || ''}
        onChange={(e) => updateConfig({ brand_name: e.target.value })}
      />

      <Input
        label={t('industryLabel')}
        value={config.industry || ''}
        onChange={(e) => updateConfig({ industry: e.target.value })}
      />

      <Input
        label={t('categoryLabel', 'Danh mục sản phẩm')}
        value={config.category || ''}
        onChange={(e) => updateConfig({ category: e.target.value })}
      />

      <div>
        <label className="block text-sm mb-1">{t('productNamesLabel', 'Sản phẩm (mỗi dòng 1 sản phẩm)')}</label>
        <textarea
          className="w-full px-2 py-1 border rounded text-sm"
          rows={3}
          placeholder="Ví dụ:&#10;Cà phê Signature Espresso&#10;Cold Brew&#10;Bánh mì Sài Gòn"
          value={(config.product_names || []).join('\n')}
          onChange={(e) => {
            const lines = e.target.value.split('\n');
            updateConfig({ product_names: lines });
          }}
          onBlur={(e) => {
            const lines = e.target.value.split('\n').map((l) => l.trim()).filter(Boolean);
            updateConfig({ product_names: lines });
          }}
        />
        <p className="text-xs text-gray-500 mt-1">{t('productNamesHint', 'Sản phẩm đầu tiên sẽ được dùng cho {{product_name}} trong role command')}</p>
      </div>

      <div>
        <label className="block text-sm mb-1">{t('descriptionLabel')}</label>
        <textarea
          className="w-full px-2 py-1 border rounded text-sm"
          rows={3}
          value={config.description || ''}
          onChange={(e) => updateConfig({ description: e.target.value })}
        />
      </div>

      <div>
        <label className="block text-sm mb-1">{t('toneOfVoiceLabel', 'Giọng điệu thương hiệu')}</label>
        <input
          type="text"
          className="w-full px-2 py-1 border rounded text-sm"
          placeholder="Ví dụ: thân thiện, chuyên nghiệp, trẻ trung"
          value={config.tone_of_voice || ''}
          onChange={(e) => updateConfig({ tone_of_voice: e.target.value })}
        />
      </div>

      <div>
        <label className="block text-sm mb-1">{t('mentionStyleLabel')}</label>
        <select
          className="w-full px-2 py-1 border rounded"
          value={config.mention_style}
          onChange={(e) => updateConfig({ mention_style: e.target.value })}
        >
          <option value="organic">{t('styleOrganic')}</option>
          <option value="testimonial">{t('styleTestimonial')}</option>
          <option value="educational">{t('styleEducational')}</option>
          <option value="promotional">{t('stylePromotional')}</option>
        </select>
      </div>
    </div>
  );
};

// ============================================================================
// SEEDING CONTENTS MANAGER PANEL
// ============================================================================

// ============================================================================
// SEEDING CONTENT PICKER
// Loads seeding_contents collection for a given account (post URLs registered by
// workers or added manually) and presents them as a checkbox list.
// Falls back to manual URL input when no contents are found.
// This component only changes HOW post_urls[] gets populated — the config shape
// (SeedingPostEntry.post_urls: string[]) stays identical.
// ============================================================================

interface SeedingContentPickerProps {
  accountId: string;
  selectedUrls: string[];
  onChange: (urls: string[]) => void;
}

const SeedingContentPicker: React.FC<SeedingContentPickerProps> = ({
  accountId,
  selectedUrls,
  onChange,
}) => {
  const { t } = useTranslation('workflow');
  const [contents, setContents] = useState<SeedingContentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [manualUrl, setManualUrl] = useState('');
  const [deleting, setDeleting] = useState<Set<string>>(new Set());

  const reloadContents = async () => {
    if (!accountId) {
      setContents([]);
      return;
    }
    try {
      setLoading(true);
      setFetchError(null);
      const items = await SeedingContentsAPI.listByAccount(accountId, true);
      setContents(items);
    } catch (err: any) {
      console.error('[SeedingContentPicker] Failed to load seeding contents:', err);
      setFetchError(err.message || 'Failed to load seeding contents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accountId) {
      setContents([]);
      return;
    }
    setLoading(true);
    setFetchError(null);
    SeedingContentsAPI.listByAccount(accountId, true)
      .then((items) => setContents(items))
      .catch((err) => {
        console.error('[SeedingContentPicker] Failed to load seeding contents:', err);
        setFetchError(err.message || 'Failed to load seeding contents');
      })
      .finally(() => setLoading(false));
  }, [accountId]);

  const toggleUrl = (url: string) => {
    if (selectedUrls.includes(url)) {
      onChange(selectedUrls.filter((u) => u !== url));
    } else {
      onChange([...selectedUrls, url]);
    }
  };

  const addManualUrl = () => {
    const trimmed = manualUrl.trim();
    if (!trimmed) return;
    if (!selectedUrls.includes(trimmed)) {
      onChange([...selectedUrls, trimmed]);
    }
    setManualUrl('');
  };

  const removeUrl = (url: string) => {
    onChange(selectedUrls.filter((u) => u !== url));
  };

  const handleDeleteItem = async (itemId: string, itemUrl: string) => {
    if (!confirm('Are you sure you want to delete this seeding content?')) {
      return;
    }

    try {
      setDeleting((prev) => new Set([...prev, itemId]));
      await SeedingContentsAPI.deleteItem(itemId);

      // Remove from selected URLs if it was selected
      if (selectedUrls.includes(itemUrl)) {
        removeUrl(itemUrl);
      }

      // Reload contents from database
      await reloadContents();
    } catch (err: any) {
      console.error('[SeedingContentPicker] Failed to delete item:', err);
      alert('Failed to delete seeding content: ' + (err.message || 'Unknown error'));
    } finally {
      setDeleting((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  if (!accountId) {
    return (
      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
        {t('scpSelectAccountWarning')}
      </div>
    );
  }

  if (loading) {
    return <div className="text-xs text-gray-500 italic px-2 py-1">{t('scpLoading')}</div>;
  }

  if (fetchError) {
    return (
      <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
        ⚠️ {fetchError}
      </div>
    );
  }

  // URLs in selectedUrls that didn't come from the DB list (added manually or from a previous state)
  const manuallyAddedUrls = selectedUrls.filter((u) => !contents.some((c) => c.post_url === u));

  return (
    <div className="space-y-2">
      {/* ── DB-sourced URLs ───────────────────────────────────────────────── */}
      {contents.length > 0 ? (
        <div className="border rounded bg-white max-h-44 overflow-y-auto divide-y text-xs">
          {contents.map((item) => {
            const checked = selectedUrls.includes(item.post_url);
            const isDeleting = deleting.has(item._id);
            return (
              <div
                key={item._id}
                className={`flex items-start gap-2 px-2 py-1.5 hover:bg-gray-50 ${checked ? 'bg-blue-50' : ''
                  } group`}
              >
                <input
                  type="checkbox"
                  className="mt-0.5 flex-shrink-0 cursor-pointer"
                  checked={checked}
                  onChange={() => toggleUrl(item.post_url)}
                />
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => toggleUrl(item.post_url)}>
                  <div className="break-all text-gray-800">{item.post_url}</div>
                  <div className="text-gray-400 mt-0.5">
                    {item.source === 'manual' ? t('scpManualSource') : t('scpWorkerSource')} ·{' '}
                    {new Date(item.created_at).toLocaleDateString()}
                  </div>
                </div>
                <button
                  type="button"
                  className="flex-shrink-0 text-gray-400 hover:text-red-500 px-1 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                  onClick={() => handleDeleteItem(item._id, item.post_url)}
                  disabled={isDeleting}
                  title="Delete this seeding content"
                >
                  {isDeleting ? '⏳' : '✕'}
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-xs text-gray-500 bg-gray-50 border rounded p-2 italic">
          {t('scpNoContents')}
        </div>
      )}

      {/* ── Manual URL input (always available as fallback) ────────────────── */}
      <div className="space-y-1">
        <div className="text-xs font-semibold text-gray-600">{t('scpManualUrlLabel')}</div>
        <div className="flex gap-1">
          <input
            type="url"
            className="flex-1 px-2 py-1 border rounded text-xs"
            placeholder={t('scpUrlPlaceholder')}
            value={manualUrl}
            onChange={(e) => setManualUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addManualUrl();
              }
            }}
          />
          <Button size="sm" variant="secondary" onClick={addManualUrl}>
            Add
          </Button>
        </div>
      </div>

      {/* ── URLs that were manually entered (not in DB list) ─────────────── */}
      {manuallyAddedUrls.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs font-semibold text-gray-500">{t('scpManuallyAdded')}</div>
          {manuallyAddedUrls.map((url, i) => (
            <div key={i} className="flex gap-1 items-center">
              <div className="flex-1 text-xs break-all text-gray-700 bg-gray-50 border rounded px-2 py-1">
                {url}
              </div>
              <button
                type="button"
                className="text-red-500 px-1 text-xs hover:text-red-700"
                onClick={() => removeUrl(url)}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {selectedUrls.length > 0 && (
        <div className="text-xs text-green-700 font-medium">
          {t('scpSelectedCount', { count: selectedUrls.length })}
        </div>
      )}
    </div>
  );
};

const SeedingContentsManagerPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.seeding_contents_manager_config || node.data.config || {};
  const config: SeedingContentsManagerConfig = {
    manager_id: rawConfig.manager_id,
    name: rawConfig.name || '',
    seeding_accounts: rawConfig.seeding_accounts || [],
  };

  // Fetch seeding accounts for dropdown
  const { accounts: allAccounts, loading: accountsLoading } = useAccounts('facebook');
  const [folders, setFolders] = useState<SeedingContentFolderItem[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [fanpages, setFanpages] = useState<FanpagePageOption[]>([]);
  const [fanpagesLoading, setFanpagesLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    setFoldersLoading(true);
    SeedingContentFolderAPI.listAll(1, 100)
      .then((res) => {
        if (mounted) setFolders(res.folders || []);
      })
      .catch((err) => {
        console.error('[SeedingContentsManagerPanel] Failed to load folders:', err);
        if (mounted) setFolders([]);
      })
      .finally(() => {
        if (mounted) setFoldersLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    setFanpagesLoading(true);
    import('../../services/api').then(({ apiService }) => {
      apiService.get<{ fanpages: FanpagePageOption[] }>('/api/v1/fanpages')
        .then((resp) => {
          if (mounted) setFanpages(resp.fanpages || []);
        })
        .catch(() => {
          if (mounted) setFanpages([]);
        })
        .finally(() => {
          if (mounted) setFanpagesLoading(false);
        });
    });
    return () => {
      mounted = false;
    };
  }, []);

  const updateConfig = (updates: Partial<SeedingContentsManagerConfig>) => {
    onChange({
      ...node.data,
      seeding_contents_manager_config: { ...config, ...updates },
    });
  };

  const addAccount = () => {
    const newEntry: SeedingPostEntry = {
      account_id: '',
      source_kind: 'account',
      post_mode: 'specific',
      post_urls: [],
      selected_folder_ids: [],
      selected_fanpage_page_ids: [],
      frequency_control: { max_total_per_post: 0, max_per_day_per_post: 3, cooldown_hours: 24 },
    };
    updateConfig({ seeding_accounts: [...config.seeding_accounts, newEntry] });
  };

  const removeAccount = (index: number) => {
    const updated = [...config.seeding_accounts];
    updated.splice(index, 1);
    updateConfig({ seeding_accounts: updated });
  };

  const updateAccount = (index: number, updates: Partial<SeedingPostEntry>) => {
    const updated = config.seeding_accounts.map((a, i) =>
      i === index ? { ...a, ...updates } : a,
    );
    updateConfig({ seeding_accounts: updated });
  };

  const updateFreq = (index: number, freqUpdates: Partial<FrequencyControl>) => {
    const entry = config.seeding_accounts[index];
    updateAccount(index, {
      frequency_control: { ...entry.frequency_control, ...freqUpdates },
    });
  };

  const { t } = useTranslation('workflow');

  const toggleFollowFolder = (index: number, folderId: string) => {
    const entry = config.seeding_accounts[index];
    const selected = new Set(entry.selected_folder_ids || []);
    if (selected.has(folderId)) {
      selected.delete(folderId);
    } else {
      selected.add(folderId);
    }
    updateAccount(index, { selected_folder_ids: Array.from(selected) });
  };

  const toggleFollowFanpage = (index: number, pageId: string) => {
    const entry = config.seeding_accounts[index];
    const selected = new Set(entry.selected_fanpage_page_ids || []);
    if (selected.has(pageId)) {
      selected.delete(pageId);
    } else {
      selected.add(pageId);
    }
    updateAccount(index, { selected_fanpage_page_ids: Array.from(selected) });
  };

  return (
    <div className="space-y-3">
      <SectionTitle>{t('scmTitle')}</SectionTitle>

      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-xs text-purple-800">
        {t('scmHowItWorks')}
      </div>

      <Input
        label={t('managerName')}
        placeholder={t('managerNamePlaceholder')}
        value={config.name}
        onChange={(e) => updateConfig({ name: e.target.value })}
      />

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-semibold">{t('seedingAccountsLabel')}</label>
          <Button size="sm" variant="secondary" onClick={addAccount}>
            {t('addAccountButton')}
          </Button>
        </div>

        {config.seeding_accounts.length === 0 ? (
          <div className="text-xs text-gray-500 italic p-2 border rounded bg-gray-50">
            {t('noAccountsConfigured')}
          </div>
        ) : (
          config.seeding_accounts.map((entry, index) => (
            <div key={index} className="border rounded-lg p-3 bg-gray-50 space-y-2">
              {entry.source_kind === 'fanpage' && entry.post_mode !== 'follow' && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  Fanpage source only supports follow mode. Switched to follow.
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-700">{t('accountNumber', { index: index + 1 })}</span>
                <button
                  type="button"
                  onClick={() => removeAccount(index)}
                  className="text-red-500 hover:text-red-700 text-xs"
                >
                  {t('removeAccountButton')}
                </button>
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1">Source Type</label>
                <select
                  className="w-full px-2 py-1 border rounded text-sm"
                  value={entry.source_kind || 'account'}
                  onChange={(e) => {
                    const next = e.target.value as 'account' | 'fanpage';
                    if (next === 'fanpage') {
                      updateAccount(index, {
                        source_kind: 'fanpage',
                        post_mode: 'follow',
                        account_id: '',
                        post_urls: [],
                      });
                    } else {
                      updateAccount(index, { source_kind: 'account' });
                    }
                  }}
                >
                  <option value="account">Personal account catalogue</option>
                  <option value="fanpage">Fanpage catalogue</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1">
                  {entry.source_kind === 'fanpage' ? 'Source Fanpages' : `${t('accountLabel')}`} {entry.account_id && entry.source_kind !== 'fanpage' && <span className="text-green-600">✓</span>}
                </label>
                {(entry.source_kind || 'account') === 'fanpage' ? (
                  fanpagesLoading ? (
                    <div className="text-xs text-gray-500 italic px-2 py-1">Loading fanpages...</div>
                  ) : fanpages.length === 0 ? (
                    <div className="text-xs text-gray-500 italic px-2 py-1">No fanpages connected yet.</div>
                  ) : (
                    <div className="grid grid-cols-1 gap-1 max-h-28 overflow-y-auto border rounded bg-white p-1.5">
                      {fanpages.map((fp) => {
                        const checked = (entry.selected_fanpage_page_ids || []).includes(fp.page_id);
                        return (
                          <label key={fp.id} className="flex items-center gap-2 text-gray-700">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleFollowFanpage(index, fp.page_id)}
                            />
                            <span>{fp.page_name}</span>
                            <span className="text-gray-400">({fp.page_id})</span>
                          </label>
                        );
                      })}
                    </div>
                  )
                ) : accountsLoading ? (
                  <div className="text-xs text-gray-500 italic px-2 py-1">{t('loadingAccounts')}</div>
                ) : allAccounts.length === 0 ? (
                  <input
                    type="text"
                    className="w-full px-2 py-1 border rounded text-xs font-mono"
                    placeholder={t('noAccountsFoundPaste')}
                    value={entry.account_id}
                    onChange={(e) => updateAccount(index, { account_id: e.target.value })}
                  />
                ) : (
                  <select
                    className="w-full px-2 py-1 border rounded text-sm"
                    value={entry.account_id}
                    onChange={(e) => updateAccount(index, { account_id: e.target.value })}
                  >
                    <option value="">{t('selectFBAccount')}</option>
                    {allAccounts.map((acc) => (
                      <option key={acc.account_id} value={acc.account_id}>
                        {acc.name} ({acc.status})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1">{t('postMode')}</label>
                <select
                  className="w-full px-2 py-1 border rounded text-sm"
                  value={entry.post_mode}
                  onChange={(e) =>
                    updateAccount(index, {
                      post_mode: ((entry.source_kind || 'account') === 'fanpage' ? 'follow' : e.target.value) as 'specific' | 'follow',
                    })
                  }
                >
                  <option value="specific" disabled={(entry.source_kind || 'account') === 'fanpage'}>{t('postModeSpecific')}</option>
                  <option value="follow">{t('postModeFollow')}</option>
                </select>
                {entry.post_mode === 'follow' && (
                  <div className="space-y-2 text-xs text-gray-500 mt-1 bg-blue-50 rounded p-2">
                    <div>{t('postModeFollowHint')}</div>
                    <div className="text-gray-700 font-semibold">Folder filter</div>
                    {foldersLoading ? (
                      <div className="italic">Loading folders...</div>
                    ) : folders.length === 0 ? (
                      <div className="italic">No folders found. Default folder will be used.</div>
                    ) : (
                      <div className="grid grid-cols-1 gap-1 max-h-28 overflow-y-auto border border-blue-200 rounded bg-white p-1.5">
                        {folders.map((folder) => {
                          const checked = (entry.selected_folder_ids || []).includes(folder._id);
                          return (
                            <label key={folder._id} className="flex items-center gap-2 text-gray-700">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleFollowFolder(index, folder._id)}
                              />
                              <span>
                                {folder.name} [{formatFolderCategory(folder)}]
                                {folder.is_default ? ' (default)' : ''}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                    <div className="text-[11px] text-gray-500">
                      Empty selection means all folders.
                    </div>
                    {(entry.source_kind || 'account') === 'fanpage' && (
                      <div className="text-[11px] text-red-600 font-semibold">
                        Fanpage follow mode requires selecting at least 1 fanpage.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {entry.post_mode === 'specific' && (
                <div>
                  <label className="block text-xs font-semibold mb-1">{t('postUrls')}</label>
                  <SeedingContentPicker
                    accountId={entry.account_id}
                    selectedUrls={entry.post_urls || []}
                    onChange={(urls) => updateAccount(index, { post_urls: urls })}
                  />
                </div>
              )}

              <div className="border-t pt-2 space-y-1">
                <label className="block text-xs font-semibold text-gray-600">
                  {t('frequencyControl')}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs text-gray-500">{t('maxTotalPerPost')}</label>
                    <input
                      type="number"
                      min="0"
                      className="w-full px-1 py-0.5 border rounded text-xs"
                      value={entry.frequency_control?.max_total_per_post ?? 0}
                      onChange={(e) =>
                        updateFreq(index, { max_total_per_post: parseInt(e.target.value) || 0 })
                      }
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">{t('maxPerDayPerPost')}</label>
                    <input
                      type="number"
                      min="0"
                      className="w-full px-1 py-0.5 border rounded text-xs"
                      value={entry.frequency_control?.max_per_day_per_post ?? 3}
                      onChange={(e) =>
                        updateFreq(index, { max_per_day_per_post: parseInt(e.target.value) || 0 })
                      }
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">{t('cooldownHours')}</label>
                    <input
                      type="number"
                      min="0"
                      className="w-full px-1 py-0.5 border rounded text-xs"
                      value={entry.frequency_control?.cooldown_hours ?? 24}
                      onChange={(e) =>
                        updateFreq(index, { cooldown_hours: parseInt(e.target.value) || 0 })
                      }
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-400">
                  {t('frequencyHint')}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ============================================================================
// POST TO GROUP PANEL
// ============================================================================

const PostToGroupPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.post_to_group_config || node.data.postToGroupConfig || node.data.config || {};

  const config = {
    group_urls: rawConfig.group_urls || [],
    content_hints: rawConfig.content_hints || '',
    link_url: rawConfig.link_url || '',
    brand_info_node_id: rawConfig.brand_info_node_id,
    has_image: rawConfig.has_image || false,
    image_source_type: (rawConfig.image_source_type || 'none') as 'none' | 'context' | 'vault_asset' | 'vault_folder',
    image_asset_id: rawConfig.image_asset_id || '',
    vault_folder_id: rawConfig.vault_folder_id || '',
    add_to_seeding_contents: rawConfig.add_to_seeding_contents || false,
    seeding_folder_ids: (rawConfig.seeding_folder_ids as string[]) || [],
    post_mode: (rawConfig.post_mode || 'discussion') as 'discussion' | 'seeding',
    anonymous_post: rawConfig.anonymous_post || false,
  };

  const isSeeding = config.post_mode === 'seeding';

  const { t } = useTranslation('workflow');
  const [seedingFolders, setSeedingFolders] = useState<SeedingContentFolderItem[]>([]);
  const [seedingFoldersLoading, setSeedingFoldersLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    setSeedingFoldersLoading(true);
    SeedingContentFolderAPI.listAll(1, 100)
      .then((res) => { if (mounted) setSeedingFolders(res.folders || []); })
      .catch(() => { if (mounted) setSeedingFolders([]); })
      .finally(() => { if (mounted) setSeedingFoldersLoading(false); });
    return () => { mounted = false; };
  }, []);

  const updateConfig = (updates: Partial<PostToGroupConfig>) => {
    onChange({
      ...node.data,
      post_to_group_config: {
        ...config,
        ...updates,
      },
    });
  };

  const handleAddGroup = () => {
    updateConfig({ group_urls: [...(config.group_urls || []), ''] });
  };

  const handleRemoveGroup = (index: number) => {
    const newUrls = [...(config.group_urls || [])];
    newUrls.splice(index, 1);
    updateConfig({ group_urls: newUrls });
  };

  const handleUpdateGroup = (index: number, value: string) => {
    const newUrls = [...(config.group_urls || [])];
    newUrls[index] = value;
    updateConfig({ group_urls: newUrls });
  };

  return (
    <div className="space-y-3">
      <SectionTitle>{t('postToGroup')}</SectionTitle>

      {/* Master Timeline Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <span className="text-lg">📅</span>
          <div className="flex-1">
            <div className="text-sm font-semibold text-blue-900">{t('timingBanner')}</div>
            <div className="text-xs text-blue-700 mt-1">
              {t('timingBannerHint')}
            </div>
          </div>
        </div>
      </div>

      {/* Brand Info Selector */}
      <BrandInfoSelector
        value={config.brand_info_node_id}
        onChange={(brandInfoNodeId) => updateConfig({ brand_info_node_id: brandInfoNodeId })}
      />

      {/* Post Mode */}
      <div>
        <label className="block text-sm font-semibold mb-1">{t('postModeLabel')}</label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${!isSeeding
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
              }`}
            onClick={() => updateConfig({ post_mode: 'discussion' })}
          >
            {t('discussionModeGroup')}
          </button>
          <button
            type="button"
            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${isSeeding
              ? 'bg-orange-500 text-white border-orange-500'
              : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'
              }`}
            onClick={() => updateConfig({ post_mode: 'seeding' })}
          >
            {t('seedingModeGroup')}
          </button>
        </div>
        {isSeeding && (
          <div className="mt-2 text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded px-2 py-1">
            {t('seedingModeWarning')}
          </div>
        )}
      </div>

      {/* Anonymous post mode */}
      <div className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
        <input
          id="anonymous_post"
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 cursor-pointer"
          checked={config.anonymous_post || false}
          onChange={(e) => updateConfig({ anonymous_post: e.target.checked })}
        />
        <div>
          <label htmlFor="anonymous_post" className="text-sm font-medium text-gray-800 cursor-pointer select-none">
            {t('anonymousPost')}
          </label>
          <p className="mt-0.5 text-xs text-gray-500">{t('anonymousPostHint')}</p>
        </div>
      </div>

      {/* Target Groups - Dynamic Array */}
      <FieldArray
        label={t('targetGroups')}
        help={t('targetGroupsHelp')}
        addLabel={t('addGroupUrl')}
        onAdd={handleAddGroup}
      >
        {(config.group_urls || []).length === 0 ? (
          <div className="text-sm text-gray-500 italic py-2">
            {t('noGroupsYet')}
          </div>
        ) : (
          (config.group_urls || []).map((url: string, index: number) => (
            <FieldArrayItem
              key={index}
              onRemove={() => handleRemoveGroup(index)}
            >
              <Input
                type="url"
                placeholder="https://www.facebook.com/groups/123456789"
                value={url}
                onChange={(e) => handleUpdateGroup(index, e.target.value)}
                className="font-mono text-sm"
              />
            </FieldArrayItem>
          ))
        )}
      </FieldArray>

      <div>
        <label className="block text-sm font-medium mb-1.5">
          {t('contentGuidance')}{isSeeding && <span className="text-red-500 ml-1">*</span>}
        </label>
        <TextArea
          rows={3}
          placeholder={isSeeding ? t('contentGuidanceSeedingGroupPlaceholder') : t('contentGuidanceGroupPlaceholder')}
          value={config.content_hints || ''}
          onChange={(e) => updateConfig({ content_hints: e.target.value })}
        />
      </div>

      <div>
        <label className="block text-sm mb-1">{t('linkUrl', 'Link URL')} <span className="text-gray-400 text-xs">({t('optional', 'optional')})</span></label>
        <Input
          type="url"
          placeholder="https://example.com"
          value={config.link_url || ''}
          onChange={(e) => updateConfig({ link_url: e.target.value })}
        />
      </div>

      {/* Image Source */}
      <div>
        <label className="block text-sm font-medium mb-1.5">{t('imageAttachmentGroup')}</label>
        <select
          className="w-full px-2 py-1 border rounded text-sm"
          value={config.image_source_type}
          onChange={(e) =>
            updateConfig({
              image_source_type: e.target.value as any,
              image_asset_id: e.target.value !== 'vault_asset' ? '' : config.image_asset_id,
              vault_folder_id: e.target.value !== 'vault_folder' ? '' : config.vault_folder_id,
            })
          }
        >
          <option value="none">{t('noImage')}</option>
          <option value="context">{t('fromPreviousNode')}</option>
          <option value="character_context">{t('characterContext', 'Character Context (Image2Image)')}</option>
          <option value="vault_asset">{t('pickFromVault')}</option>
          <option value="vault_folder">{t('pickFromVaultFolder', 'Vault Folder (Sequential/Random)')}</option>
        </select>

        {config.image_source_type === 'context' && (
          <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
            {t('contextImageHint')}
          </div>
        )}

        {config.image_source_type === 'vault_asset' && (
          <div className="mt-2">
            <div className="text-xs text-gray-600 mb-1">{t('selectImageFromVault')}</div>
            <VaultAssetPicker
              mediaType="image"
              selectedId={config.image_asset_id || undefined}
              onSelect={(id: string) => updateConfig({ image_asset_id: id })}
            />
            {config.image_asset_id && (
              <p className="text-xs text-gray-500 mt-1 truncate">
                {t('selectedImage', { id: config.image_asset_id })}
              </p>
            )}
          </div>
        )}

        {config.image_source_type === 'vault_folder' && (
          <div className="mt-2">
            <label className="block text-xs text-gray-600 mb-1">
              {t('selectVaultFolder', 'Select Folder')}
            </label>
            {seedingFoldersLoading ? (
              <p className="text-xs text-purple-500">Loading folders...</p>
            ) : seedingFolders.length === 0 ? (
              <p className="text-xs text-purple-400">No folders found</p>
            ) : (
              <select
                className="w-full px-2 py-1 border rounded text-sm"
                value={config.vault_folder_id || ''}
                onChange={(e) => updateConfig({ vault_folder_id: e.target.value })}
              >
                <option value="" disabled>-- {t('selectVaultFolder', 'Select Folder')} --</option>
                {seedingFolders.map((folder) => (
                  <option key={folder._id} value={folder._id}>
                    {folder.name} {folder.is_default ? '(default)' : ''}
                  </option>
                ))}
              </select>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {t('vaultFolderHint', 'Assets will be posted one by one without duplication.')}
            </p>
          </div>
        )}
      </div>

      {/* Seeding Contents Registration */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={config.add_to_seeding_contents || false}
            onChange={(e) => updateConfig({ add_to_seeding_contents: e.target.checked })}
            className="w-4 h-4"
          />
          <span className="text-sm font-semibold text-purple-900">{t('addToSeedingGroup')}</span>
        </label>
        {config.add_to_seeding_contents && (
          <div className="mt-2 ml-6 space-y-2">
            <p className="text-xs text-purple-700">{t('addToSeedingGroupHint')}</p>
            <div>
              <p className="text-xs font-semibold text-purple-800 mb-1">{t('seedingTargetFolders', 'Target Folders (optional)')}</p>
              {seedingFoldersLoading ? (
                <p className="text-xs text-purple-500">Loading folders...</p>
              ) : seedingFolders.length === 0 ? (
                <p className="text-xs text-purple-400">{t('noSeedingFolders', 'No folders found — posts go to default pool')}</p>
              ) : (
                <div className="space-y-1 max-h-36 overflow-y-auto border border-purple-200 rounded p-2 bg-white">
                  {seedingFolders.map((folder) => {
                    const selected = (config.seeding_folder_ids || []).includes(folder._id);
                    return (
                      <label key={folder._id} className="flex items-center gap-2 cursor-pointer hover:bg-purple-50 rounded px-1">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => {
                            const current = config.seeding_folder_ids || [];
                            const updated = e.target.checked
                              ? [...current, folder._id]
                              : current.filter((id) => id !== folder._id);
                            updateConfig({ seeding_folder_ids: updated });
                          }}
                          className="w-3 h-3"
                        />
                        <span className="text-xs text-purple-900">{folder.name}</span>
                        <span className="text-xs text-purple-500">[{formatFolderCategory(folder)}]</span>
                        {folder.is_default && <span className="text-xs text-purple-400">(default)</span>}
                      </label>
                    );
                  })}
                </div>
              )}
              {(config.seeding_folder_ids || []).length === 0 && seedingFolders.length > 0 && (
                <p className="text-xs text-purple-400 mt-1">{t('noFolderSelected', 'None selected → uses default pool')}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// POST TO FANPAGE PANEL
// ============================================================================

const PostToFanpagePanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.post_to_fanpage_config || node.data.config || {};

  const config: PostToFanpageConfig = {
    page_ids: rawConfig.page_ids || [],
    content_source_mode: rawConfig.content_source_mode || 'ai',
    content_strategy: rawConfig.content_strategy || 'shared',
    content_type: rawConfig.content_type || 'text',
    manual_message: rawConfig.manual_message || '',
    content_hints: rawConfig.content_hints || '',
    brand_info_node_id: rawConfig.brand_info_node_id,
    image_source_type: (rawConfig.image_source_type || 'none') as 'none' | 'context' | 'vault_asset' | 'character_context',
    image_asset_id: rawConfig.image_asset_id || '',
    link_url: rawConfig.link_url || '',
    add_to_seeding_contents: rawConfig.add_to_seeding_contents || false,
    seeding_folder_ids: (rawConfig.seeding_folder_ids as string[]) || [],
    schedule_mode: (rawConfig.schedule_mode || 'all') as 'all' | 'round_robin',
  };

  const { t } = useTranslation('workflow');
  const [fanpages, setFanpages] = React.useState<Array<{ id: string; page_name: string; page_id: string; picture_url?: string; }>>([]);
  const [loadingPages, setLoadingPages] = React.useState(false);
  const [seedingFolders, setSeedingFolders] = useState<SeedingContentFolderItem[]>([]);
  const [seedingFoldersLoading, setSeedingFoldersLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    setSeedingFoldersLoading(true);
    SeedingContentFolderAPI.listAll(1, 100)
      .then((res) => { if (mounted) setSeedingFolders(res.folders || []); })
      .catch(() => { if (mounted) setSeedingFolders([]); })
      .finally(() => { if (mounted) setSeedingFoldersLoading(false); });
    return () => { mounted = false; };
  }, []);

  React.useEffect(() => {
    setLoadingPages(true);
    import('../../services/api').then(({ apiService }) => {
      apiService.get<{ fanpages: Array<{ id: string; page_id: string; page_name: string; picture_url?: string }> }>('/api/v1/fanpages')
        .then((resp) => { setFanpages(resp.fanpages ?? []); })
        .catch(() => { setFanpages([]); })
        .finally(() => setLoadingPages(false));
    });
  }, []);

  const updateConfig = (updates: Partial<PostToFanpageConfig>) => {
    onChange({
      ...node.data,
      post_to_fanpage_config: { ...config, ...updates },
    });
  };

  const togglePage = (pageId: string) => {
    const current = config.page_ids || [];
    const updated = current.includes(pageId)
      ? current.filter((id) => id !== pageId)
      : [...current, pageId];
    updateConfig({ page_ids: updated });
  };

  return (
    <div className="space-y-3">
      <SectionTitle>{t('postToFanpage', 'Post to Fanpage')}</SectionTitle>

      {/* API-mode info banner */}
      <div className="bg-pink-50 border border-pink-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <span className="text-lg">📄</span>
          <div className="flex-1">
            <div className="text-sm font-semibold text-pink-900">{t('fanpageApiMode', 'Facebook Graph API Mode')}</div>
            <div className="text-xs text-pink-700 mt-1">
              {t('fanpageApiModeHint', 'Posts are published via Graph API — no Facebook account login required. Connect pages in Fanpage Management first.')}
            </div>
          </div>
        </div>
      </div>

      {/* Brand Info Selector */}
      <BrandInfoSelector
        value={config.brand_info_node_id}
        onChange={(brandInfoNodeId) => updateConfig({ brand_info_node_id: brandInfoNodeId })}
      />

      {/* Target Pages */}
      <div>
        <label className="block text-sm font-semibold mb-1">
          {t('targetPages', 'Target Pages')}
          {' '}
          <span className="text-gray-400 font-normal text-xs">{t('targetPagesHint', '(empty = all connected pages)')}</span>
        </label>
        {loadingPages ? (
          <div className="text-xs text-gray-500 py-2">{t('loadingPages', 'Loading pages…')}</div>
        ) : fanpages.length === 0 ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            {t('noFanpagesConnected', 'No pages connected yet. Go to Fanpage Management to connect.')}
          </div>
        ) : (
          <div className="space-y-1">
            {fanpages.map((fp) => {
              const selected = (config.page_ids || []).includes(fp.id);
              return (
                <label
                  key={fp.id}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded border cursor-pointer transition-colors ${selected ? 'bg-pink-50 border-pink-300' : 'bg-white border-gray-200 hover:border-pink-200'
                    }`}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => togglePage(fp.id)}
                    className="w-4 h-4 accent-pink-500"
                  />
                  {fp.picture_url && (
                    <img src={fp.picture_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                  )}
                  <span className="text-sm text-gray-800">{fp.page_name}</span>
                  <span className="text-xs text-gray-400 ml-auto">{fp.page_id}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Content Type */}
      <div>
        <label className="block text-sm mb-1">{t('contentType', 'Content Type')}</label>
        <select
          className="w-full px-2 py-1 border rounded"
          value={config.content_type}
          onChange={(e) => updateConfig({ content_type: e.target.value as any })}
        >
          <option value="text">{t('textOnly', 'Text only')}</option>
          <option value="image">{t('imageOnly', 'Image only')}</option>
          <option value="both">{t('textAndImage', 'Text + Image')}</option>
        </select>
      </div>

      <div>
        <label className="block text-sm mb-1">{t('contentSource', 'Content Source')}</label>
        <select
          className="w-full px-2 py-1 border rounded"
          value={config.content_source_mode || 'ai'}
          onChange={(e) => updateConfig({ content_source_mode: e.target.value as any })}
        >
          <option value="ai">{t('generateWithAi', 'Generate with AI')}</option>
          <option value="manual">{t('manualContent', 'Manual content')}</option>
          <option value="upstream">{t('adaptUpstream', 'Adapt from upstream node')}</option>
        </select>
      </div>

      <div>
        <label className="block text-sm mb-1">{t('contentStrategy', 'Content Strategy')}</label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            className={`px-3 py-2 rounded-lg border text-sm font-medium ${config.content_strategy !== 'per_page' ? 'bg-pink-600 text-white border-pink-600' : 'bg-white text-gray-700 border-gray-300'}`}
            onClick={() => updateConfig({ content_strategy: 'shared' })}
          >
            {t('sharedAcrossPages', 'Shared across pages')}
          </button>
          <button
            type="button"
            className={`px-3 py-2 rounded-lg border text-sm font-medium ${config.content_strategy === 'per_page' ? 'bg-pink-600 text-white border-pink-600' : 'bg-white text-gray-700 border-gray-300'}`}
            onClick={() => updateConfig({ content_strategy: 'per_page' })}
          >
            {t('personalizePerPage', 'Personalize per page')}
          </button>
        </div>
      </div>

      {/* Image Source */}
      {config.content_type !== 'text' && (
        <div>
          <label className="block text-sm font-medium mb-1.5">{t('imageAttachment', 'Image Attachment')}</label>
          <select
            className="w-full px-2 py-1 border rounded text-sm"
            value={config.image_source_type}
            onChange={(e) =>
              updateConfig({
                image_source_type: e.target.value as any,
                image_asset_id: e.target.value !== 'vault_asset' ? '' : config.image_asset_id,
              })
            }
          >
            <option value="none">{t('noImage', 'No image')}</option>
            <option value="context">{t('fromPreviousNode', 'From previous node (AI asset)')}</option>
            <option value="character_context">{t('characterContext', 'Character Context (Image2Image)')}</option>
            <option value="vault_asset">{t('pickFromVault', 'Pick from media vault')}</option>
          </select>

          {config.image_source_type === 'context' && (
            <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
              {t('contextImageHint', 'Worker will use the vault_asset_id produced by the upstream AI Generate Asset node.')}
            </div>
          )}

          {config.image_source_type === 'vault_asset' && (
            <div className="mt-2">
              <div className="text-xs text-gray-600 mb-1">{t('selectImageFromVault', 'Select image from vault:')}</div>
              <VaultAssetPicker
                mediaType="image"
                selectedId={config.image_asset_id || undefined}
                onSelect={(id: string) => updateConfig({ image_asset_id: id })}
              />
              {config.image_asset_id && (
                <p className="text-xs text-gray-500 mt-1 truncate">
                  {t('selectedImage', { id: config.image_asset_id })}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {config.content_source_mode === 'manual' ? (
        <div>
          <label className="block text-sm mb-1">{t('manualContent', 'Manual Content')}</label>
          <TextArea
            rows={4}
            value={config.manual_message || ''}
            onChange={(e) => updateConfig({ manual_message: e.target.value })}
            placeholder={t('manualContentPlaceholder', 'Write the exact message that should be published to the selected fanpages')}
          />
        </div>
      ) : (
        <div>
          <label className="block text-sm mb-1">{t('contentHints', 'Content Hints')}</label>
          <textarea
            className="w-full px-2 py-1 border rounded text-sm"
            rows={3}
            placeholder={t('contentHintsPlaceholder', 'E.g. "Announce new product launch, highlight key benefits, include CTA"')}
            value={config.content_hints}
            onChange={(e) => updateConfig({ content_hints: e.target.value })}
          />
        </div>
      )}

      {/* Link URL */}
      <div>
        <label className="block text-sm mb-1">{t('linkUrl', 'Link URL')} <span className="text-gray-400 text-xs">({t('optional', 'optional')})</span></label>
        <Input
          type="url"
          placeholder="https://example.com/product"
          value={config.link_url || ''}
          onChange={(e) => updateConfig({ link_url: e.target.value })}
        />
      </div>

      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={config.add_to_seeding_contents || false}
            onChange={(e) => updateConfig({ add_to_seeding_contents: e.target.checked })}
            className="w-4 h-4"
          />
          <span className="text-sm font-semibold text-purple-900">{t('addToSeedingGroup')}</span>
        </label>
        {config.add_to_seeding_contents && (
          <div className="mt-2 ml-6 space-y-2">
            <p className="text-xs text-purple-700">{t('addToSeedingGroupHint')}</p>
            <div>
              <p className="text-xs font-semibold text-purple-800 mb-1">{t('seedingTargetFolders', 'Target Folders (optional)')}</p>
              {seedingFoldersLoading ? (
                <p className="text-xs text-purple-500">Loading folders...</p>
              ) : seedingFolders.length === 0 ? (
                <p className="text-xs text-purple-400">{t('noSeedingFolders', 'No folders found — posts go to default pool')}</p>
              ) : (
                <div className="space-y-1 max-h-36 overflow-y-auto border border-purple-200 rounded p-2 bg-white">
                  {seedingFolders.map((folder) => {
                    const selected = (config.seeding_folder_ids || []).includes(folder._id);
                    return (
                      <label key={folder._id} className="flex items-center gap-2 cursor-pointer hover:bg-purple-50 rounded px-1">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => {
                            const current = config.seeding_folder_ids || [];
                            const updated = e.target.checked
                              ? [...current, folder._id]
                              : current.filter((id) => id !== folder._id);
                            updateConfig({ seeding_folder_ids: updated });
                          }}
                          className="w-3 h-3"
                        />
                        <span className="text-xs text-purple-900">{folder.name}</span>
                        <span className="text-xs text-purple-500">[{formatFolderCategory(folder)}]</span>
                        {folder.is_default && <span className="text-xs text-purple-400">(default)</span>}
                      </label>
                    );
                  })}
                </div>
              )}
              {(config.seeding_folder_ids || []).length === 0 && seedingFolders.length > 0 && (
                <p className="text-xs text-purple-400 mt-1">{t('noFolderSelected', 'None selected → uses default pool')}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Schedule Mode */}
      <div>
        <label className="block text-sm font-semibold mb-1">{t('fanpageScheduleMode', 'Schedule Mode')}</label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${config.schedule_mode !== 'round_robin'
              ? 'bg-pink-600 text-white border-pink-600'
              : 'bg-white text-gray-700 border-gray-300 hover:border-pink-400'
              }`}
            onClick={() => updateConfig({ schedule_mode: 'all' })}
          >
            {t('scheduleAll', 'All pages')}
          </button>
          <button
            type="button"
            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${config.schedule_mode === 'round_robin'
              ? 'bg-pink-600 text-white border-pink-600'
              : 'bg-white text-gray-700 border-gray-300 hover:border-pink-400'
              }`}
            onClick={() => updateConfig({ schedule_mode: 'round_robin' })}
          >
            {t('scheduleRoundRobin', 'Round-robin')}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {config.schedule_mode === 'round_robin'
            ? t('roundRobinHint', 'One page per run, cycling through the list.')
            : t('allPagesHint', 'Post to all selected pages on every run.')}
        </p>
      </div>
    </div>
  );
};

type FanpagePageOption = { id: string; page_name: string; page_id: string; picture_url?: string };
type FanpagePostOption = {
  fanpageId: string;
  pageId: string;
  pageName: string;
  post_id: string;
  permalink_url?: string;
  message?: string;
  created_at?: string;
  created_time?: string;
};
type FanpageCommentPreviewOption = {
  comment_id: string;
  from_name?: string;
  profile_url?: string;
  message?: string;
  created_time?: string;
};

type FanpagePostsCacheEntry = {
  posts: FanpagePostOption[];
  cachedAt: number;
};

const FANPAGE_POSTS_CACHE_TTL_MS = 5 * 60 * 1000;
const fanpagePostsCache = new Map<string, FanpagePostsCacheEntry>();

const buildFanpagePostsCacheKey = (scope: string[]) =>
  scope.length > 0 ? [...scope].sort().join(',') : '__all__';

const FanpageTargetingFields: React.FC<{
  config: any;
  updateConfig: (updates: any) => void;
}> = ({ config, updateConfig }) => {
  const { t } = useTranslation('workflow');
  const [fanpages, setFanpages] = React.useState<FanpagePageOption[]>([]);
  const [posts, setPosts] = React.useState<FanpagePostOption[]>([]);
  const [loadingPages, setLoadingPages] = React.useState(false);
  const [loadingPosts, setLoadingPosts] = React.useState(false);

  React.useEffect(() => {
    setLoadingPages(true);
    import('../../services/api').then(({ apiService }) => {
      apiService.get<{ fanpages: FanpagePageOption[] }>('/api/v1/fanpages')
        .then((resp) => setFanpages(resp.fanpages ?? []))
        .catch(() => setFanpages([]))
        .finally(() => setLoadingPages(false));
    });
  }, []);

  React.useEffect(() => {
    const scope = (config.page_ids && config.page_ids.length > 0 ? config.page_ids : fanpages.map((page) => page.id)).slice(0, 8);
    if ((config.target_post_mode || 'latest_posts') !== 'specific_posts' || scope.length === 0) {
      setPosts([]);
      return;
    }

    setLoadingPosts(true);
    import('../../services/api').then(({ apiService }) => {
      Promise.all(scope.map(async (fanpageId) => {
        try {
          const response = await apiService.get<{ posts: Array<{ post_id: string; message?: string; created_at?: string }> }>(`/api/v1/fanpages/${fanpageId}/posts?limit=8`);
          const page = fanpages.find((entry) => entry.id === fanpageId);
          return (response.posts ?? []).map((post) => ({
            fanpageId,
            pageName: page?.page_name || fanpageId,
            post_id: post.post_id,
            message: post.message,
            created_at: post.created_at,
          }));
        } catch {
          return [];
        }
      }))
        .then((allPosts) => setPosts(allPosts.flat()))
        .finally(() => setLoadingPosts(false));
    });
  }, [config.page_ids, config.target_post_mode, fanpages]);

  const togglePage = (pageId: string) => {
    const current = config.page_ids || [];
    const updated = current.includes(pageId) ? current.filter((id: string) => id !== pageId) : [...current, pageId];
    updateConfig({ page_ids: updated });
  };

  const togglePost = (postId: string) => {
    const current = config.post_ids || [];
    const updated = current.includes(postId) ? current.filter((id: string) => id !== postId) : [...current, postId];
    updateConfig({ post_ids: updated });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-semibold mb-1">{t('targetPages', 'Target Pages')}</label>
        {loadingPages ? (
          <div className="text-xs text-gray-500 py-2">{t('loadingPages', 'Loading pages…')}</div>
        ) : (
          <div className="space-y-1">
            {fanpages.map((fp) => {
              const selected = (config.page_ids || []).includes(fp.id);
              return (
                <label key={fp.id} className={`flex items-center gap-2 px-2 py-1.5 rounded border cursor-pointer ${selected ? 'bg-pink-50 border-pink-300' : 'bg-white border-gray-200'}`}>
                  <input type="checkbox" checked={selected} onChange={() => togglePage(fp.id)} className="w-4 h-4 accent-pink-500" />
                  <span className="text-sm text-gray-800">{fp.page_name}</span>
                  <span className="text-xs text-gray-400 ml-auto">{fp.page_id}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-semibold mb-1">{t('postSelectionMode', 'Post Selection')}</label>
        <select
          className="w-full px-2 py-1 border rounded"
          value={config.target_post_mode || 'latest_posts'}
          onChange={(e) => updateConfig({ target_post_mode: e.target.value, post_ids: e.target.value === 'specific_posts' ? (config.post_ids || []) : [] })}
        >
          <option value="latest_posts">{t('latestPosts', 'Latest posts')}</option>
          <option value="top_engagement">{t('topEngagementPosts', 'Top engagement posts')}</option>
          <option value="specific_posts">{t('specificPosts', 'Specific posts')}</option>
        </select>
      </div>

      {(config.target_post_mode || 'latest_posts') === 'specific_posts' && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-semibold">{t('fanpagePosts', 'Fanpage Posts')}</label>
            {loadingPosts && <span className="text-xs text-gray-500">{t('loadingPosts', 'Loading posts…')}</span>}
          </div>
          <div className="space-y-1 max-h-52 overflow-y-auto border rounded p-2 bg-gray-50">
            {posts.map((post) => {
              const selected = (config.post_ids || []).includes(post.post_id);
              return (
                <label key={post.post_id} className={`block p-2 rounded border cursor-pointer ${selected ? 'bg-white border-pink-300' : 'bg-white border-gray-200'}`}>
                  <div className="flex items-start gap-2">
                    <input type="checkbox" checked={selected} onChange={() => togglePost(post.post_id)} className="mt-1 w-4 h-4 accent-pink-500" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold text-gray-700">{post.pageName}</div>
                      <div className="text-xs text-gray-500 line-clamp-2">{post.message || post.post_id}</div>
                      <div className="text-[11px] text-gray-400 mt-1">{post.post_id}</div>
                    </div>
                  </div>
                </label>
              );
            })}
            {!loadingPosts && posts.length === 0 && <div className="text-xs text-gray-500">{t('noPostsLoaded', 'No synced posts available yet. Open Fanpage content center and sync posts first.')}</div>}
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="block text-xs mb-1">{t('maxPostsToScan', 'Max Posts')}</label>
          <Input type="number" min={1} value={String(config.max_posts_to_scan ?? 3)} onChange={(e) => updateConfig({ max_posts_to_scan: Number(e.target.value || 1) })} />
        </div>
        <div>
          <label className="block text-xs mb-1">{t('feedFetchLimit', 'Feed Limit')}</label>
          <Input type="number" min={1} value={String(config.feed_fetch_limit ?? 10)} onChange={(e) => updateConfig({ feed_fetch_limit: Number(e.target.value || 1) })} />
        </div>
        <div>
          <label className="block text-xs mb-1">{t('scheduleMode', 'Schedule')}</label>
          <select className="w-full px-2 py-2 border rounded text-sm" value={config.schedule_mode || 'all'} onChange={(e) => updateConfig({ schedule_mode: e.target.value })}>
            <option value="all">{t('scheduleAll', 'All pages')}</option>
            <option value="round_robin">{t('scheduleRoundRobin', 'Round-robin')}</option>
          </select>
        </div>
      </div>
    </div>
  );
};

const FanpageSelectedPostFields: React.FC<{
  config: any;
  updateConfig: (updates: any) => void;
  showCommentsPreview?: boolean;
}> = ({ config, updateConfig, showCommentsPreview = false }) => {
  const [fanpages, setFanpages] = React.useState<FanpagePageOption[]>([]);
  const [posts, setPosts] = React.useState<FanpagePostOption[]>([]);
  const [comments, setComments] = React.useState<FanpageCommentPreviewOption[]>([]);
  const [loadingPages, setLoadingPages] = React.useState(false);
  const [loadingPosts, setLoadingPosts] = React.useState(false);
  const [loadingComments, setLoadingComments] = React.useState(false);

  React.useEffect(() => {
    setLoadingPages(true);
    import('../../services/api').then(({ apiService }) => {
      apiService.get<{ fanpages: FanpagePageOption[] }>('/api/v1/fanpages')
        .then((resp) => setFanpages(resp.fanpages ?? []))
        .catch(() => setFanpages([]))
        .finally(() => setLoadingPages(false));
    });
  }, []);

  React.useEffect(() => {
    const scope = (config.page_ids && config.page_ids.length > 0 ? config.page_ids : fanpages.map((page) => page.id)).slice(0, 8);
    if (scope.length === 0) {
      setPosts([]);
      return;
    }

    const cacheKey = buildFanpagePostsCacheKey(scope);
    const cached = fanpagePostsCache.get(cacheKey);
    if (cached && Date.now() - cached.cachedAt < FANPAGE_POSTS_CACHE_TTL_MS) {
      setPosts(cached.posts);
      setLoadingPosts(false);
      return;
    }

    setLoadingPosts(true);
    import('../../services/api').then(({ apiService }) => {
      Promise.all(scope.map(async (fanpageId) => {
        try {
          const response = await apiService.get<{ posts: Array<{ post_id: string; message?: string; created_at?: string; created_time?: string; permalink_url?: string }> }>(`/api/v1/fanpages/${fanpageId}/posts?limit=10`);
          const page = fanpages.find((entry) => entry.id === fanpageId);
          return (response.posts ?? []).map((post) => ({
            fanpageId,
            pageId: page?.page_id || '',
            pageName: page?.page_name || fanpageId,
            post_id: post.post_id,
            permalink_url: post.permalink_url,
            message: post.message,
            created_at: post.created_at,
            created_time: post.created_time,
          }));
        } catch {
          return [];
        }
      }))
        .then((allPosts) => {
          const merged = allPosts.flat().sort((a, b) => {
            const aTime = new Date(a.created_time || a.created_at || 0).getTime();
            const bTime = new Date(b.created_time || b.created_at || 0).getTime();
            return bTime - aTime;
          });
          setPosts(merged);
          fanpagePostsCache.set(cacheKey, {
            posts: merged,
            cachedAt: Date.now(),
          });
        })
        .finally(() => setLoadingPosts(false));
    });
  }, [config.page_ids, fanpages]);

  React.useEffect(() => {
    if (!config.selected_post_id) {
      return;
    }

    const selectedVisible = posts.some((post) => post.post_id === config.selected_post_id);
    if (selectedVisible) {
      return;
    }

    setPosts((prev) => {
      if (prev.some((post) => post.post_id === config.selected_post_id)) {
        return prev;
      }

      const fallback: FanpagePostOption = {
        fanpageId: '',
        pageId: config.selected_page_id || '',
        pageName: config.selected_page_id || 'Saved selection',
        post_id: config.selected_post_id,
        permalink_url: config.selected_post_url || config.post_url || '',
        message: 'Previously selected post',
      };

      return [fallback, ...prev];
    });
  }, [
    config.post_url,
    config.selected_page_id,
    config.selected_post_id,
    config.selected_post_url,
    posts,
  ]);

  React.useEffect(() => {
    if (!config.selected_post_id || !showCommentsPreview) {
      setComments([]);
      return;
    }

    const selectedPost = posts.find((post) => post.post_id === config.selected_post_id);
    if (!selectedPost?.fanpageId) {
      setComments([]);
      return;
    }

    setLoadingComments(true);
    import('../../services/api').then(({ apiService }) => {
      apiService.get<{ comments: FanpageCommentPreviewOption[] }>(`/api/v1/fanpages/${selectedPost.fanpageId}/posts/${config.selected_post_id}/comments?limit=20`)
        .then((resp) => setComments(resp.comments ?? []))
        .catch(() => setComments([]))
        .finally(() => setLoadingComments(false));
    });
  }, [config.selected_post_id, posts, showCommentsPreview]);

  const togglePage = (pageId: string) => {
    const current = config.page_ids || [];
    const updated = current.includes(pageId) ? current.filter((id: string) => id !== pageId) : [...current, pageId];
    updateConfig({ page_ids: updated });
  };

  const selectPost = (post: FanpagePostOption) => {
    updateConfig({
      target_post_mode: 'specific_posts',
      post_ids: [post.post_id],
      selected_page_id: post.pageId,
      selected_post_id: post.post_id,
      selected_post_url: post.permalink_url || '',
      post_url: post.permalink_url || '',
    });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-semibold mb-1">Target Pages</label>
        {loadingPages ? (
          <div className="text-xs text-gray-500 py-2">Loading pages…</div>
        ) : (
          <div className="space-y-1">
            {fanpages.map((fp) => {
              const selected = (config.page_ids || []).includes(fp.id);
              return (
                <label key={fp.id} className={`flex items-center gap-2 px-2 py-1.5 rounded border cursor-pointer ${selected ? 'bg-pink-50 border-pink-300' : 'bg-white border-gray-200'}`}>
                  <input type="checkbox" checked={selected} onChange={() => togglePage(fp.id)} className="w-4 h-4 accent-pink-500" />
                  <span className="text-sm text-gray-800">{fp.page_name}</span>
                  <span className="text-xs text-gray-400 ml-auto">{fp.page_id}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-semibold">10 bài post mới nhất để chọn</label>
          {loadingPosts && <span className="text-xs text-gray-500">Loading posts…</span>}
        </div>
        <div className="space-y-1 max-h-60 overflow-y-auto border rounded p-2 bg-gray-50">
          {posts.map((post) => {
            const selected = config.selected_post_id === post.post_id;
            return (
              <label key={`${post.fanpageId}-${post.post_id}`} className={`block p-2 rounded border cursor-pointer ${selected ? 'bg-white border-pink-300 ring-1 ring-pink-100' : 'bg-white border-gray-200'}`}>
                <div className="flex items-start gap-2">
                  <input type="radio" name={`${config.selected_page_id || 'fanpage'}-post-picker`} checked={selected} onChange={() => selectPost(post)} className="mt-1 w-4 h-4 accent-pink-500" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-gray-700">{post.pageName}</div>
                    <div className="text-xs text-gray-500 line-clamp-2">{post.message || post.post_id}</div>
                    <div className="text-[11px] text-gray-400 mt-1 break-all">{post.post_id}</div>
                    {post.permalink_url && <div className="text-[11px] text-blue-500 mt-1 break-all">{post.permalink_url}</div>}
                  </div>
                </div>
              </label>
            );
          })}
          {!loadingPosts && posts.length === 0 && <div className="text-xs text-gray-500">Chưa load được bài post live từ Fanpage.</div>}
        </div>
      </div>

      {config.selected_post_id && (
        <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-700">
          <div><strong>Post ID:</strong> {config.selected_post_id}</div>
          {config.selected_post_url && <div className="mt-1 break-all"><strong>Post Link:</strong> {config.selected_post_url}</div>}
        </div>
      )}

      {showCommentsPreview && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-semibold">20 comment mới nhất của post đã chọn</label>
            {loadingComments && <span className="text-xs text-gray-500">Loading comments…</span>}
          </div>
          <div className="space-y-1 max-h-56 overflow-y-auto border rounded p-2 bg-gray-50">
            {comments.map((comment) => (
              <div key={comment.comment_id} className="rounded border border-gray-200 bg-white px-2.5 py-2">
                <div className="text-xs font-semibold text-gray-700">{comment.from_name || 'Người dùng ẩn danh'}</div>
                <div className="text-xs text-gray-500 line-clamp-2">{comment.message || '(không có nội dung)'}</div>
                <div className="text-[11px] text-gray-400 mt-1 break-all">{comment.comment_id}</div>
              </div>
            ))}
            {!loadingComments && comments.length === 0 && <div className="text-xs text-gray-500">Worker sẽ lấy tối đa 20 comment mới nhất từ Facebook ở runtime để chọn reply.</div>}
          </div>
        </div>
      )}
    </div>
  );
};

const FanpageCommentOnPostPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.fanpage_comment_on_post_config || node.data.config || {};
  const legacyPostURL = (rawConfig as Record<string, any>).post_url || '';
  const config: FanpageCommentOnPostConfig = {
    page_ids: rawConfig.page_ids || [],
    target_post_mode: rawConfig.target_post_mode || 'latest_posts',
    post_ids: rawConfig.post_ids || [],
    selected_page_id: rawConfig.selected_page_id || '',
    selected_post_id: rawConfig.selected_post_id || '',
    selected_post_url: rawConfig.selected_post_url || legacyPostURL || '',
    max_posts_to_scan: rawConfig.max_posts_to_scan || 3,
    feed_fetch_limit: rawConfig.feed_fetch_limit || 10,
    schedule_mode: rawConfig.schedule_mode || 'all',
    comment_mode: rawConfig.comment_mode || 'discussion',
    seeding_mode: rawConfig.seeding_mode || '',
    content_hints: rawConfig.content_hints || '',
    brand_info_node_id: rawConfig.brand_info_node_id,
    tag_names: rawConfig.tag_names || [],
    hashtags: rawConfig.hashtags || [],
  };
  const updateConfig = (updates: Partial<FanpageCommentOnPostConfig>) => onChange({ ...node.data, fanpage_comment_on_post_config: { ...config, ...updates } });

  return (
    <div className="space-y-3">
      <SectionTitle>Fanpage Comment on Post</SectionTitle>
      <BrandInfoSelector value={config.brand_info_node_id} onChange={(brand_info_node_id) => updateConfig({ brand_info_node_id })} />
      <FanpageSelectedPostFields config={config} updateConfig={updateConfig} />
      <div>
        <label className="block text-sm mb-1">Comment Mode</label>
        <select className="w-full px-2 py-1 border rounded" value={config.comment_mode || 'discussion'} onChange={(e) => updateConfig({ comment_mode: e.target.value as any })}>
          <option value="discussion">Discussion</option>
          <option value="seeding">Seeding</option>
        </select>
      </div>
      <TextArea rows={3} value={config.content_hints} onChange={(e) => updateConfig({ content_hints: e.target.value })} placeholder="Hướng dẫn AI viết comment cho fanpage" />
      <FieldArray
        label="Tag Names"
        onAdd={() => updateConfig({ tag_names: [...(config.tag_names || []), ''] })}
      >
        {(config.tag_names || []).map((item, index) => (
          <FieldArrayItem
            key={`tag-${index}`}
            onRemove={() => updateConfig({ tag_names: (config.tag_names || []).filter((_, idx) => idx !== index) })}
          >
            <Input
              value={item}
              onChange={(e) => updateConfig({ tag_names: (config.tag_names || []).map((entry, idx) => idx === index ? e.target.value : entry) })}
            />
          </FieldArrayItem>
        ))}
      </FieldArray>
      <FieldArray
        label="Hashtags"
        onAdd={() => updateConfig({ hashtags: [...(config.hashtags || []), ''] })}
      >
        {(config.hashtags || []).map((item, index) => (
          <FieldArrayItem
            key={`hashtag-${index}`}
            onRemove={() => updateConfig({ hashtags: (config.hashtags || []).filter((_, idx) => idx !== index) })}
          >
            <Input
              value={item}
              onChange={(e) => updateConfig({ hashtags: (config.hashtags || []).map((entry, idx) => idx === index ? e.target.value : entry) })}
            />
          </FieldArrayItem>
        ))}
      </FieldArray>
    </div>
  );
};

const FanpageReplyToCommentsPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.fanpage_reply_to_comments_config || node.data.config || {};
  const legacyPostURL = (rawConfig as Record<string, any>).post_url || '';
  const config: FanpageReplyToCommentsConfig = {
    page_ids: rawConfig.page_ids || [],
    target_post_mode: rawConfig.target_post_mode || 'latest_posts',
    post_ids: rawConfig.post_ids || [],
    selected_page_id: rawConfig.selected_page_id || '',
    selected_post_id: rawConfig.selected_post_id || '',
    selected_post_url: rawConfig.selected_post_url || legacyPostURL || '',
    max_posts_to_scan: rawConfig.max_posts_to_scan || 3,
    feed_fetch_limit: rawConfig.feed_fetch_limit || 10,
    schedule_mode: rawConfig.schedule_mode || 'all',
    max_replies: rawConfig.max_replies || rawConfig.max_replies_per_post || 2,
    max_comments: rawConfig.max_comments || rawConfig.max_comments_to_scan || 20,
    content_hints: rawConfig.content_hints || '',
    comment_style: rawConfig.comment_style || {},
    brand_info_node_id: rawConfig.brand_info_node_id,
    reply_mode: rawConfig.reply_mode || 'random',
    reply_keywords: rawConfig.reply_keywords || [],
    reply_max_per_keyword: rawConfig.reply_max_per_keyword || 0,
  };
  const updateConfig = (updates: Partial<FanpageReplyToCommentsConfig>) => onChange({ ...node.data, fanpage_reply_to_comments_config: { ...config, ...updates } });

  return (
    <div className="space-y-3">
      <SectionTitle>Fanpage Reply to Comments</SectionTitle>
      <BrandInfoSelector value={config.brand_info_node_id} onChange={(brand_info_node_id) => updateConfig({ brand_info_node_id })} />
      <FanpageSelectedPostFields config={config} updateConfig={updateConfig} showCommentsPreview />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-sm mb-1">Replies per Post</label>
          <Input type="number" min={1} value={String(config.max_replies || 2)} onChange={(e) => updateConfig({ max_replies: Number(e.target.value || 1) })} />
        </div>
        <div>
          <label className="block text-sm mb-1">Comments to Scan</label>
          <Input type="number" min={1} max={20} value={String(config.max_comments || 20)} onChange={(e) => updateConfig({ max_comments: Number(e.target.value || 1) })} />
        </div>
      </div>
      <div>
        <label className="block text-sm mb-1">Reply Mode</label>
        <select className="w-full px-2 py-1 border rounded" value={config.reply_mode || 'random'} onChange={(e) => updateConfig({ reply_mode: e.target.value as any })}>
          <option value="random">Random</option>
          <option value="keyword">Keyword Match</option>
        </select>
      </div>
      {config.reply_mode === 'keyword' && (
        <TextArea rows={3} value={(config.reply_keywords || []).join('\n')} onChange={(e) => updateConfig({ reply_keywords: e.target.value.split('\n').map((item) => item.trim()).filter(Boolean) })} placeholder="Mỗi dòng là một keyword comment" />
      )}
      <TextArea rows={3} value={config.content_hints} onChange={(e) => updateConfig({ content_hints: e.target.value })} placeholder="Hướng dẫn AI trả lời comment cho fanpage" />
    </div>
  );
};

const FanpageLikeCommentsPanel: React.FC<{ node: WorkflowNode; onChange: (config: any) => void }> = ({ node, onChange }) => {
  const rawConfig = node.data.fanpage_like_comments_config || node.data.config || {};
  const legacyPostURL = (rawConfig as Record<string, any>).post_url || '';
  const config: FanpageLikeCommentsConfig = {
    page_ids: rawConfig.page_ids || [],
    target_post_mode: rawConfig.target_post_mode || 'latest_posts',
    post_ids: rawConfig.post_ids || [],
    selected_page_id: rawConfig.selected_page_id || '',
    selected_post_id: rawConfig.selected_post_id || '',
    selected_post_url: rawConfig.selected_post_url || legacyPostURL || '',
    max_posts_to_scan: rawConfig.max_posts_to_scan || 3,
    feed_fetch_limit: rawConfig.feed_fetch_limit || 10,
    schedule_mode: rawConfig.schedule_mode || 'all',
    comment_filter_mode: rawConfig.comment_filter_mode || 'all',
    comment_keywords: rawConfig.comment_keywords || [],
    max_comments_to_like: rawConfig.max_comments_to_like || 5,
  };
  const updateConfig = (updates: Partial<FanpageLikeCommentsConfig>) => onChange({ ...node.data, fanpage_like_comments_config: { ...config, ...updates } });
  return (
    <div className="space-y-3">
      <SectionTitle>Fanpage Like Comments</SectionTitle>
      <FanpageSelectedPostFields config={config} updateConfig={updateConfig} showCommentsPreview />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-sm mb-1">Filter Mode</label>
          <select className="w-full px-2 py-1 border rounded" value={config.comment_filter_mode || 'all'} onChange={(e) => updateConfig({ comment_filter_mode: e.target.value as any })}>
            <option value="all">All comments</option>
            <option value="keyword">Keyword match</option>
          </select>
        </div>
        <div>
          <label className="block text-sm mb-1">Max Comments</label>
          <Input type="number" min={1} value={String(config.max_comments_to_like || 5)} onChange={(e) => updateConfig({ max_comments_to_like: Number(e.target.value || 1) })} />
        </div>
      </div>
      {config.comment_filter_mode === 'keyword' && <TextArea rows={3} value={(config.comment_keywords || []).join('\n')} onChange={(e) => updateConfig({ comment_keywords: e.target.value.split('\n').map((item) => item.trim()) })} placeholder="Mỗi dòng là một keyword comment" />}
    </div>
  );
};

const FanpageModerateCommentsPanel: React.FC<{ node: WorkflowNode; onChange: (config: any) => void }> = ({ node, onChange }) => {
  const rawConfig = node.data.fanpage_moderate_comments_config || node.data.config || {};
  const legacyPostURL = (rawConfig as Record<string, any>).post_url || '';
  const config: FanpageModerateCommentsConfig = {
    page_ids: rawConfig.page_ids || [],
    target_post_mode: rawConfig.target_post_mode || 'latest_posts',
    post_ids: rawConfig.post_ids || [],
    selected_page_id: rawConfig.selected_page_id || '',
    selected_post_id: rawConfig.selected_post_id || '',
    selected_post_url: rawConfig.selected_post_url || legacyPostURL || '',
    max_posts_to_scan: rawConfig.max_posts_to_scan || 3,
    feed_fetch_limit: rawConfig.feed_fetch_limit || 10,
    schedule_mode: rawConfig.schedule_mode || 'all',
    moderation_action: rawConfig.moderation_action || 'hide',
    comment_filter_mode: rawConfig.comment_filter_mode || 'keyword',
    comment_keywords: rawConfig.comment_keywords || [],
    max_comments_to_moderate: rawConfig.max_comments_to_moderate || 5,
  };
  const updateConfig = (updates: Partial<FanpageModerateCommentsConfig>) => onChange({ ...node.data, fanpage_moderate_comments_config: { ...config, ...updates } });
  return (
    <div className="space-y-3">
      <SectionTitle>Fanpage Moderate Comments</SectionTitle>
      <FanpageSelectedPostFields config={config} updateConfig={updateConfig} showCommentsPreview />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-sm mb-1">Action</label>
          <select className="w-full px-2 py-1 border rounded" value={config.moderation_action || 'hide'} onChange={(e) => updateConfig({ moderation_action: e.target.value as any })}>
            <option value="hide">Hide</option>
            <option value="unhide">Unhide</option>
            <option value="delete">Delete</option>
          </select>
        </div>
        <div>
          <label className="block text-sm mb-1">Max Comments</label>
          <Input type="number" min={1} value={String(config.max_comments_to_moderate || 5)} onChange={(e) => updateConfig({ max_comments_to_moderate: Number(e.target.value || 1) })} />
        </div>
      </div>
      <TextArea rows={3} value={(config.comment_keywords || []).join('\n')} onChange={(e) => updateConfig({ comment_keywords: e.target.value.split('\n').map((item) => item.trim()) })} placeholder="Mỗi dòng là một keyword comment để moderate" />
    </div>
  );
};

const FanpagePrivateReplyPanel: React.FC<{ node: WorkflowNode; onChange: (config: any) => void }> = ({ node, onChange }) => {
  const rawConfig = node.data.fanpage_private_reply_config || node.data.config || {};
  const legacyPostURL = (rawConfig as Record<string, any>).post_url || '';
  const config: FanpagePrivateReplyConfig = {
    page_ids: rawConfig.page_ids || [],
    target_post_mode: rawConfig.target_post_mode || 'latest_posts',
    post_ids: rawConfig.post_ids || [],
    selected_page_id: rawConfig.selected_page_id || '',
    selected_post_id: rawConfig.selected_post_id || '',
    selected_post_url: rawConfig.selected_post_url || legacyPostURL || '',
    max_posts_to_scan: rawConfig.max_posts_to_scan || 3,
    feed_fetch_limit: rawConfig.feed_fetch_limit || 10,
    schedule_mode: rawConfig.schedule_mode || 'all',
    message_source_mode: rawConfig.message_source_mode || 'ai',
    manual_message: rawConfig.manual_message || '',
    content_hints: rawConfig.content_hints || '',
    brand_info_node_id: rawConfig.brand_info_node_id,
    comment_filter_mode: rawConfig.comment_filter_mode || 'keyword',
    comment_keywords: rawConfig.comment_keywords || [],
    max_messages_per_post: rawConfig.max_messages_per_post || 3,
    reply_max_per_keyword: rawConfig.reply_max_per_keyword || 1,
  };
  const updateConfig = (updates: Partial<FanpagePrivateReplyConfig>) => onChange({ ...node.data, fanpage_private_reply_config: { ...config, ...updates } });
  return (
    <div className="space-y-3">
      <SectionTitle>Fanpage Private Reply</SectionTitle>
      <BrandInfoSelector value={config.brand_info_node_id} onChange={(brand_info_node_id) => updateConfig({ brand_info_node_id })} />
      <FanpageSelectedPostFields config={config} updateConfig={updateConfig} showCommentsPreview />
      <div>
        <label className="block text-sm mb-1">Message Source</label>
        <select className="w-full px-2 py-1 border rounded" value={config.message_source_mode || 'ai'} onChange={(e) => updateConfig({ message_source_mode: e.target.value as any })}>
          <option value="ai">AI</option>
          <option value="manual">Manual</option>
        </select>
      </div>
      {config.message_source_mode === 'manual' ? (
        <TextArea rows={4} value={config.manual_message || ''} onChange={(e) => updateConfig({ manual_message: e.target.value })} placeholder="Tin nhắn riêng sẽ gửi cho người bình luận" />
      ) : (
        <TextArea rows={3} value={config.content_hints || ''} onChange={(e) => updateConfig({ content_hints: e.target.value })} placeholder="Hướng dẫn AI viết private reply" />
      )}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-sm mb-1">Max Messages</label>
          <Input type="number" min={1} value={String(config.max_messages_per_post || 3)} onChange={(e) => updateConfig({ max_messages_per_post: Number(e.target.value || 1) })} />
        </div>
        <div>
          <label className="block text-sm mb-1">Max per Keyword</label>
          <Input type="number" min={0} value={String(config.reply_max_per_keyword || 1)} onChange={(e) => updateConfig({ reply_max_per_keyword: Number(e.target.value || 0) })} />
        </div>
      </div>
      <TextArea rows={3} value={(config.comment_keywords || []).join('\n')} onChange={(e) => updateConfig({ comment_keywords: e.target.value.split('\n').map((item) => item.trim()) })} placeholder="Mỗi dòng là một keyword comment" />
    </div>
  );
};

// ============================================================================
// FILTER POST PANEL
// ============================================================================

const FilterPostPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.filter_post_config || node.data.config || {};
  const config = {
    source_type: rawConfig.source_type || 'keyword',
    search_mode: rawConfig.search_mode || 'keyword',
    keyword: rawConfig.keyword || '',
    recent_only: rawConfig.recent_only ?? true,
    max_posts: rawConfig.max_posts || 15,
    max_days: rawConfig.max_days || 3,
  };

  const updateConfig = (updates: any) => {
    onChange({
      ...node.data,
      filter_post_config: { ...config, ...updates },
    });
  };

  const { t } = useTranslation('workflow');

  const isHashtag = config.search_mode === 'hashtag';

  // Display value in the input: strip # and convert _ → space for hashtag mode
  const keywordDisplayValue = isHashtag && config.keyword.startsWith('#')
    ? config.keyword.slice(1).replace(/_/g, ' ')
    : config.keyword;

  const handleKeywordChange = (raw: string) => {
    if (isHashtag) {
      const formatted = '#' + raw.replace(/\s+/g, '_');
      updateConfig({ keyword: formatted });
    } else {
      updateConfig({ keyword: raw });
    }
  };

  const handleSearchModeChange = (mode: 'keyword' | 'hashtag') => {
    let newKeyword = config.keyword;
    if (mode === 'hashtag') {
      // Convert current keyword → hashtag format
      const base = newKeyword.startsWith('#') ? newKeyword.slice(1) : newKeyword;
      newKeyword = '#' + base.trim().replace(/\s+/g, '_');
    } else {
      // Convert hashtag → plain keyword (strip # and replace _ with space)
      if (newKeyword.startsWith('#')) {
        newKeyword = newKeyword.slice(1).replace(/_/g, ' ');
      }
    }
    updateConfig({ search_mode: mode, keyword: newKeyword });
  };

  return (
    <div className="space-y-3">
      <SectionTitle>{t('filterPosts')}</SectionTitle>

      <div>
        <label className="block text-sm font-semibold mb-1">{t('sourceType')}</label>
        <select
          className="w-full px-2 py-1 border rounded text-sm"
          value={config.source_type}
          onChange={(e) => updateConfig({ source_type: e.target.value })}
        >
          <option value="keyword">{t('byKeyword')}</option>
          <option value="my_groups">{t('fromMyGroups')}</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          {config.source_type === 'keyword'
            ? t('byKeywordHint')
            : t('fromMyGroupsHint')}
        </p>
      </div>

      {/* Search mode: keyword vs hashtag (only for keyword source_type) */}
      {config.source_type === 'keyword' && (
        <div>
          <label className="block text-sm font-semibold mb-1">Chế độ tìm kiếm</label>
          <div className="flex gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={`search-mode-${node.id}`}
                value="keyword"
                checked={!isHashtag}
                onChange={() => handleSearchModeChange('keyword')}
              />
              <span className="text-sm">Từ khóa thường</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={`search-mode-${node.id}`}
                value="hashtag"
                checked={isHashtag}
                onChange={() => handleSearchModeChange('hashtag')}
              />
              <span className="text-sm">Hashtag (#)</span>
            </label>
          </div>
        </div>
      )}

      {/* Keyword / Hashtag input */}
      <div>
        <label className="block text-sm font-semibold mb-1">
          {isHashtag ? 'Hashtag' : t('keyword')}
        </label>
        {isHashtag ? (
          <div className="flex items-center border rounded overflow-hidden">
            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-sm font-mono select-none">#</span>
            <input
              type="text"
              className="flex-1 px-2 py-1 text-sm outline-none"
              placeholder="thời_trang_mùa_hè"
              value={keywordDisplayValue}
              onChange={(e) => handleKeywordChange(e.target.value)}
            />
          </div>
        ) : (
          <input
            type="text"
            className="w-full px-2 py-1 border rounded text-sm"
            placeholder={t('keywordPlaceholder')}
            value={config.keyword}
            onChange={(e) => handleKeywordChange(e.target.value)}
          />
        )}
        <p className="text-xs text-gray-500 mt-1">
          {config.source_type === 'my_groups'
            ? 'Bắt buộc: keyword dùng để lọc bài viết trong từng nhóm đã tham gia.'
            : isHashtag
              ? `Hashtag sẽ tìm: ${config.keyword || '#...'} — khoảng cách tự chuyển thành _`
              : t('byKeywordHint')}
        </p>
      </div>

      <Input
        label={t('maxPosts')}
        type="number"
        min={1}
        max={50}
        value={config.max_posts}
        onChange={(e) => updateConfig({ max_posts: parseInt(e.target.value) || 15 })}
      />

      <Input
        label={t('maxAge')}
        type="number"
        min={1}
        max={30}
        value={config.max_days}
        onChange={(e) => updateConfig({ max_days: parseInt(e.target.value) || 3 })}
      />

      {config.source_type === 'keyword' && (
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={config.recent_only}
            onChange={(e) => updateConfig({ recent_only: e.target.checked })}
          />
          <span className="text-sm">{t('recentOnly')}</span>
        </label>
      )}

      <div className="mt-3 p-2 bg-blue-50 rounded text-xs">
        <p className="text-sm font-semibold mb-1">{t('outputSection')}</p>
        <p className="text-gray-600">{t('filterOutputHint')}</p>
      </div>
    </div>
  );
};

// ============================================================================
// AI TEXT PROCESS PANEL
// ============================================================================

const AITextProcessPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawCfg = node.data.ai_text_process_config || node.data.config || {};
  const cfg = {
    input_source: (rawCfg.input_source ?? 'auto') as string,
    input_text: (rawCfg.input_text ?? '') as string,
    prompt_template: (rawCfg.prompt_template ?? '') as string,
    output_shared_key: (rawCfg.output_shared_key ?? 'post_text') as string,
    task_type: (rawCfg.task_type ?? 'text.generate') as string,
  };

  const update = (updates: Partial<typeof cfg>) => {
    onChange({ ...node.data, ai_text_process_config: { ...cfg, ...updates } });
  };

  const { t } = useTranslation('workflow');

  return (
    <div className="space-y-3">
      <SectionTitle>{t('aiTextProcess')}</SectionTitle>

      <div className="p-2 bg-purple-50 rounded text-xs">
        <p className="text-sm font-semibold mb-1">{t('aiTextHowItWorks')}</p>
        <p className="text-gray-600">
          {t('aiTextDescription')}
        </p>
      </div>

      {/* Input Source */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">{t('inputSource')}</label>
        <select
          className="w-full border rounded px-2 py-1 text-sm"
          value={cfg.input_source}
          onChange={e => update({ input_source: e.target.value })}
        >
          <option value="auto">{t('inputSourceAuto')}</option>
          <option value="shared_remake_caption">{t('inputSourceRemakeCaption')}</option>
          <option value="shared_post_text">{t('inputSourcePostText')}</option>
        </select>
        <p className="text-xs text-gray-500 mt-0.5">
          {t('inputSourceAutoHint')}
        </p>
      </div>

      {/* Optional direct input override */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          {t('overrideInput')}
        </label>
        <textarea
          className="w-full border rounded px-2 py-1 text-sm"
          rows={2}
          placeholder={t('overrideInputPlaceholder')}
          value={cfg.input_text}
          onChange={e => update({ input_text: e.target.value })}
        />
        <p className="text-xs text-gray-500 mt-0.5">{t('overrideInputHint')}</p>
      </div>

      {/* Prompt template */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          {t('promptTemplate')} <span className="text-gray-400">{t('promptTemplatePlaceholderNote')}</span>
        </label>
        <textarea
          className="w-full border rounded px-2 py-1 text-sm font-mono"
          rows={3}
          placeholder={t('promptTemplatePlaceholder')}
          value={cfg.prompt_template}
          onChange={e => update({ prompt_template: e.target.value })}
        />
      </div>

      {/* Output key */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          {t('outputSharedKey')}
        </label>
        <input
          type="text"
          className="w-full border rounded px-2 py-1 text-sm font-mono"
          value={cfg.output_shared_key}
          onChange={e => update({ output_shared_key: e.target.value || 'post_text' })}
        />
        <p className="text-xs text-gray-500 mt-0.5">
          {t('outputKeyHint', { key: cfg.output_shared_key })}
        </p>
      </div>

      {/* Output info */}
      <div className="mt-1 p-2 bg-green-50 rounded text-xs">
        <p className="text-xs font-semibold mb-1">{t('aiTextOutputSection')}</p>
        <p className="text-gray-600">
          {t('aiTextOutputDescription', { key: cfg.output_shared_key })}
        </p>
      </div>
    </div>
  );
};

// ============================================================================
// AI GENERATE ASSET PANEL
// ============================================================================

/** Derives which context inputs a task_type requires from the upstream node. */
function getTaskInputRequirements(taskType: string): { needsImage: boolean; needsPrompt: boolean } {
  const needsImage = taskType.startsWith('image.transform') || taskType.startsWith('image.collect');
  const needsPrompt = !taskType.startsWith('image.collect');
  return { needsImage, needsPrompt };
}

const AIGenerateAssetPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.ai_generate_asset_config || node.data.config || {};
  const config: AIGenerateAssetConfig = {
    task_type: rawConfig.task_type ?? '',
  };

  const [workflows, setWorkflows] = React.useState<import('./api').AIWorkflowSummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [fetchError, setFetchError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setLoading(true);
    setFetchError(null);
    AIWorkflowAPI.list()
      .then((wfs) => {
        setWorkflows(wfs);
        // If no task_type set yet and workflows loaded, default to first
        if (!config.task_type && wfs.length > 0) {
          onChange({ ...node.data, ai_generate_asset_config: { task_type: wfs[0].task_type } });
        }
      })
      .catch((err) => {
        setFetchError(err?.message ?? 'Failed to load AI workflows');
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateConfig = (updates: Partial<AIGenerateAssetConfig>) => {
    onChange({ ...node.data, ai_generate_asset_config: { ...config, ...updates } });
  };

  const selectedWorkflow = workflows.find((w) => w.task_type === config.task_type);
  const { needsImage, needsPrompt } = getTaskInputRequirements(config.task_type);
  const { t } = useTranslation('workflow');

  return (
    <div className="space-y-3">
      <SectionTitle>{t('aiGenerateAsset')}</SectionTitle>

      {/* How it works */}
      <div className="p-2 bg-purple-50 rounded text-xs">
        <p className="text-sm font-semibold mb-1">{t('aiGenerateHowItWorks')}</p>
        <p className="text-gray-600">
          {t('aiGenerateDescription')}
        </p>
      </div>

      {/* Task Type selector */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          {t('taskTypeLabel')}
          {loading && <span className="ml-2 text-gray-400 text-xs">{t('aiTaskLoading')}</span>}
        </label>

        {fetchError ? (
          <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700">
            ⚠️ {fetchError}
          </div>
        ) : (
          <select
            className="w-full border rounded px-2 py-1 text-sm"
            value={config.task_type}
            disabled={loading}
            onChange={(e) => updateConfig({ task_type: e.target.value })}
          >
            {!config.task_type && <option value="">{t('selectTaskType')}</option>}
            {workflows.map((wf) => (
              <option key={wf.task_type} value={wf.task_type}>
                {wf.name} ({wf.task_type})
              </option>
            ))}
          </select>
        )}

        {selectedWorkflow?.description && (
          <p className="mt-1 text-xs text-gray-500">{selectedWorkflow.description}</p>
        )}
        {selectedWorkflow?.provider && (
          <p className="mt-0.5 text-xs text-gray-400">
            {t('providerHint', { provider: selectedWorkflow.provider })}
          </p>
        )}
      </div>

      {/* Required inputs info — based on selected task_type */}
      {config.task_type && (
        <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs">
          <p className="text-xs font-semibold text-blue-800 mb-1.5">{t('aiInputSection')}</p>
          <ul className="space-y-1">
            {needsImage && (
              <li className="flex items-start gap-1.5">
                <span className="text-blue-500 mt-0.5">▸</span>
                <span className="text-blue-700">
                  <strong>image_path</strong> — {t('imageInputHint')}
                </span>
              </li>
            )}
            {needsPrompt && (
              <li className="flex items-start gap-1.5">
                <span className="text-blue-500 mt-0.5">▸</span>
                <span className="text-blue-700">
                  <strong>prompt</strong> / <strong>brand_info</strong> — {t('promptInputHint')}
                </span>
              </li>
            )}
            {!needsImage && !needsPrompt && (
              <li className="text-blue-600">{t('noInputNeeded')}</li>
            )}
          </ul>
        </div>
      )}

      {/* No task type selected warning */}
      {!loading && !fetchError && !config.task_type && workflows.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-700">
          ⚠️ {t('noAIWorkflows')}
        </div>
      )}

      {/* Output */}
      <div className="mt-2 p-2 bg-green-50 rounded text-xs">
        <p className="text-xs font-semibold mb-1">{t('aiOutputSection')}</p>
        <p className="text-gray-600">
          {t('aiGenerateOutputDescription')}
        </p>
      </div>
    </div>
  );
};

// ============================================================================
// AI REMAKE IMAGE PANEL
// ============================================================================

const AIRemakeImagePanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const [showPrompt, setShowPrompt] = React.useState(false);
  const rawConfig = node.data.ai_remake_image_config || node.data.ai_grok_remake_image_config || node.data.config || {};
  const config: AIRemakeImageConfig = {
    ai_provider: (rawConfig.ai_provider === 'gemini' ? 'gemini' : 'grok'),
    model: rawConfig.model ?? '',
  };
  const { t } = useTranslation('workflow');

  const updateConfig = (updates: Partial<AIRemakeImageConfig>) => {
    const next = { ...config, ...updates };
    onChange({
      ...node.data,
      ai_remake_image_config: next,
      ai_grok_remake_image_config: next,
    });
  };

  return (
    <div className="space-y-3">
      <SectionTitle>{t('aiRemakeImage')}</SectionTitle>

      {/* Config badge */}
      <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
        <span className="text-lg">⚙️</span>
        <div>
          <div className="text-sm font-semibold text-blue-900">{t('remakeConfigBadge')}</div>
          <div className="text-xs text-blue-700">{t('remakeConfigHint')}</div>
        </div>
      </div>

      {/* Pipeline flow */}
      <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-xs">
        <p className="text-xs font-semibold text-blue-900 mb-2">{t('pipelineTitle')}</p>
        <div className="space-y-1 text-blue-800">
          <div className="flex items-center gap-2">
            <span className="bg-blue-200 rounded px-1 font-mono">find_trend_fb</span>
            <span>→ {t('pipelineStep1')}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="bg-purple-200 rounded px-1 font-mono">{config.ai_provider === 'gemini' ? 'image.transform' : 'image.transform.grok'}</span>
            <span>→ {t('pipelineStep3')}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="bg-gray-200 rounded px-1 font-mono">wkr-ai-controller</span>
            <span>→ {t('pipelineStep4')}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="bg-green-200 rounded px-1 font-mono">post node</span>
            <span>→ {t('aiRemakeOutputDescription')}</span>
          </div>
        </div>
      </div>

      {/* Input */}
      <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs">
        <p className="text-xs font-semibold text-amber-900 mb-1">{t('aiRemakeInput')}</p>
        <ul className="space-y-0.5 text-amber-800">
          <li>▸ <code>shared['image_path']</code> — {t('aiRemakeImagePath')}</li>
          <li>▸ <code>shared['image_urls']</code> — {t('aiRemakeImageUrls')}</li>
        </ul>
        <p className="mt-1 text-amber-700">{t('aiRemakeDependency')}</p>
      </div>

      {/* Model / task */}
      <div className="p-2 bg-gray-50 border border-gray-200 rounded text-xs space-y-1">
        <div>
          <label className="block text-xs text-gray-600 mb-1">{t('providerLabel')}</label>
          <select
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
            value={config.ai_provider || 'grok'}
            onChange={(e) => updateConfig({ ai_provider: (e.target.value as 'grok' | 'gemini') })}
          >
            <option value="grok">Grok</option>
            <option value="gemini">Gemini</option>
          </select>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('taskTypeLabel')}</span>
          <code className="text-purple-700">{config.ai_provider === 'gemini' ? 'image.transform' : 'image.transform.grok'}</code>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">{t('modelLabel')}</label>
          <Input
            value={config.model || ''}
            onChange={(e) => updateConfig({ model: e.target.value })}
            placeholder={t('optionalModelPlaceholder')}
          />
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('promptLabel')}</span>
          <button
            className="text-blue-600 underline text-xs"
            onClick={() => setShowPrompt(p => !p)}
          >
            {showPrompt ? t('hidePrompt') : t('showPrompt')}
          </button>
        </div>
        {showPrompt && (
          <pre className="mt-1 p-2 bg-white border rounded text-xs text-gray-700 whitespace-pre-wrap break-words leading-relaxed">
            {`Create an illustrative image following STRICT DECISION TREE ANALYSIS:
START: Analyze the provided reference image(s)
1. CHARACTERS (HUMAN/AI): Replace central character if needed, preserve background.
2. MAIN SUBJECT: Refine subject, preserve layout.
3. TEXT: Extract exact text. DO NOT change spelling.
4. BRANDING: Preserve primary brand logo.
5. STRUCTURE: LOCK layout structure.
6. TECHNICAL: Preserve perspective & lighting mood.
7. OUTPUT: Generate EXACTLY ONE (1) high-quality image.
END -> Output masterpiece.`}
          </pre>
        )}
      </div>

      {/* Output */}
      <div className="mt-1 p-2 bg-green-50 rounded text-xs">
        <p className="text-xs font-semibold mb-1">{t('aiRemakeOutput')}</p>
        <p className="text-gray-600">
          {t('aiRemakeOutputDescription')}
        </p>
        <p className="text-gray-500 mt-1">
          {t('aiRemakeUsageHint')}
        </p>
      </div>
    </div>
  );
};

// ============================================================================
// FIND TREND FB PANEL
// ============================================================================

// ============================================================================
// FIND GROUP POSTS PANEL
// ============================================================================

const FindGroupPostsPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.find_group_posts_config || node.data.config || {};
  const config: FindGroupPostsConfig = {
    group_urls: rawConfig.group_urls ?? [],
    search_mode: rawConfig.search_mode ?? 'smart_ai',
    criteria: rawConfig.criteria ?? '',
    days_back: rawConfig.days_back ?? undefined,
    requirement: rawConfig.requirement ?? 'default',
    max_scrolls_per_group: rawConfig.max_scrolls_per_group ?? undefined,
  };

  const updateConfig = (updates: Partial<FindGroupPostsConfig>) => {
    // Send partial patch only; store layer merges with current node state
    // to avoid stale snapshot overwriting recent fields (e.g. days_back).
    onChange({ find_group_posts_config: updates });
  };

  // Local state for URL input box
  const [urlInput, setUrlInput] = React.useState('');
  const { t } = useTranslation('workflow');

  const addGroupURL = () => {
    const url = urlInput.trim();
    if (!url) return;
    if (!config.group_urls.includes(url)) {
      updateConfig({ group_urls: [...config.group_urls, url] });
    }
    setUrlInput('');
  };

  const removeGroupURL = (index: number) => {
    updateConfig({ group_urls: config.group_urls.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-3">
      <SectionTitle>{t('findGroupPosts')}</SectionTitle>

      {/* Group URLs */}
      <div>
        <label className="block text-sm font-semibold mb-1">
          {t('groupUrls')} <span className="text-red-500">{t('required')}</span>
        </label>
        <div className="flex gap-2 mb-1">
          <input
            className="flex-1 px-2 py-1 border rounded text-sm"
            type="text"
            placeholder={t('groupUrlPlaceholderFGP')}
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGroupURL(); } }}
          />
          <button
            type="button"
            className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={addGroupURL}
          >
            Add
          </button>
        </div>
        {config.group_urls.length === 0 && (
          <p className="text-xs text-red-500">{t('urlRequired')}</p>
        )}
        <div className="space-y-1 mt-1">
          {config.group_urls.map((url, i) => (
            <div key={i} className="flex items-center gap-2 bg-gray-50 rounded px-2 py-1 text-xs">
              <span className="flex-1 truncate text-blue-700">{url}</span>
              <button
                type="button"
                className="text-red-400 hover:text-red-600 font-bold"
                onClick={() => removeGroupURL(i)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Content Criteria */}
      <div>
        <label className="block text-sm font-semibold mb-1">{t('fgpSearchMode')}</label>
        <div className="space-y-1 mb-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name={`fgp-search-mode-${node.id}`}
              checked={(config.search_mode || 'smart_ai') === 'smart_ai'}
              onChange={() => updateConfig({ search_mode: 'smart_ai' })}
            />
            <span>{t('fgpSearchModeSmartAI')}</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name={`fgp-search-mode-${node.id}`}
              checked={(config.search_mode || 'smart_ai') === 'keyword'}
              onChange={() => updateConfig({ search_mode: 'keyword' })}
            />
            <span>{t('fgpSearchModeKeyword')}</span>
          </label>
        </div>

        <label className="block text-sm font-semibold mb-1">{t('contentCriteria')}</label>
        <input
          className="w-full px-2 py-1 border rounded text-sm"
          type="text"
          placeholder={t('criteriaPlaceholder')}
          value={config.criteria}
          onChange={(e) => updateConfig({ criteria: e.target.value })}
        />
        <p className="text-xs text-gray-500 mt-1">
          {(config.search_mode || 'smart_ai') === 'keyword' ? t('criteriaHintKeywordOnly') : t('criteriaHint')}
        </p>
      </div>

      {/* Lookback window */}
      <div>
        <label className="block text-sm font-semibold mb-1">
          {t('daysBack')}
        </label>
        <input
          className="w-full px-2 py-1 border rounded text-sm"
          type="number"
          min={0}
          max={60}
          placeholder={t('daysBackPlaceholder')}
          value={config.days_back ?? ''}
          onChange={(e) => {
            const raw = e.target.value.trim();
            if (raw === '') {
              updateConfig({ days_back: undefined as any });
            } else {
              updateConfig({ days_back: Math.max(0, parseInt(raw) || 0) });
            }
          }}
        />
        <p className="text-xs text-gray-500 mt-1">
          {t('daysBackHint')}
        </p>
      </div>

      {/* Requirement label */}
      <div>
        <label className="block text-sm font-semibold mb-1">{t('requirementLabel')}</label>
        <input
          className="w-full px-2 py-1 border rounded text-sm"
          type="text"
          placeholder="default"
          value={config.requirement}
          onChange={(e) => updateConfig({ requirement: e.target.value || 'default' })}
        />
        <p className="text-xs text-gray-500 mt-1">
          {t('requirementHint')}
        </p>
      </div>

      {/* Optional: max scrolls override */}
      <div>
        <label className="block text-sm font-semibold mb-1">
          {t('maxScrollsPerGroup')} <span className="text-gray-400 font-normal">{t('autoOption')}</span>
        </label>
        <input
          className="w-full px-2 py-1 border rounded text-sm"
          type="number"
          min={0}
          max={60}
          value={config.max_scrolls_per_group ?? 0}
          onChange={(e) => {
            const v = Math.max(0, parseInt(e.target.value) || 0);
            updateConfig({ max_scrolls_per_group: v === 0 ? undefined : v });
          }}
        />
      </div>

      {/* Output description */}
      <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
        <p className="text-sm font-semibold mb-1">{t('fgpOutput')}</p>
        <p className="text-gray-600">
          {t('fgpOutputDescription')}
        </p>
      </div>
    </div>
  );
};

// ============================================================================
// FIND TOP POSTS PANEL
// ============================================================================

const FindTopPostsPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.find_top_posts_config || node.data.config || {};
  const parsedSourceUrls = Array.isArray(rawConfig.source_urls)
    ? rawConfig.source_urls.filter((u: any) => typeof u === 'string' && u.trim()).map((u: string) => u.trim())
    : [];
  const sourceUrls = parsedSourceUrls.length > 0
    ? parsedSourceUrls
    : (rawConfig.source_url ? [String(rawConfig.source_url).trim()] : []);

  const config: FindTopPostsConfig = {
    source_url: rawConfig.source_url ?? sourceUrls[0] ?? '',
    source_urls: sourceUrls,
    days_back: rawConfig.days_back ?? 7,
    keyword: rawConfig.keyword ?? '',
    top_n: rawConfig.top_n ?? 1,
    max_scan: rawConfig.max_scan ?? undefined,
    label: rawConfig.label ?? 'default',
  };

  const parseSourceUrls = (value: string): string[] => {
    const chunks = value
      .split(/[\n,;]+/)
      .map((v) => v.trim())
      .filter(Boolean);
    return Array.from(new Set(chunks));
  };

  const updateConfig = (updates: Partial<FindTopPostsConfig>) => {
    const merged = { ...config, ...updates };
    const normalizedUrls = Array.isArray(merged.source_urls)
      ? merged.source_urls.map((u) => String(u).trim()).filter(Boolean)
      : [];
    merged.source_urls = normalizedUrls;
    merged.source_url = normalizedUrls[0] || '';
    onChange({ ...node.data, find_top_posts_config: merged });
  };

  const { t } = useTranslation('workflow');
  const sourceUrlsText = (config.source_urls || []).join('\n');
  const [urlsRawText, setUrlsRawText] = React.useState(sourceUrlsText);

  // Sync local text when switching to a different node
  const nodeId = node.id;
  React.useEffect(() => {
    setUrlsRawText((config.source_urls || []).join('\n'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId]);

  return (
    <div className="space-y-3">
      <SectionTitle>{t('findTopPosts')}</SectionTitle>

      {/* Source URLs */}
      <div>
        <label className="block text-sm font-semibold mb-1">
          {t('sourceUrls')} <span className="text-red-500">{t('required')}</span>
        </label>
        <textarea
          className="w-full px-2 py-1 border rounded text-sm"
          rows={4}
          placeholder={t('sourceUrlsPlaceholder')}
          value={urlsRawText}
          onChange={(e) => setUrlsRawText(e.target.value)}
          onBlur={(e) => updateConfig({ source_urls: parseSourceUrls(e.target.value) })}
        />
        {(!config.source_urls || config.source_urls.length === 0) && (
          <p className="text-xs text-red-500 mt-1">{t('val_sourceUrlRequired')}</p>
        )}
        <p className="text-xs text-gray-500 mt-1">{t('sourceUrlsHint')}</p>
      </div>

      {/* Lookback days */}
      <div>
        <label className="block text-sm font-semibold mb-1">
          {t('daysBack')} <span className="text-red-500">{t('required')}</span>
        </label>
        <input
          className="w-full px-2 py-1 border rounded text-sm"
          type="number"
          min={1}
          max={30}
          placeholder="7"
          value={config.days_back ?? ''}
          onChange={(e) => {
            const raw = e.target.value.trim();
            if (raw === '') {
              updateConfig({ days_back: undefined as any });
            } else {
              updateConfig({ days_back: Math.max(1, Math.min(30, parseInt(raw) || 7)) });
            }
          }}
        />
        <p className="text-xs text-gray-500 mt-1">{t('daysBackFtpHint')}</p>
      </div>

      {/* Keyword filter */}
      <div>
        <label className="block text-sm font-semibold mb-1">
          {t('keyword')} <span className="text-gray-400 font-normal">{t('optionalLabel')}</span>
        </label>
        <input
          className="w-full px-2 py-1 border rounded text-sm"
          type="text"
          placeholder={t('keywordPlaceholder')}
          value={config.keyword ?? ''}
          onChange={(e) => updateConfig({ keyword: e.target.value })}
        />
        <p className="text-xs text-gray-500 mt-1">{t('ftpKeywordHint')}</p>
      </div>

      {/* Top N */}
      <div>
        <label className="block text-sm font-semibold mb-1">{t('topN')}</label>
        <input
          className="w-full px-2 py-1 border rounded text-sm"
          type="number"
          min={1}
          max={50}
          value={config.top_n ?? 1}
          onChange={(e) => updateConfig({ top_n: Math.max(1, parseInt(e.target.value) || 1) })}
        />
        <p className="text-xs text-gray-500 mt-1">{t('topNHint')}</p>
      </div>

      {/* Max scan */}
      <div>
        <label className="block text-sm font-semibold mb-1">
          {t('maxScan')} <span className="text-gray-400 font-normal">{t('autoOption')}</span>
        </label>
        <input
          className="w-full px-2 py-1 border rounded text-sm"
          type="number"
          min={0}
          max={500}
          placeholder="500"
          value={config.max_scan ?? ''}
          onChange={(e) => {
            const v = parseInt(e.target.value) || 0;
            updateConfig({ max_scan: v <= 0 ? undefined : Math.min(500, v) });
          }}
        />
        <p className="text-xs text-gray-500 mt-1">{t('maxScanHint')}</p>
      </div>

      {/* Label */}
      <div>
        <label className="block text-sm font-semibold mb-1">{t('requirementLabel')}</label>
        <input
          className="w-full px-2 py-1 border rounded text-sm"
          type="text"
          placeholder="default"
          value={config.label ?? 'default'}
          onChange={(e) => updateConfig({ label: e.target.value || 'default' })}
        />
        <p className="text-xs text-gray-500 mt-1">{t('requirementHint')}</p>
      </div>

      {/* Output description */}
      <div className="mt-2 p-2 bg-emerald-50 rounded text-xs">
        <p className="text-sm font-semibold mb-1">{t('ftpOutput')}</p>
        <p className="text-gray-600">{t('ftpOutputDescription')}</p>
      </div>
    </div>
  );
};

// ============================================================================
// FIND TREND FB PANEL
// ============================================================================

const FindTrendFBPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.find_trend_fb_config || node.data.config || {};
  const config: FindTrendFBConfig = {
    keyword: rawConfig.keyword ?? '',
    discovery_mode: (rawConfig.discovery_mode || 'personal') as 'personal' | 'group' | 'fanpage',
    max_scrolls: rawConfig.max_scrolls ?? 5,
    group_search_url: rawConfig.group_search_url ?? '',
    group_count: rawConfig.group_count ?? 5,
    posts_per_group: rawConfig.posts_per_group ?? 20,
    page_search_url: rawConfig.page_search_url ?? '',
    page_count: rawConfig.page_count ?? 5,
    posts_per_page: rawConfig.posts_per_page ?? 20,
  };

  const updateConfig = (updates: Partial<FindTrendFBConfig>) => {
    onChange({ ...node.data, find_trend_fb_config: { ...config, ...updates } });
  };
  const { t } = useTranslation('workflow');

  return (
    <div className="space-y-3">
      <SectionTitle>{t('findTrendFB')}</SectionTitle>

      <div className="p-2 bg-blue-50 rounded text-xs">
        <p className="text-sm font-semibold mb-1">{t('hashtagHow')}</p>
        <p className="text-gray-600">
          {t('hashtagHint')}
        </p>
      </div>

      <Input
        label={t('hashtagKeyword')}
        type="text"
        placeholder={t('hashtagKeywordPlaceholder')}
        value={config.keyword || ''}
        onChange={(e) => updateConfig({ keyword: e.target.value })}
      />

      <div>
        <label className="block text-sm font-semibold mb-1">Discovery Mode</label>
        <div className="flex gap-4 flex-wrap text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`find-trend-discovery-mode-${node.id}`}
              value="personal"
              checked={(config.discovery_mode || 'personal') === 'personal'}
              onChange={() => updateConfig({ discovery_mode: 'personal' })}
            />
            <span>Personal Posts (current)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`find-trend-discovery-mode-${node.id}`}
              value="group"
              checked={(config.discovery_mode || 'personal') === 'group'}
              onChange={() => updateConfig({ discovery_mode: 'group' })}
            />
            <span>From Facebook Groups</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`find-trend-discovery-mode-${node.id}`}
              value="fanpage"
              checked={(config.discovery_mode || 'personal') === 'fanpage'}
              onChange={() => updateConfig({ discovery_mode: 'fanpage' })}
            />
            <span>From Facebook Fanpages</span>
          </label>
        </div>
      </div>

      <Input
        label={t('scrollDepth')}
        type="number"
        min={2}
        max={15}
        value={config.max_scrolls}
        onChange={(e) => updateConfig({ max_scrolls: Math.min(15, Math.max(2, parseInt(e.target.value) || 5)) })}
        helperText={t('scrollDepthHint')}
      />

      {(config.discovery_mode || 'personal') === 'group' && (
        <>
          <Input
            label="Group Search URL (optional)"
            type="text"
            placeholder="https://www.facebook.com/search/groups?q=l%C3%A0m%20%C4%91%E1%BA%B9p"
            value={config.group_search_url || ''}
            onChange={(e) => updateConfig({ group_search_url: e.target.value })}
            helperText="Để trống để worker tự tạo URL theo keyword"
          />

          <Input
            label="Top Groups To Scan"
            type="number"
            min={1}
            max={10}
            value={config.group_count ?? 5}
            onChange={(e) => updateConfig({ group_count: Math.min(10, Math.max(1, parseInt(e.target.value) || 5)) })}
            helperText="Số group có member + bài/ngày cao nhất sẽ được scan sâu"
          />

          <Input
            label="Newest Posts Per Group"
            type="number"
            min={5}
            max={50}
            value={config.posts_per_group ?? 20}
            onChange={(e) => updateConfig({ posts_per_group: Math.min(50, Math.max(5, parseInt(e.target.value) || 20)) })}
            helperText="Số bài mới nhất để đánh giá engagement trong mỗi group"
          />
        </>
      )}

      {(config.discovery_mode || 'personal') === 'fanpage' && (
        <>
          <Input
            label="Fanpage Search URL (optional)"
            type="text"
            placeholder="https://www.facebook.com/search/pages?q=l%C3%A0m%20%C4%91%E1%BA%B9p"
            value={config.page_search_url || ''}
            onChange={(e) => updateConfig({ page_search_url: e.target.value })}
            helperText="Để trống để worker tự tạo URL theo keyword"
          />

          <Input
            label="Top Fanpages To Scan"
            type="number"
            min={1}
            max={10}
            value={config.page_count ?? 5}
            onChange={(e) => updateConfig({ page_count: Math.min(10, Math.max(1, parseInt(e.target.value) || 5)) })}
            helperText="Số fanpage có follower cao nhất sẽ được scan sâu"
          />

          <Input
            label="Newest Posts Per Fanpage"
            type="number"
            min={5}
            max={50}
            value={config.posts_per_page ?? 20}
            onChange={(e) => updateConfig({ posts_per_page: Math.min(50, Math.max(5, parseInt(e.target.value) || 20)) })}
            helperText="Số bài mới nhất để đánh giá engagement trong mỗi fanpage"
          />
        </>
      )}

      <div className="mt-3 p-2 bg-green-50 rounded text-xs">
        <p className="text-sm font-semibold mb-1">{t('findTrendOutput')}</p>
        <p className="text-gray-600">
          {t('findTrendOutputDescription')}
        </p>
      </div>
    </div>
  );
};

// ============================================================================
// SHARE URL PANEL
// ============================================================================

const ShareURLPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.share_url_config || node.data.config || {};

  const config: ShareURLConfig = {
    url: rawConfig.url || '',
    share_mode: (rawConfig.share_mode || 'with_caption') as 'with_caption' | 'silent',
    dispatch_mode: (rawConfig.dispatch_mode || 'specific') as 'specific' | 'managed' | 'group_posts' | 'filter_posts' | 'from_top_posts',
    seeding_contents_manager_node_id: rawConfig.seeding_contents_manager_node_id,
    find_group_posts_node_id: rawConfig.find_group_posts_node_id,
    filter_post_node_id: rawConfig.filter_post_node_id,
    find_top_posts_node_id: rawConfig.find_top_posts_node_id,
  };

  // Normalize: write share_mode default back into node state for nodes loaded
  // from DB that were saved without share_mode (omitempty dropped empty string)
  useEffect(() => {
    if (!rawConfig.share_mode) {
      onChange({
        ...node.data,
        share_url_config: { ...config },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id]);

  const updateConfig = (updates: Partial<ShareURLConfig>) => {
    onChange({
      ...node.data,
      share_url_config: { ...config, ...updates },
    });
  };

  const isManaged = config.dispatch_mode === 'managed';
  const isGroupPosts = config.dispatch_mode === 'group_posts';
  const isFilterPosts = config.dispatch_mode === 'filter_posts';
  const isFromTopPosts = config.dispatch_mode === 'from_top_posts';
  const isSilent = config.share_mode === 'silent';
  const { t } = useTranslation('workflow');

  return (
    <div className="space-y-3">
      <SectionTitle>{t('shareUrl')}</SectionTitle>

      {/* Dispatch Mode */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">{t('postUrlSource')}</label>
        <div className="flex gap-3 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`dispatch-mode-${node.id}`}
              value="specific"
              checked={!isManaged && !isGroupPosts && !isFilterPosts && !isFromTopPosts}
              onChange={() => updateConfig({ dispatch_mode: 'specific', seeding_contents_manager_node_id: undefined, find_group_posts_node_id: undefined, filter_post_node_id: undefined, find_top_posts_node_id: undefined })}
            />
            <span className="text-sm">{t('specificUrl')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`dispatch-mode-${node.id}`}
              value="managed"
              checked={isManaged}
              onChange={() => updateConfig({ dispatch_mode: 'managed', url: '', find_group_posts_node_id: undefined, filter_post_node_id: undefined, find_top_posts_node_id: undefined })}
            />
            <span className="text-sm">{t('managedMode')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`dispatch-mode-${node.id}`}
              value="group_posts"
              checked={isGroupPosts}
              onChange={() => updateConfig({ dispatch_mode: 'group_posts', url: '', seeding_contents_manager_node_id: undefined, filter_post_node_id: undefined, find_top_posts_node_id: undefined })}
            />
            <span className="text-sm">{t('groupPostsMode')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`dispatch-mode-${node.id}`}
              value="filter_posts"
              checked={isFilterPosts}
              onChange={() => updateConfig({ dispatch_mode: 'filter_posts', url: '', seeding_contents_manager_node_id: undefined, find_group_posts_node_id: undefined, find_top_posts_node_id: undefined })}
            />
            <span className="text-sm">{t('filterPostsMode')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`dispatch-mode-${node.id}`}
              value="from_top_posts"
              checked={isFromTopPosts}
              onChange={() => updateConfig({ dispatch_mode: 'from_top_posts', url: '', seeding_contents_manager_node_id: undefined, find_group_posts_node_id: undefined, filter_post_node_id: undefined })}
            />
            <span className="text-sm">{t('fromTopPostsMode')}</span>
          </label>
        </div>
        {isManaged && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
            {t('managedModeHint')}
          </div>
        )}
        {isGroupPosts && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
            {t('groupPostsModeHint')}
          </div>
        )}
        {isFilterPosts && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
            {t('filterPostsModeHint')}
          </div>
        )}
        {isFromTopPosts && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
            {t('fromTopPostsModeHint')}
          </div>
        )}
      </div>

      {/* SCM Selector (managed mode) */}
      {isManaged && (
        <SeedingContentsManagerSelector
          value={config.seeding_contents_manager_node_id}
          onChange={(id) => updateConfig({ seeding_contents_manager_node_id: id })}
        />
      )}

      {/* Find Group Posts Selector (group_posts mode) */}
      {isGroupPosts && (
        <FindGroupPostsSelector
          value={config.find_group_posts_node_id}
          onChange={(id) => updateConfig({ find_group_posts_node_id: id })}
        />
      )}

      {/* Filter Post Selector (filter_posts mode) */}
      {isFilterPosts && (
        <FilterPostSelector
          value={config.filter_post_node_id}
          onChange={(id) => updateConfig({ filter_post_node_id: id })}
        />
      )}

      {/* Find Top Posts Selector (from_top_posts mode) */}
      {isFromTopPosts && (
        <FindTopPostsSelector
          value={config.find_top_posts_node_id}
          onChange={(id) => updateConfig({ find_top_posts_node_id: id })}
        />
      )}

      {/* Specific: single URL */}
      {!isManaged && !isGroupPosts && !isFilterPosts && !isFromTopPosts && (
        <Input
          label={t('urlToShare')}
          type="url"
          placeholder={t('shareUrlPlaceholder')}
          value={config.url}
          onChange={(e) => updateConfig({ url: e.target.value })}
        />
      )}

      {/* Share Mode — always visible */}
      <div>
        <label className="block text-sm font-semibold mb-1">{t('shareMode')}</label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${!isSilent
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
              }`}
            onClick={() => updateConfig({ share_mode: 'with_caption' })}
          >
            {t('withCaption')}
          </button>
          <button
            type="button"
            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${isSilent
              ? 'bg-gray-700 text-white border-gray-700'
              : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'
              }`}
            onClick={() => updateConfig({ share_mode: 'silent' })}
          >
            {t('silentShare')}
          </button>
        </div>
        {!isSilent ? (
          <div className="mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
            {t('captionHint')}
          </div>
        ) : (
          <div className="mt-2 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-1">
            {t('silentHint')}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// SEARCH GROUPS PANEL
// ============================================================================

const SearchGroupsPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.search_groups_config || node.data.config || {};
  const config = {
    keywords: rawConfig.keywords || [],
    max_results: rawConfig.max_results || 20,
  };

  const [keywordInput, setKeywordInput] = React.useState('');
  const { t } = useTranslation('workflow');

  const updateConfig = (updates: any) => {
    onChange({
      ...node.data,
      search_groups_config: { ...config, ...updates },
    });
  };

  const addKeyword = () => {
    const kw = keywordInput.trim();
    if (kw && !config.keywords.includes(kw)) {
      updateConfig({ keywords: [...config.keywords, kw] });
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword: string) => {
    updateConfig({ keywords: config.keywords.filter((k: string) => k !== keyword) });
  };

  return (
    <div className="space-y-3">
      <SectionTitle>{t('searchGroups')}</SectionTitle>

      <div>
        <label className="block text-sm font-semibold mb-1">{t('keywordsLabel')}</label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            className="flex-1 px-2 py-1 border rounded text-sm"
            placeholder={t('searchKeywordPlaceholder')}
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
          />
          <Button size="sm" variant="secondary" onClick={addKeyword}>
            Add
          </Button>
        </div>
        <div className="flex flex-wrap gap-1">
          {config.keywords.map((keyword: string) => (
            <Badge key={keyword} variant="primary">
              {keyword}
              <button className="ml-1" onClick={() => removeKeyword(keyword)}>×</button>
            </Badge>
          ))}
        </div>
      </div>

      <Input
        label={t('maxResults')}
        type="number"
        min={1}
        max={50}
        value={config.max_results}
        onChange={(e) => updateConfig({ max_results: parseInt(e.target.value) || 20 })}
      />
    </div>
  );
};

// ============================================================================
// JOIN GROUPS PANEL
// ============================================================================

const JoinGroupsPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.join_groups_config || node.data.config || {};

  const config = {
    join_mode: (rawConfig.join_mode || 'random') as 'random' | 'by_url',
    max_joins: rawConfig.max_joins ?? 3,
    min_members: rawConfig.min_members ?? 0,
    keywords: rawConfig.keywords || [],
    group_urls: rawConfig.group_urls || [],
  };

  const [keywordInput, setKeywordInput] = React.useState('');
  const { t } = useTranslation('workflow');

  const updateConfig = (updates: any) => {
    onChange({
      ...node.data,
      join_groups_config: { ...config, ...updates },
    });
  };

  const isByUrl = config.join_mode === 'by_url';

  // ── keyword helpers (random mode) ──────────────────────────────────────────
  const MAX_KEYWORDS = 10;
  const isAtKeywordLimit = config.keywords.length >= MAX_KEYWORDS;

  const addKeyword = () => {
    const kw = keywordInput.trim();
    if (kw && !config.keywords.includes(kw) && !isAtKeywordLimit) {
      updateConfig({ keywords: [...config.keywords, kw] });
      setKeywordInput('');
    }
  };
  const removeKeyword = (keyword: string) => {
    updateConfig({ keywords: config.keywords.filter((k: string) => k !== keyword) });
  };

  // ── group_urls textarea helper (by_url mode) ───────────────────────────────
  const groupUrlsText = (config.group_urls as string[]).join('\n');
  const handleGroupUrlsChange = (text: string) => {
    const urls = text
      .split('\n')
      .map((l: string) => l.trim())
      .filter((l: string) => l.length > 0);
    updateConfig({ group_urls: urls });
  };

  return (
    <div className="space-y-3">
      <SectionTitle>{t('joinGroups')}</SectionTitle>

      {/* Mode toggle */}
      <div>
        <label className="block text-sm font-semibold mb-1">{t('joinMode')}</label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${!isByUrl
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
              }`}
            onClick={() => updateConfig({ join_mode: 'random' })}
          >
            {t('randomJoinMode')}
          </button>
          <button
            type="button"
            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${isByUrl
              ? 'bg-indigo-600 text-white border-indigo-600'
              : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
              }`}
            onClick={() => updateConfig({ join_mode: 'by_url' })}
          >
            {t('byUrlJoinMode')}
          </button>
        </div>
        {!isByUrl ? (
          <div className="mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
            {t('randomJoinHint')}
          </div>
        ) : (
          <div className="mt-2 text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1">
            {t('byUrlJoinHint')}
          </div>
        )}
      </div>

      {/* ── RANDOM MODE: keywords ─────────────────────────────────────────── */}
      {!isByUrl && (
        <div className="space-y-3">
          <label className="block text-sm font-semibold mb-1">
            {t('joinKeywords')}
            <span className={`ml-2 text-xs font-normal ${isAtKeywordLimit ? 'text-red-500' : 'text-gray-500'}`}>
              {config.keywords.length}/{MAX_KEYWORDS}
            </span>
            <span className="ml-1 font-normal text-gray-500 text-xs">
              {t('joinKeywordsHint')}
            </span>
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              className="flex-1 px-2 py-1 border rounded text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder={isAtKeywordLimit ? t('joinKeywordLimitReached') : t('joinKeywordPlaceholder')}
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
              disabled={isAtKeywordLimit}
            />
            <Button size="sm" variant="secondary" onClick={addKeyword} disabled={isAtKeywordLimit}>
              {t('addMore')}
            </Button>
          </div>
          <div className="flex flex-wrap gap-1">
            {config.keywords.map((keyword: string) => (
              <Badge key={keyword} variant="primary">
                {keyword}
                <button className="ml-1" onClick={() => removeKeyword(keyword)}>×</button>
              </Badge>
            ))}
          </div>

          <Input
            label={t('minimumMembers')}
            type="number"
            min={0}
            value={config.min_members}
            onChange={(e) => updateConfig({ min_members: parseInt(e.target.value) || 0 })}
          />
        </div>
      )}

      {/* ── BY URL MODE: group URL list ───────────────────────────────────── */}
      {isByUrl && (
        <div className="space-y-1">
          <label className="block text-sm font-semibold">
            {t('groupUrlList')}
            <span className="ml-1 text-red-500">{t('required')}</span>
          </label>
          <textarea
            value={groupUrlsText}
            onChange={(e) => handleGroupUrlsChange(e.target.value)}
            className="w-full p-2 border rounded font-mono text-xs"
            rows={6}
            placeholder={"https://facebook.com/groups/abc123\nhttps://facebook.com/groups/def456\n..."}
          />
          <p className="text-xs text-gray-500">
            {t('groupUrlListHint')}
          </p>
        </div>
      )}

      {/* Max Joins */}
      <Input
        label={isByUrl ? t('maxJoins') + ' (limit)' : t('maxJoins')}
        type="number"
        min={1}
        max={10}
        value={config.max_joins}
        onChange={(e) => updateConfig({ max_joins: parseInt(e.target.value) || 3 })}
      />
    </div>
  );
};

// ============================================================================
// LIKE PAGES PANEL
// ============================================================================

const LikePagesPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const config = node.data.like_pages_config || node.data.config || {
    page_urls: [],
    from_suggestions: false,
  };

  const updateConfig = (updates: any) => {
    onChange({
      ...node.data,
      like_pages_config: { ...config, ...updates },
    });
  };

  // Parse page_urls from textarea (one URL per line)
  const pageUrlsText = (config.page_urls || []).join('\n');
  const [pageUrlsRawText, setPageUrlsRawText] = React.useState(pageUrlsText);

  // Sync local text when switching to a different node
  React.useEffect(() => {
    setPageUrlsRawText((config.page_urls || []).join('\n'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id]);

  const handlePageUrlsChange = (text: string) => {
    const urls = text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
    updateConfig({ page_urls: urls });
  };
  const { t } = useTranslation('workflow');

  return (
    <div className="space-y-3">
      <SectionTitle>{t('likePages')}</SectionTitle>

      <div className="space-y-2">
        <label className="text-sm font-semibold">{t('pageUrls')}</label>
        <textarea
          value={pageUrlsRawText}
          onChange={(e) => setPageUrlsRawText(e.target.value)}
          onBlur={(e) => handlePageUrlsChange(e.target.value)}
          className="w-full p-2 border rounded font-mono text-sm"
          rows={5}
          placeholder="https://facebook.com/page1\nhttps://facebook.com/page2\n..."
        />
        <p className="text-xs text-gray-500">
          {t('pageUrlsHint')}
        </p>
      </div>

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={config.from_suggestions}
          onChange={(e) => updateConfig({ from_suggestions: e.target.checked })}
        />
        <span className="text-sm">{t('fromSuggestions')}</span>
      </label>
    </div>
  );
};

// ============================================================================
// LIKE POSTS PANEL
// ============================================================================

const LikePostsPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.like_posts_config || node.data.config || {};

  const config: LikePostsConfig = {
    urls: rawConfig.urls || [],
    dispatch_mode: (rawConfig.dispatch_mode || 'specific') as 'specific' | 'managed' | 'group_posts' | 'filter_posts' | 'from_top_posts',
    seeding_contents_manager_node_id: rawConfig.seeding_contents_manager_node_id,
    find_group_posts_node_id: rawConfig.find_group_posts_node_id,
    filter_post_node_id: rawConfig.filter_post_node_id,
    find_top_posts_node_id: rawConfig.find_top_posts_node_id,
  };

  const updateConfig = (updates: Partial<LikePostsConfig>) => {
    onChange({
      ...node.data,
      like_posts_config: { ...config, ...updates },
    });
  };

  const isManaged = config.dispatch_mode === 'managed';
  const isGroupPosts = config.dispatch_mode === 'group_posts';
  const isFilterPosts = config.dispatch_mode === 'filter_posts';
  const isFromTopPosts = config.dispatch_mode === 'from_top_posts';
  const urlsText = (config.urls || []).join('\n');

  const handleUrlsChange = (text: string) => {
    const urls = text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
    updateConfig({ urls });
  };
  const { t } = useTranslation('workflow');

  return (
    <div className="space-y-3">
      <SectionTitle>{t('likePosts')}</SectionTitle>

      {/* Dispatch Mode */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">{t('postUrlSource')}</label>
        <div className="flex gap-3 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`dispatch-mode-${node.id}`}
              value="specific"
              checked={!isManaged && !isGroupPosts && !isFilterPosts && !isFromTopPosts}
              onChange={() => updateConfig({ dispatch_mode: 'specific', seeding_contents_manager_node_id: undefined, find_group_posts_node_id: undefined, filter_post_node_id: undefined, find_top_posts_node_id: undefined })}
            />
            <span className="text-sm">{t('specificUrl')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`dispatch-mode-${node.id}`}
              value="managed"
              checked={isManaged}
              onChange={() => updateConfig({ dispatch_mode: 'managed', urls: [], find_group_posts_node_id: undefined, filter_post_node_id: undefined, find_top_posts_node_id: undefined })}
            />
            <span className="text-sm">{t('managedMode')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`dispatch-mode-${node.id}`}
              value="group_posts"
              checked={isGroupPosts}
              onChange={() => updateConfig({ dispatch_mode: 'group_posts', urls: [], seeding_contents_manager_node_id: undefined, filter_post_node_id: undefined, find_top_posts_node_id: undefined })}
            />
            <span className="text-sm">{t('groupPostsMode')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`dispatch-mode-${node.id}`}
              value="filter_posts"
              checked={isFilterPosts}
              onChange={() => updateConfig({ dispatch_mode: 'filter_posts', urls: [], seeding_contents_manager_node_id: undefined, find_group_posts_node_id: undefined, find_top_posts_node_id: undefined })}
            />
            <span className="text-sm">{t('filterPostsMode')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`dispatch-mode-${node.id}`}
              value="from_top_posts"
              checked={isFromTopPosts}
              onChange={() => updateConfig({ dispatch_mode: 'from_top_posts', urls: [], seeding_contents_manager_node_id: undefined, find_group_posts_node_id: undefined, filter_post_node_id: undefined })}
            />
            <span className="text-sm">{t('fromTopPostsMode')}</span>
          </label>
        </div>
        {isManaged && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
            {t('managedModeHint')}
          </div>
        )}
        {isGroupPosts && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
            {t('groupPostsModeHint')}
          </div>
        )}
        {isFilterPosts && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
            {t('filterPostsModeHint')}
          </div>
        )}
        {isFromTopPosts && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
            {t('fromTopPostsModeHint')}
          </div>
        )}
      </div>

      {/* SCM Selector (managed mode) */}
      {isManaged && (
        <SeedingContentsManagerSelector
          value={config.seeding_contents_manager_node_id}
          onChange={(id) => updateConfig({ seeding_contents_manager_node_id: id })}
        />
      )}

      {/* Find Group Posts Selector (group_posts mode) */}
      {isGroupPosts && (
        <FindGroupPostsSelector
          value={config.find_group_posts_node_id}
          onChange={(id) => updateConfig({ find_group_posts_node_id: id })}
        />
      )}

      {/* Filter Post Selector (filter_posts mode) */}
      {isFilterPosts && (
        <FilterPostSelector
          value={config.filter_post_node_id}
          onChange={(id) => updateConfig({ filter_post_node_id: id })}
        />
      )}

      {/* Find Top Posts Selector (from_top_posts mode) */}
      {isFromTopPosts && (
        <FindTopPostsSelector
          value={config.find_top_posts_node_id}
          onChange={(id) => updateConfig({ find_top_posts_node_id: id })}
        />
      )}

      {/* Specific: URL list */}
      {!isManaged && !isGroupPosts && !isFilterPosts && !isFromTopPosts && (
        <div className="space-y-2">
          <label className="text-sm font-semibold">{t('postUrlsOnePerLine')}</label>
          <textarea
            value={urlsText}
            onChange={(e) => handleUrlsChange(e.target.value)}
            className="w-full p-2 border rounded font-mono text-sm"
            rows={5}
            placeholder="https://facebook.com/post1&#10;https://facebook.com/post2&#10;..."
          />
          <p className="text-xs text-gray-500">
            {t('postUrlsHint')}
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// LIKE POST TIKTOK PANEL
// ============================================================================

// ============================================================================
// CREATE POST INSTAGRAM PANEL
// ============================================================================

function CreatePostInstagramPanel({ node, onChange }: { node: any; onChange: (n: any) => void }) {
  const { t } = useTranslation('workflow');
  const rawConfig = node.data.create_post_instagram_config || node.data.config || {};

  const config: CreatePostInstagramConfig = {
    content_type: rawConfig.content_type || 'both',
    content_hints: rawConfig.content_hints || '',
    image_source_type: rawConfig.image_source_type || 'none',
    image_asset_id: rawConfig.image_asset_id || '',
    brand_info_node_id: rawConfig.brand_info_node_id,
  };

  const updateConfig = (updates: Partial<CreatePostInstagramConfig>) => {
    onChange({
      ...node.data,
      create_post_instagram_config: { ...config, ...updates },
    });
  };

  return (
    <div className="space-y-4">
      <SectionTitle>Create Post on Instagram</SectionTitle>

      <BrandInfoSelector
        value={config.brand_info_node_id}
        onChange={(brandInfoNodeId) => updateConfig({ brand_info_node_id: brandInfoNodeId })}
      />

      <div>
        <label className="block text-sm mb-1">{t('contentType')}</label>
        <select
          className="w-full px-2 py-1 border rounded"
          value={config.content_type}
          onChange={(e) => updateConfig({ content_type: e.target.value as any })}
        >
          <option value="both">{t('textAndImage', 'Text and Image')}</option>
          <option value="image">{t('imageOnly', 'Image Only')}</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1.5">{t('imageAttachment')}</label>
        <select
          className="w-full px-2 py-1 border rounded text-sm"
          value={config.image_source_type}
          onChange={(e) =>
            updateConfig({
              image_source_type: e.target.value as any,
              image_asset_id: e.target.value !== 'vault_asset' ? '' : config.image_asset_id,
            })
          }
        >
          <option value="none">{t('noImage')}</option>
          <option value="context">{t('fromPreviousNode')}</option>
          <option value="character_context">{t('characterContext', 'Character Context (Image2Image)')}</option>
          <option value="vault_asset">{t('pickFromVault')}</option>
        </select>

        {config.image_source_type === 'context' && (
          <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
            {t('contextImageHint')}
          </div>
        )}

        {config.image_source_type === 'vault_asset' && (
          <div className="mt-2">
            <div className="text-xs text-gray-600 mb-1">
              {t('selectImageFromVault')}
            </div>
            <VaultAssetPicker
              mediaType="image"
              selectedId={config.image_asset_id || undefined}
              onSelect={(id) => updateConfig({ image_asset_id: id })}
            />
            {config.image_asset_id && (
              <p className="text-xs text-gray-500 mt-1 truncate">
                {t('selectedImage', { id: config.image_asset_id })}
              </p>
            )}
          </div>
        )}
      </div>

      {config.content_type !== 'image' && (
        <div>
          <label className="block text-sm mb-1">{t('contentHints')}</label>
          <textarea
            className="w-full px-2 py-1 border rounded text-sm"
            rows={3}
            placeholder={t('contentHintsPlaceholder')}
            value={config.content_hints}
            onChange={(e) => updateConfig({ content_hints: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}

// ============================================================================
// LIKE POST INSTAGRAM PANEL
// ============================================================================

const LikePostInstagramPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const { t } = useTranslation('workflow');
  const rawConfig = node.data.like_post_instagram_config || node.data.config || {};

  const config: LikePostInstagramConfig = {
    urls: rawConfig.urls || [],
  };

  const updateConfig = (updates: Partial<LikePostInstagramConfig>) => {
    onChange({
      ...node.data,
      like_post_instagram_config: { ...config, ...updates },
    });
  };

  const urlsText = (config.urls || []).join('\n');

  return (
    <div className="space-y-4">
      <SectionTitle>Like Post on Instagram</SectionTitle>
      <div className="space-y-2">
        <label className="block text-sm font-semibold">Instagram Post URLs</label>
        <textarea
          key={`ig-like-urls-${node.id}`}
          defaultValue={urlsText}
          onBlur={(e) => {
            const urls = e.target.value
              .split('\n')
              .map((l) => l.trim())
              .filter((l) => l.length > 0);
            updateConfig({ urls });
          }}
          className="w-full p-2 border rounded font-mono text-sm"
          rows={5}
          placeholder="https://www.instagram.com/p/..."
        />
        <p className="text-xs text-gray-500">Each URL on a new line.</p>
      </div>
    </div>
  );
};

// ============================================================================
// REPOST POST INSTAGRAM PANEL
// ============================================================================

const RepostPostInstagramPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const { t } = useTranslation('workflow');
  const rawConfig = node.data.repost_post_instagram_config || node.data.config || {};

  const config: RepostPostInstagramConfig = {
    urls: rawConfig.urls || [],
  };

  const updateConfig = (updates: Partial<RepostPostInstagramConfig>) => {
    onChange({
      ...node.data,
      repost_post_instagram_config: { ...config, ...updates },
    });
  };

  const urlsText = (config.urls || []).join('\n');

  return (
    <div className="space-y-4">
      <SectionTitle>Repost on Instagram</SectionTitle>
      <div className="space-y-2">
        <label className="block text-sm font-semibold">Instagram Post URLs</label>
        <textarea
          key={`ig-repost-urls-${node.id}`}
          defaultValue={urlsText}
          onBlur={(e) => {
            const urls = e.target.value
              .split('\n')
              .map((l) => l.trim())
              .filter((l) => l.length > 0);
            updateConfig({ urls });
          }}
          className="w-full p-2 border rounded font-mono text-sm"
          rows={5}
          placeholder="https://www.instagram.com/p/..."
        />
        <p className="text-xs text-gray-500">Each URL on a new line.</p>
      </div>
    </div>
  );
};

// ============================================================================
// FOLLOW INSTAGRAM PANEL
// ============================================================================

const FollowInstagramPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const { t } = useTranslation('workflow');
  const rawConfig = node.data.follow_instagram_config || node.data.config || {};

  const config: FollowInstagramConfig = {
    urls: rawConfig.urls || [],
  };

  const updateConfig = (updates: Partial<FollowInstagramConfig>) => {
    onChange({
      ...node.data,
      follow_instagram_config: { ...config, ...updates },
    });
  };

  const urlsText = (config.urls || []).join('\n');

  return (
    <div className="space-y-4">
      <SectionTitle>Follow on Instagram</SectionTitle>
      <div className="space-y-2">
        <label className="block text-sm font-semibold">Instagram Profile URLs</label>
        <textarea
          key={`ig-follow-urls-${node.id}`}
          defaultValue={urlsText}
          onBlur={(e) => {
            const urls = e.target.value
              .split('\n')
              .map((l) => l.trim())
              .filter((l) => l.length > 0);
            updateConfig({ urls });
          }}
          className="w-full p-2 border rounded font-mono text-sm"
          rows={5}
          placeholder="https://www.instagram.com/epoint2016/"
        />
        <p className="text-xs text-gray-500">Each URL on a new line.</p>
      </div>
    </div>
  );
};

// ============================================================================
// REPLY COMMENT INSTAGRAM PANEL
// ============================================================================

const ReplyCommentInstagramPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const { t } = useTranslation('workflow');
  const rawConfig = node.data.reply_comment_instagram_config || node.data.config || {};

  const config: ReplyCommentInstagramConfig = {
    urls: rawConfig.urls || [],
    keywords: rawConfig.keywords || [],
    reply_template: rawConfig.reply_template || '',
    max_comments: rawConfig.max_comments !== undefined ? rawConfig.max_comments : 100,
  };

  const updateConfig = (updates: Partial<ReplyCommentInstagramConfig>) => {
    onChange({
      ...node.data,
      reply_comment_instagram_config: { ...config, ...updates },
    });
  };

  const urlsText = (config.urls || []).join('\n');
  const keywordsText = (config.keywords || []).join('\n');

  return (
    <div className="space-y-4">
      <SectionTitle>Reply Comment on Instagram</SectionTitle>
      
      <div className="space-y-2">
        <label className="block text-sm font-semibold">Instagram Post URLs</label>
        <textarea
          key={`ig-reply-urls-${node.id}`}
          defaultValue={urlsText}
          onBlur={(e) => {
            const urls = e.target.value
              .split('\n')
              .map((l) => l.trim())
              .filter((l) => l.length > 0);
            updateConfig({ urls });
          }}
          className="w-full p-2 border rounded font-mono text-sm"
          rows={4}
          placeholder="https://www.instagram.com/p/DZVDslsEUvY/"
        />
        <p className="text-xs text-gray-500">Each URL on a new line.</p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold">Keywords to Match</label>
        <textarea
          key={`ig-reply-keywords-${node.id}`}
          defaultValue={keywordsText}
          onBlur={(e) => {
            const keywords = e.target.value
              .split('\n')
              .map((l) => l.trim())
              .filter((l) => l.length > 0);
            updateConfig({ keywords });
          }}
          className="w-full p-2 border rounded font-mono text-sm"
          rows={3}
          placeholder="Inbox&#10;Giá&#10;Tư vấn"
        />
        <p className="text-xs text-gray-500">Each keyword on a new line (case-insensitive match).</p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold">Reply Template (Optional)</label>
        <textarea
          key={`ig-reply-template-${node.id}`}
          value={config.reply_template}
          onChange={(e) => updateConfig({ reply_template: e.target.value })}
          className="w-full p-2 border rounded text-sm"
          rows={3}
          placeholder="Cảm ơn bạn, check inbox nhé! (If left blank, AI will generate reply)"
        />
        <p className="text-xs text-gray-500">Use {`{username}`} to mention the commenter.</p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold">Max Comments to Scan</label>
        <input
          type="number"
          min={1}
          max={300}
          value={config.max_comments}
          onChange={(e) => {
            const val = parseInt(e.target.value, 10);
            updateConfig({ max_comments: isNaN(val) ? 100 : val });
          }}
          className="w-full p-2 border rounded text-sm"
        />
        <p className="text-xs text-gray-500">Scanning from top to bottom. Default: 100, Max: 300.</p>
      </div>
    </div>
  );
};

// ============================================================================
// COMMENT POST INSTAGRAM PANEL
// ============================================================================

const CommentPostInstagramPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const { t } = useTranslation('workflow');
  const rawConfig = node.data.comment_post_instagram_config || node.data.config || {};

  const config: CommentPostInstagramConfig = {
    urls: rawConfig.urls || [],
    generation_method: rawConfig.generation_method || 'ai',
    fixed_comments: rawConfig.fixed_comments || [],
    content_hints: rawConfig.content_hints || '',
    comment_mode: rawConfig.comment_mode || 'discussion',
    seeding_mode: rawConfig.seeding_mode || '',
  };

  const updateConfig = (updates: Partial<CommentPostInstagramConfig>) => {
    onChange({
      ...node.data,
      comment_post_instagram_config: { ...config, ...updates },
    });
  };

  const urlsText = (config.urls || []).join('\n');
  const fixedCommentsText = (config.fixed_comments || []).join('\n');

  return (
    <div className="space-y-4">
      <SectionTitle>Comment Post on Instagram</SectionTitle>
      
      <div className="space-y-2">
        <label className="block text-sm font-semibold">Instagram Post URLs</label>
        <textarea
          key={`ig-comment-urls-${node.id}`}
          defaultValue={urlsText}
          onBlur={(e) => {
            const urls = e.target.value
              .split('\n')
              .map((l) => l.trim())
              .filter((l) => l.length > 0);
            updateConfig({ urls });
          }}
          className="w-full p-2 border rounded font-mono text-sm"
          rows={5}
          placeholder="https://www.instagram.com/p/..."
        />
        <p className="text-xs text-gray-500">Each URL on a new line.</p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium">{t('generationMethod', 'Nguồn tạo Comment')}</label>
        <select
          value={config.generation_method}
          onChange={(e) => updateConfig({ generation_method: e.target.value as any })}
          className="w-full p-2 border rounded text-sm bg-white"
        >
          <option value="ai">{t('methodAI', 'Dùng AI tạo nội dung')}</option>
          <option value="fixed">{t('methodFixed', 'Nhập danh sách mẫu (Random)')}</option>
        </select>
      </div>

      {config.generation_method === 'fixed' ? (
        <div className="space-y-2 pl-4 border-l-2 border-primary">
          <label className="block text-sm font-semibold">{t('fixedComments', 'Danh sách mẫu bình luận')}</label>
          <textarea
            key={`ig-fixed-comments-${node.id}`}
            defaultValue={fixedCommentsText}
            onBlur={(e) => {
              const fixed_comments = e.target.value
                .split('\n')
                .map((l) => l.trim())
                .filter((l) => l.length > 0);
              updateConfig({ fixed_comments });
            }}
            className="w-full p-2 border rounded text-sm"
            rows={5}
            placeholder={t('fixedCommentsPlaceholder', 'Mỗi bình luận một dòng...\nTuyệt vời quá!\nSản phẩm này giá sao shop?')}
          />
        </div>
      ) : (
        <div className="space-y-4 pl-4 border-l-2 border-primary">
          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('contentHints', 'Content Hints (Prompt for AI)')}</label>
            <textarea
              value={config.content_hints}
              onChange={(e) => updateConfig({ content_hints: e.target.value })}
              className="w-full p-2 border rounded text-sm"
              rows={3}
              placeholder={t('contentHintsPlaceholder', 'E.g. compliment the photo, ask about the product...')}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium">{t('commentMode', 'Comment Mode')}</label>
            <select
              value={config.comment_mode}
              onChange={(e) => updateConfig({ comment_mode: e.target.value as any })}
              className="w-full p-2 border rounded text-sm bg-white"
            >
              <option value="discussion">{t('modeDiscussion', 'Natural Discussion (Bàn luận tự nhiên)')}</option>
              <option value="seeding">{t('modeSeeding', 'Seeding / Sales (Seeding bán hàng)')}</option>
            </select>
          </div>

          {config.comment_mode === 'seeding' && (
            <div className="space-y-2 pl-4 border-l-2 border-secondary">
              <label className="block text-sm font-medium">{t('seedingMode', 'Seeding Intent')}</label>
              <select
                value={config.seeding_mode || ''}
                onChange={(e) => updateConfig({ seeding_mode: e.target.value })}
                className="w-full p-2 border rounded text-sm bg-white"
              >
                <option value="">-- Random --</option>
                <option value="ask_info">{t('intentAskInfo', 'Hỏi thêm thông tin/Giá/Địa chỉ (Ask info)')}</option>
                <option value="interest">{t('intentInterest', 'Thể hiện sự quan tâm/Khen ngợi (Interest)')}</option>
                <option value="inbox_cta">{t('intentInboxCta', 'Kêu gọi check inbox (Check inbox CTA)')}</option>
                <option value="social_proof">{t('intentSocialProof', 'Tạo hiệu ứng chim mồi (Social proof)')}</option>
                <option value="close">{t('intentClose', 'Chốt đơn (Close sale)')}</option>
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const LikePostTiktokPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const { t } = useTranslation('workflow');
  const rawConfig = node.data.like_post_tiktok_config || node.data.config || {};

  const config: LikePostTiktokConfig = {
    dispatch_mode: rawConfig.dispatch_mode || 'urls',
    urls: rawConfig.urls || [],
    tiktok_ids: rawConfig.tiktok_ids || [],
    count: rawConfig.count ?? 10,
    order: rawConfig.order || 'newest',
    keyword: rawConfig.keyword || '',
  };

  const updateConfig = (updates: Partial<LikePostTiktokConfig>) => {
    onChange({
      ...node.data,
      like_post_tiktok_config: { ...config, ...updates },
    });
  };

  const isUrls = config.dispatch_mode === 'urls';
  const isSeeding = config.dispatch_mode === 'seeding_for_account';
  const isKeyword = config.dispatch_mode === 'keyword_search';

  const urlsText = (config.urls || []).join('\n');
  const tiktokIdsText = (config.tiktok_ids || []).join('\n');

  return (
    <div className="space-y-4">
      <SectionTitle>{t('likePostTiktok')}</SectionTitle>

      {/* Dispatch Mode */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">{t('tiktokDispatchMode')}</label>
        <div className="flex flex-col gap-2">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-dispatch-${node.id}`}
              value="urls"
              checked={isUrls}
              onChange={() => updateConfig({ dispatch_mode: 'urls' })}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium">{t('tiktokModeUrls')}</span>
              <span className="text-gray-500 ml-1">— {t('tiktokModeUrlsDesc')}</span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-dispatch-${node.id}`}
              value="seeding_for_account"
              checked={isSeeding}
              onChange={() => updateConfig({ dispatch_mode: 'seeding_for_account', order: 'newest' })}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium">{t('tiktokModeSeeding')}</span>
              <span className="text-gray-500 ml-1">— {t('tiktokModeSeedingDesc')}</span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-dispatch-${node.id}`}
              value="keyword_search"
              checked={isKeyword}
              onChange={() => updateConfig({ dispatch_mode: 'keyword_search', order: 'top' })}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium">{t('tiktokModeKeyword')}</span>
              <span className="text-gray-500 ml-1">— {t('tiktokModeKeywordDesc')}</span>
            </span>
          </label>
        </div>
      </div>

      {/* MODE: urls */}
      {isUrls && (
        <div className="space-y-2">
          <label className="block text-sm font-semibold">{t('tiktokVideoUrls')}</label>
          <textarea
            key={`tiktok-urls-${node.id}`}
            defaultValue={urlsText}
            onBlur={(e) => {
              const urls = e.target.value
                .split('\n')
                .map((l) => l.trim())
                .filter((l) => l.length > 0);
              updateConfig({ urls });
            }}
            className="w-full p-2 border rounded font-mono text-sm"
            rows={5}
            placeholder={t('tiktokVideoUrlsPlaceholder')}
          />
          <p className="text-xs text-gray-500">{t('tiktokVideoUrlsHint')}</p>
        </div>
      )}

      {/* MODE: seeding_for_account */}
      {isSeeding && (
        <div className="space-y-3">
          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokSeedingIds')}</label>
            <textarea
              key={`tiktok-ids-${node.id}`}
              defaultValue={tiktokIdsText}
              onBlur={(e) => {
                const tiktok_ids = e.target.value
                  .split('\n')
                  .map((l) => l.trim())
                  .filter((l) => l.length > 0);
                updateConfig({ tiktok_ids });
              }}
              className="w-full p-2 border rounded font-mono text-sm"
              rows={4}
              placeholder={t('tiktokSeedingIdsPlaceholder')}
            />
            <p className="text-xs text-gray-500">{t('tiktokSeedingIdsHint')}</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokLikeCount')}</label>
            <input
              type="number"
              min={1}
              max={500}
              value={config.count ?? 10}
              onChange={(e) => updateConfig({ count: Math.max(1, parseInt(e.target.value) || 10) })}
              className="w-full px-3 py-2 border rounded"
            />
            <p className="text-xs text-gray-500">{t('tiktokLikeCountSeedingHint')}</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokOrder')}</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`tiktok-order-${node.id}`}
                  value="newest"
                  checked={(config.order || 'newest') === 'newest'}
                  onChange={() => updateConfig({ order: 'newest' })}
                />
                <span className="text-sm">{t('tiktokOrderNewest')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`tiktok-order-${node.id}`}
                  value="random"
                  checked={config.order === 'random'}
                  onChange={() => updateConfig({ order: 'random' })}
                />
                <span className="text-sm">{t('tiktokOrderRandom')}</span>
              </label>
            </div>
            <p className="text-xs text-gray-500">
              {config.order === 'random' ? t('tiktokOrderRandomHint') : t('tiktokOrderNewestHint')}
            </p>
          </div>
        </div>
      )}

      {/* MODE: keyword_search */}
      {isKeyword && (
        <div className="space-y-3">
          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokKeyword')}</label>
            <input
              type="text"
              value={config.keyword || ''}
              onChange={(e) => updateConfig({ keyword: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              placeholder={t('tiktokKeywordPlaceholder')}
            />
            <p className="text-xs text-gray-500">{t('tiktokKeywordHint')}</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokLikeCount')}</label>
            <input
              type="number"
              min={1}
              max={500}
              value={config.count ?? 10}
              onChange={(e) => updateConfig({ count: Math.max(1, parseInt(e.target.value) || 10) })}
              className="w-full px-3 py-2 border rounded"
            />
            <p className="text-xs text-gray-500">{t('tiktokLikeCountKeywordHint')}</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokKeywordOrder')}</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`tiktok-kw-order-${node.id}`}
                  value="top"
                  checked={(config.order || 'top') === 'top'}
                  onChange={() => updateConfig({ order: 'top' })}
                />
                <span className="text-sm">{t('tiktokOrderTop')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`tiktok-kw-order-${node.id}`}
                  value="random"
                  checked={config.order === 'random'}
                  onChange={() => updateConfig({ order: 'random' })}
                />
                <span className="text-sm">{t('tiktokOrderRandom')}</span>
              </label>
            </div>
            <p className="text-xs text-gray-500">
              {config.order === 'random' ? t('tiktokOrderRandomHint') : t('tiktokKeywordOrderTopHint')}
            </p>
          </div>
        </div>
      )}

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 space-y-1">
        <div className="font-semibold">ℹ️ {t('tiktokInfoTitle')}</div>
        <ul className="list-disc list-inside space-y-0.5">
          <li>{t('tiktokInfoNoReLike')}</li>
          <li>{t('tiktokInfoQuota')}</li>
          <li>{t('tiktokInfoMaxAvailable')}</li>
        </ul>
      </div>
    </div>
  );
};

const SharePostTiktokPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const { t } = useTranslation('workflow');
  const rawConfig = node.data.share_post_tiktok_config || node.data.config || {};

  const config: SharePostTiktokConfig = {
    dispatch_mode: rawConfig.dispatch_mode || 'urls',
    urls: rawConfig.urls || [],
    tiktok_ids: rawConfig.tiktok_ids || [],
    count: rawConfig.count ?? 10,
    order: rawConfig.order || 'newest',
    keyword: rawConfig.keyword || '',
  };

  const updateConfig = (updates: Partial<SharePostTiktokConfig>) => {
    onChange({
      ...node.data,
      share_post_tiktok_config: { ...config, ...updates },
    });
  };

  const isUrls = config.dispatch_mode === 'urls';
  const isSeeding = config.dispatch_mode === 'seeding_for_account';
  const isKeyword = config.dispatch_mode === 'keyword_search';

  const urlsText = (config.urls || []).join('\n');
  const tiktokIdsText = (config.tiktok_ids || []).join('\n');

  return (
    <div className="space-y-4">
      <SectionTitle>{t('sharePostTiktok')}</SectionTitle>

      {/* Dispatch Mode */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">{t('tiktokDispatchMode')}</label>
        <div className="flex flex-col gap-2">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-share-dispatch-${node.id}`}
              value="urls"
              checked={isUrls}
              onChange={() => updateConfig({ dispatch_mode: 'urls' })}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium">{t('tiktokModeUrls')}</span>
              <span className="text-gray-500 ml-1">— {t('tiktokShareModeUrlsDesc')}</span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-share-dispatch-${node.id}`}
              value="seeding_for_account"
              checked={isSeeding}
              onChange={() => updateConfig({ dispatch_mode: 'seeding_for_account', order: 'newest' })}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium">{t('tiktokModeSeeding')}</span>
              <span className="text-gray-500 ml-1">— {t('tiktokShareModeSeedingDesc')}</span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-share-dispatch-${node.id}`}
              value="keyword_search"
              checked={isKeyword}
              onChange={() => updateConfig({ dispatch_mode: 'keyword_search', order: 'top' })}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium">{t('tiktokModeKeyword')}</span>
              <span className="text-gray-500 ml-1">— {t('tiktokShareModeKeywordDesc')}</span>
            </span>
          </label>
        </div>
      </div>

      {/* MODE: urls */}
      {isUrls && (
        <div className="space-y-2">
          <label className="block text-sm font-semibold">{t('tiktokVideoUrls')}</label>
          <textarea
            key={`tiktok-urls-${node.id}`}
            defaultValue={urlsText}
            onBlur={(e) => {
              const urls = e.target.value
                .split('\n')
                .map((l) => l.trim())
                .filter((l) => l.length > 0);
              updateConfig({ urls });
            }}
            className="w-full p-2 border rounded font-mono text-sm"
            rows={5}
            placeholder={t('tiktokVideoUrlsPlaceholder')}
          />
          <p className="text-xs text-gray-500">{t('tiktokShareVideoUrlsHint')}</p>
        </div>
      )}

      {/* MODE: seeding_for_account */}
      {isSeeding && (
        <div className="space-y-3">
          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokSeedingIds')}</label>
            <textarea
              key={`tiktok-ids-${node.id}`}
              defaultValue={tiktokIdsText}
              onBlur={(e) => {
                const tiktok_ids = e.target.value
                  .split('\n')
                  .map((l) => l.trim())
                  .filter((l) => l.length > 0);
                updateConfig({ tiktok_ids });
              }}
              className="w-full p-2 border rounded font-mono text-sm"
              rows={4}
              placeholder={t('tiktokSeedingIdsPlaceholder')}
            />
            <p className="text-xs text-gray-500">{t('tiktokSeedingIdsHint')}</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokShareCount')}</label>
            <input
              type="number"
              min={1}
              max={500}
              value={config.count ?? 10}
              onChange={(e) => updateConfig({ count: Math.max(1, parseInt(e.target.value) || 10) })}
              className="w-full px-3 py-2 border rounded"
            />
            <p className="text-xs text-gray-500">{t('tiktokShareCountSeedingHint')}</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokOrder')}</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`tiktok-share-order-${node.id}`}
                  value="newest"
                  checked={(config.order || 'newest') === 'newest'}
                  onChange={() => updateConfig({ order: 'newest' })}
                />
                <span className="text-sm">{t('tiktokOrderNewest')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`tiktok-share-order-${node.id}`}
                  value="random"
                  checked={config.order === 'random'}
                  onChange={() => updateConfig({ order: 'random' })}
                />
                <span className="text-sm">{t('tiktokOrderRandom')}</span>
              </label>
            </div>
            <p className="text-xs text-gray-500">
              {config.order === 'random' ? t('tiktokOrderRandomHint') : t('tiktokOrderNewestHint')}
            </p>
          </div>
        </div>
      )}

      {/* MODE: keyword_search */}
      {isKeyword && (
        <div className="space-y-3">
          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokKeyword')}</label>
            <input
              type="text"
              value={config.keyword || ''}
              onChange={(e) => updateConfig({ keyword: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              placeholder={t('tiktokKeywordPlaceholder')}
            />
            <p className="text-xs text-gray-500">{t('tiktokKeywordHint')}</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokShareCount')}</label>
            <input
              type="number"
              min={1}
              max={500}
              value={config.count ?? 10}
              onChange={(e) => updateConfig({ count: Math.max(1, parseInt(e.target.value) || 10) })}
              className="w-full px-3 py-2 border rounded"
            />
            <p className="text-xs text-gray-500">{t('tiktokShareCountKeywordHint')}</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokKeywordOrder')}</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`tiktok-share-kw-order-${node.id}`}
                  value="top"
                  checked={(config.order || 'top') === 'top'}
                  onChange={() => updateConfig({ order: 'top' })}
                />
                <span className="text-sm">{t('tiktokOrderTop')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`tiktok-share-kw-order-${node.id}`}
                  value="random"
                  checked={config.order === 'random'}
                  onChange={() => updateConfig({ order: 'random' })}
                />
                <span className="text-sm">{t('tiktokOrderRandom')}</span>
              </label>
            </div>
            <p className="text-xs text-gray-500">
              {config.order === 'random' ? t('tiktokOrderRandomHint') : t('tiktokKeywordOrderTopHint')}
            </p>
          </div>
        </div>
      )}

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 space-y-1">
        <div className="font-semibold">ℹ️ {t('tiktokInfoTitle')}</div>
        <ul className="list-disc list-inside space-y-0.5">
          <li>{t('tiktokShareInfoNoReShare')}</li>
          <li>{t('tiktokShareInfoQuota')}</li>
          <li>{t('tiktokInfoMaxAvailable')}</li>
        </ul>
      </div>
    </div>
  );
};

// ============================================================================
// COMMENT POST TIKTOK PANEL
// ============================================================================

const CommentPostTiktokPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const { t } = useTranslation('workflow');
  const rawConfig = node.data.comment_post_tiktok_config || node.data.config || {};

  const config: CommentPostTiktokConfig = {
    dispatch_mode: rawConfig.dispatch_mode || 'urls',
    comment_mode: rawConfig.comment_mode || 'ai',
    prompt: rawConfig.prompt || '',
    fixed_comments: rawConfig.fixed_comments || [],
    urls: rawConfig.urls || [],
    tiktok_ids: rawConfig.tiktok_ids || [],
    count: rawConfig.count ?? 10,
    order: rawConfig.order || 'newest',
    keyword: rawConfig.keyword || '',
  };

  const updateConfig = (updates: Partial<CommentPostTiktokConfig>) => {
    onChange({
      ...node.data,
      comment_post_tiktok_config: { ...config, ...updates },
    });
  };

  const isUrls = config.dispatch_mode === 'urls';
  const isSeeding = config.dispatch_mode === 'seeding_for_account';
  const isKeyword = config.dispatch_mode === 'keyword_search';
  const isAi = config.comment_mode === 'ai';

  const tiktokIdsText = (config.tiktok_ids || []).join('\n');
  const fixedCommentsText = (config.fixed_comments || []).join('\n');
  const urlsText = (config.urls || []).join('\n');

  return (
    <div className="space-y-4">
      <SectionTitle>{t('commentPostTiktok')}</SectionTitle>

      {/* Dispatch Mode */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">{t('tiktokDispatchMode')}</label>
        <div className="flex flex-col gap-2">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-comment-dispatch-${node.id}`}
              value="urls"
              checked={isUrls}
              onChange={() => updateConfig({ dispatch_mode: 'urls' })}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium">{t('tiktokModeUrls')}</span>
              <span className="text-gray-500 ml-1">— {t('tiktokCommentModeUrlsDesc')}</span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-comment-dispatch-${node.id}`}
              value="seeding_for_account"
              checked={isSeeding}
              onChange={() => updateConfig({ dispatch_mode: 'seeding_for_account', order: 'newest' })}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium">{t('tiktokModeSeeding')}</span>
              <span className="text-gray-500 ml-1">— {t('tiktokCommentModeSeedingDesc')}</span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-comment-dispatch-${node.id}`}
              value="keyword_search"
              checked={isKeyword}
              onChange={() => updateConfig({ dispatch_mode: 'keyword_search', order: 'top' })}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium">{t('tiktokModeKeyword')}</span>
              <span className="text-gray-500 ml-1">— {t('tiktokCommentModeKeywordDesc')}</span>
            </span>
          </label>
        </div>
      </div>

      {/* Comment Mode */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">{t('tiktokCommentMode')}</label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-comment-mode-${node.id}`}
              value="ai"
              checked={isAi}
              onChange={() => updateConfig({ comment_mode: 'ai' })}
            />
            <span className="text-sm font-medium">{t('tiktokCommentModeAi')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-comment-mode-${node.id}`}
              value="fixed"
              checked={!isAi}
              onChange={() => updateConfig({ comment_mode: 'fixed' })}
            />
            <span className="text-sm font-medium">{t('tiktokCommentModeFixed')}</span>
          </label>
        </div>
      </div>

      {/* AI Comment Prompt */}
      {isAi && (
        <div className="space-y-2">
          <label className="block text-sm font-semibold">{t('tiktokCommentPrompt')}</label>
          <textarea
            value={config.prompt || ''}
            onChange={(e) => updateConfig({ prompt: e.target.value })}
            className="w-full p-2 border rounded text-sm"
            rows={4}
            placeholder={t('tiktokCommentPromptPlaceholder')}
          />
          <p className="text-xs text-gray-500">{t('tiktokCommentPromptHint')}</p>
        </div>
      )}

      {/* Fixed Comments */}
      {!isAi && (
        <div className="space-y-2">
          <label className="block text-sm font-semibold">{t('tiktokFixedComments')}</label>
          <textarea
            key={`tiktok-fixed-comments-${node.id}`}
            defaultValue={fixedCommentsText}
            onBlur={(e) => {
              const fixed_comments = e.target.value
                .split('\n')
                .map((l) => l.trim())
                .filter((l) => l.length > 0);
              updateConfig({ fixed_comments });
            }}
            className="w-full p-2 border rounded text-sm"
            rows={5}
            placeholder={t('tiktokFixedCommentsPlaceholder')}
          />
          <p className="text-xs text-gray-500">{t('tiktokFixedCommentsHint')}</p>
        </div>
      )}

      {/* MODE: urls */}
      {isUrls && (
        <div className="space-y-2">
          <label className="block text-sm font-semibold">{t('tiktokVideoUrls')}</label>
          <textarea
            key={`tiktok-urls-${node.id}`}
            defaultValue={urlsText}
            onBlur={(e) => {
              const urls = e.target.value
                .split('\n')
                .map((l) => l.trim())
                .filter((l) => l.length > 0);
              updateConfig({ urls });
            }}
            className="w-full p-2 border rounded font-mono text-sm"
            rows={5}
            placeholder={t('tiktokVideoUrlsPlaceholder')}
          />
          <p className="text-xs text-gray-500">{t('tiktokCommentVideoUrlsHint')}</p>
        </div>
      )}

      {/* MODE: seeding_for_account */}
      {isSeeding && (
        <div className="space-y-3">
          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokSeedingIds')}</label>
            <textarea
              key={`tiktok-ids-${node.id}`}
              defaultValue={tiktokIdsText}
              onBlur={(e) => {
                const tiktok_ids = e.target.value
                  .split('\n')
                  .map((l) => l.trim())
                  .filter((l) => l.length > 0);
                updateConfig({ tiktok_ids });
              }}
              className="w-full p-2 border rounded font-mono text-sm"
              rows={4}
              placeholder={t('tiktokSeedingIdsPlaceholder')}
            />
            <p className="text-xs text-gray-500">{t('tiktokSeedingIdsHint')}</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokCommentCount')}</label>
            <input
              type="number"
              min={1}
              max={500}
              value={config.count ?? 10}
              onChange={(e) => updateConfig({ count: Math.max(1, parseInt(e.target.value) || 10) })}
              className="w-full px-3 py-2 border rounded"
            />
            <p className="text-xs text-gray-500">{t('tiktokCommentCountSeedingHint')}</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokOrder')}</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`tiktok-comment-order-${node.id}`}
                  value="newest"
                  checked={(config.order || 'newest') === 'newest'}
                  onChange={() => updateConfig({ order: 'newest' })}
                />
                <span className="text-sm">{t('tiktokOrderNewest')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`tiktok-comment-order-${node.id}`}
                  value="random"
                  checked={config.order === 'random'}
                  onChange={() => updateConfig({ order: 'random' })}
                />
                <span className="text-sm">{t('tiktokOrderRandom')}</span>
              </label>
            </div>
            <p className="text-xs text-gray-500">
              {config.order === 'random' ? t('tiktokOrderRandomHint') : t('tiktokOrderNewestHint')}
            </p>
          </div>
        </div>
      )}

      {/* MODE: keyword_search */}
      {isKeyword && (
        <div className="space-y-3">
          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokKeyword')}</label>
            <input
              type="text"
              value={config.keyword || ''}
              onChange={(e) => updateConfig({ keyword: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              placeholder={t('tiktokKeywordPlaceholder')}
            />
            <p className="text-xs text-gray-500">{t('tiktokKeywordHint')}</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokCommentCount')}</label>
            <input
              type="number"
              min={1}
              max={500}
              value={config.count ?? 10}
              onChange={(e) => updateConfig({ count: Math.max(1, parseInt(e.target.value) || 10) })}
              className="w-full px-3 py-2 border rounded"
            />
            <p className="text-xs text-gray-500">{t('tiktokCommentCountKeywordHint')}</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokKeywordOrder')}</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`tiktok-comment-kw-order-${node.id}`}
                  value="top"
                  checked={(config.order || 'top') === 'top'}
                  onChange={() => updateConfig({ order: 'top' })}
                />
                <span className="text-sm">{t('tiktokOrderTop')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`tiktok-comment-kw-order-${node.id}`}
                  value="random"
                  checked={config.order === 'random'}
                  onChange={() => updateConfig({ order: 'random' })}
                />
                <span className="text-sm">{t('tiktokOrderRandom')}</span>
              </label>
            </div>
            <p className="text-xs text-gray-500">
              {config.order === 'random' ? t('tiktokOrderRandomHint') : t('tiktokKeywordOrderTopHint')}
            </p>
          </div>
        </div>
      )}

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 space-y-1">
        <div className="font-semibold">ℹ️ {t('tiktokInfoTitle')}</div>
        <ul className="list-disc list-inside space-y-0.5">
          <li>{t('tiktokCommentInfoUnique')}</li>
          <li>{t('tiktokCommentInfoCaptcha')}</li>
          <li>{t('tiktokInfoMaxAvailable')}</li>
        </ul>
      </div>
    </div>
  );
};

// ============================================================================
// REPLY COMMENT TIKTOK PANEL
// ============================================================================

const ReplyCommentTiktokPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const { t } = useTranslation('workflow');
  const rawConfig = node.data.reply_comment_tiktok_config || node.data.config || {};

  const config: ReplyCommentTiktokConfig = {
    dispatch_mode: rawConfig.dispatch_mode || 'urls',
    reply_mode: rawConfig.reply_mode || 'keyword',
    keywords: rawConfig.keywords || [],
    max_replies: rawConfig.max_replies ?? 5,
    comment_mode: rawConfig.comment_mode || 'ai',
    prompt: rawConfig.prompt || '',
    fixed_comments: rawConfig.fixed_comments || [],
    urls: rawConfig.urls || [],
    tiktok_ids: rawConfig.tiktok_ids || [],
    count: rawConfig.count ?? 10,
    order: rawConfig.order || 'newest',
    keyword: rawConfig.keyword || '',
  };

  const updateConfig = (updates: Partial<ReplyCommentTiktokConfig>) => {
    onChange({
      ...node.data,
      reply_comment_tiktok_config: { ...config, ...updates },
    });
  };

  const isUrls = config.dispatch_mode === 'urls';
  const isSeeding = config.dispatch_mode === 'seeding_for_account';
  const isKeywordDispatch = config.dispatch_mode === 'keyword_search';
  const isKeywordReply = config.reply_mode === 'keyword';
  const isAi = config.comment_mode === 'ai';

  const tiktokIdsText = (config.tiktok_ids || []).join('\n');
  const fixedCommentsText = (config.fixed_comments || []).join('\n');
  const urlsText = (config.urls || []).join('\n');
  const keywordsText = (config.keywords || []).join('\n');

  return (
    <div className="space-y-4">
      <SectionTitle>{t('replyCommentTiktok')}</SectionTitle>

      {/* Dispatch Mode */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">{t('tiktokDispatchMode')}</label>
        <div className="flex flex-col gap-2">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-reply-dispatch-${node.id}`}
              value="urls"
              checked={isUrls}
              onChange={() => updateConfig({ dispatch_mode: 'urls' })}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium">{t('tiktokModeUrls')}</span>
              <span className="text-gray-500 ml-1">— {t('tiktokCommentModeUrlsDesc')}</span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-reply-dispatch-${node.id}`}
              value="seeding_for_account"
              checked={isSeeding}
              onChange={() => updateConfig({ dispatch_mode: 'seeding_for_account', order: 'newest' })}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium">{t('tiktokModeSeeding')}</span>
              <span className="text-gray-500 ml-1">— {t('tiktokCommentModeSeedingDesc')}</span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-reply-dispatch-${node.id}`}
              value="keyword_search"
              checked={isKeywordDispatch}
              onChange={() => updateConfig({ dispatch_mode: 'keyword_search', order: 'top' })}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium">{t('tiktokModeKeyword')}</span>
              <span className="text-gray-500 ml-1">— {t('tiktokCommentModeKeywordDesc')}</span>
            </span>
          </label>
        </div>
      </div>

      {/* Reply Targeting Mode */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">{t('tiktokReplyMode')}</label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-reply-mode-${node.id}`}
              value="keyword"
              checked={isKeywordReply}
              onChange={() => updateConfig({ reply_mode: 'keyword' })}
            />
            <span className="text-sm font-medium">{t('tiktokReplyModeKeyword')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-reply-mode-${node.id}`}
              value="random"
              checked={!isKeywordReply}
              onChange={() => updateConfig({ reply_mode: 'random' })}
            />
            <span className="text-sm font-medium">{t('tiktokReplyModeRandom')}</span>
          </label>
        </div>
      </div>

      {/* Keyword list (only when reply_mode=keyword) */}
      {isKeywordReply && (
        <div className="space-y-2">
          <label className="block text-sm font-semibold">{t('tiktokReplyKeywords')}</label>
          <textarea
            key={`tiktok-keywords-${node.id}`}
            defaultValue={keywordsText}
            onBlur={(e) => {
              const keywords = e.target.value
                .split('\n')
                .map((l) => l.trim())
                .filter((l) => l.length > 0);
              updateConfig({ keywords });
            }}
            className="w-full p-2 border rounded text-sm"
            rows={3}
            placeholder={t('tiktokReplyKeywordsPlaceholder')}
          />
          <p className="text-xs text-gray-500">{t('tiktokReplyKeywordsHint')}</p>
        </div>
      )}

      {/* Max Replies */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">{t('tiktokMaxReplies')}</label>
        <input
          type="number"
          min={1}
          max={50}
          value={config.max_replies ?? 5}
          onChange={(e) => updateConfig({ max_replies: Math.max(1, parseInt(e.target.value) || 5) })}
          className="w-full px-3 py-2 border rounded"
        />
        <p className="text-xs text-gray-500">{t('tiktokMaxRepliesHint')}</p>
      </div>

      {/* Reply Content Mode */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">{t('tiktokCommentMode')}</label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-reply-content-${node.id}`}
              value="ai"
              checked={isAi}
              onChange={() => updateConfig({ comment_mode: 'ai' })}
            />
            <span className="text-sm font-medium">{t('tiktokCommentModeAi')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-reply-content-${node.id}`}
              value="fixed"
              checked={!isAi}
              onChange={() => updateConfig({ comment_mode: 'fixed' })}
            />
            <span className="text-sm font-medium">{t('tiktokCommentModeFixed')}</span>
          </label>
        </div>
      </div>

      {/* AI prompt */}
      {isAi && (
        <div className="space-y-2">
          <label className="block text-sm font-semibold">{t('tiktokCommentPrompt')}</label>
          <textarea
            value={config.prompt || ''}
            onChange={(e) => updateConfig({ prompt: e.target.value })}
            className="w-full p-2 border rounded text-sm"
            rows={4}
            placeholder={t('tiktokCommentPromptPlaceholder')}
          />
          <p className="text-xs text-gray-500">{t('tiktokCommentPromptHint')}</p>
        </div>
      )}

      {/* Fixed reply templates */}
      {!isAi && (
        <div className="space-y-2">
          <label className="block text-sm font-semibold">{t('tiktokFixedComments')}</label>
          <textarea
            key={`tiktok-fixed-comments-${node.id}`}
            defaultValue={fixedCommentsText}
            onBlur={(e) => {
              const fixed_comments = e.target.value
                .split('\n')
                .map((l) => l.trim())
                .filter((l) => l.length > 0);
              updateConfig({ fixed_comments });
            }}
            className="w-full p-2 border rounded text-sm"
            rows={5}
            placeholder={t('tiktokFixedCommentsPlaceholder')}
          />
          <p className="text-xs text-gray-500">{t('tiktokFixedCommentsHint')}</p>
        </div>
      )}

      {/* MODE: urls */}
      {isUrls && (
        <div className="space-y-2">
          <label className="block text-sm font-semibold">{t('tiktokVideoUrls')}</label>
          <textarea
            key={`tiktok-urls-${node.id}`}
            defaultValue={urlsText}
            onBlur={(e) => {
              const urls = e.target.value
                .split('\n')
                .map((l) => l.trim())
                .filter((l) => l.length > 0);
              updateConfig({ urls });
            }}
            className="w-full p-2 border rounded font-mono text-sm"
            rows={5}
            placeholder={t('tiktokVideoUrlsPlaceholder')}
          />
          <p className="text-xs text-gray-500">{t('tiktokCommentVideoUrlsHint')}</p>
        </div>
      )}

      {/* MODE: seeding_for_account */}
      {isSeeding && (
        <div className="space-y-3">
          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokSeedingIds')}</label>
            <textarea
              key={`tiktok-ids-${node.id}`}
              defaultValue={tiktokIdsText}
              onBlur={(e) => {
                const tiktok_ids = e.target.value
                  .split('\n')
                  .map((l) => l.trim())
                  .filter((l) => l.length > 0);
                updateConfig({ tiktok_ids });
              }}
              className="w-full p-2 border rounded font-mono text-sm"
              rows={4}
              placeholder={t('tiktokSeedingIdsPlaceholder')}
            />
            <p className="text-xs text-gray-500">{t('tiktokSeedingIdsHint')}</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokCommentCount')}</label>
            <input
              type="number"
              min={1}
              max={500}
              value={config.count ?? 10}
              onChange={(e) => updateConfig({ count: Math.max(1, parseInt(e.target.value) || 10) })}
              className="w-full px-3 py-2 border rounded"
            />
            <p className="text-xs text-gray-500">{t('tiktokCommentCountSeedingHint')}</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokOrder')}</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`tiktok-reply-order-${node.id}`}
                  value="newest"
                  checked={(config.order || 'newest') === 'newest'}
                  onChange={() => updateConfig({ order: 'newest' })}
                />
                <span className="text-sm">{t('tiktokOrderNewest')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`tiktok-reply-order-${node.id}`}
                  value="random"
                  checked={config.order === 'random'}
                  onChange={() => updateConfig({ order: 'random' })}
                />
                <span className="text-sm">{t('tiktokOrderRandom')}</span>
              </label>
            </div>
            <p className="text-xs text-gray-500">
              {config.order === 'random' ? t('tiktokOrderRandomHint') : t('tiktokOrderNewestHint')}
            </p>
          </div>
        </div>
      )}

      {/* MODE: keyword_search */}
      {isKeywordDispatch && (
        <div className="space-y-3">
          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokKeyword')}</label>
            <input
              type="text"
              value={config.keyword || ''}
              onChange={(e) => updateConfig({ keyword: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              placeholder={t('tiktokKeywordPlaceholder')}
            />
            <p className="text-xs text-gray-500">{t('tiktokKeywordHint')}</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokCommentCount')}</label>
            <input
              type="number"
              min={1}
              max={500}
              value={config.count ?? 10}
              onChange={(e) => updateConfig({ count: Math.max(1, parseInt(e.target.value) || 10) })}
              className="w-full px-3 py-2 border rounded"
            />
            <p className="text-xs text-gray-500">{t('tiktokCommentCountKeywordHint')}</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokKeywordOrder')}</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`tiktok-reply-kw-order-${node.id}`}
                  value="top"
                  checked={(config.order || 'top') === 'top'}
                  onChange={() => updateConfig({ order: 'top' })}
                />
                <span className="text-sm">{t('tiktokOrderTop')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`tiktok-reply-kw-order-${node.id}`}
                  value="random"
                  checked={config.order === 'random'}
                  onChange={() => updateConfig({ order: 'random' })}
                />
                <span className="text-sm">{t('tiktokOrderRandom')}</span>
              </label>
            </div>
            <p className="text-xs text-gray-500">
              {config.order === 'random' ? t('tiktokOrderRandomHint') : t('tiktokKeywordOrderTopHint')}
            </p>
          </div>
        </div>
      )}

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 space-y-1">
        <div className="font-semibold">ℹ️ {t('tiktokInfoTitle')}</div>
        <ul className="list-disc list-inside space-y-0.5">
          <li>{t('tiktokReplyInfoScan')}</li>
          <li>{t('tiktokCommentInfoCaptcha')}</li>
          <li>{t('tiktokInfoMaxAvailable')}</li>
        </ul>
      </div>
    </div>
  );
};

// ============================================================================
// LIKE COMMENT TIKTOK PANEL
// ============================================================================

const LikeCommentTiktokPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const { t } = useTranslation('workflow');
  const rawConfig = node.data.like_comment_tiktok_config || node.data.config || {};

  const config: LikeCommentTiktokConfig = {
    dispatch_mode: rawConfig.dispatch_mode || 'urls',
    target_mode: rawConfig.target_mode || 'keyword',
    keywords: rawConfig.keywords || [],
    max_likes: rawConfig.max_likes ?? 10,
    urls: rawConfig.urls || [],
    tiktok_ids: rawConfig.tiktok_ids || [],
    count: rawConfig.count ?? 10,
    order: rawConfig.order || 'newest',
    keyword: rawConfig.keyword || '',
  };

  const updateConfig = (updates: Partial<LikeCommentTiktokConfig>) => {
    onChange({
      ...node.data,
      like_comment_tiktok_config: { ...config, ...updates },
    });
  };

  const isUrls = config.dispatch_mode === 'urls';
  const isSeeding = config.dispatch_mode === 'seeding_for_account';
  const isKeywordDispatch = config.dispatch_mode === 'keyword_search';
  const isKeywordTarget = config.target_mode === 'keyword';

  const tiktokIdsText = (config.tiktok_ids || []).join('\n');
  const urlsText = (config.urls || []).join('\n');
  const keywordsText = (config.keywords || []).join('\n');

  return (
    <div className="space-y-4">
      <SectionTitle>{t('likeCommentTiktok')}</SectionTitle>

      {/* Dispatch Mode */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">{t('tiktokDispatchMode')}</label>
        <div className="flex flex-col gap-2">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-like-comment-dispatch-${node.id}`}
              value="urls"
              checked={isUrls}
              onChange={() => updateConfig({ dispatch_mode: 'urls' })}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium">{t('tiktokModeUrls')}</span>
              <span className="text-gray-500 ml-1">— {t('tiktokCommentModeUrlsDesc')}</span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-like-comment-dispatch-${node.id}`}
              value="seeding_for_account"
              checked={isSeeding}
              onChange={() => updateConfig({ dispatch_mode: 'seeding_for_account', order: 'newest' })}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium">{t('tiktokModeSeeding')}</span>
              <span className="text-gray-500 ml-1">— {t('tiktokCommentModeSeedingDesc')}</span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-like-comment-dispatch-${node.id}`}
              value="keyword_search"
              checked={isKeywordDispatch}
              onChange={() => updateConfig({ dispatch_mode: 'keyword_search', order: 'top' })}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium">{t('tiktokModeKeyword')}</span>
              <span className="text-gray-500 ml-1">— {t('tiktokCommentModeKeywordDesc')}</span>
            </span>
          </label>
        </div>
      </div>

      {/* Comment Targeting Mode */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">{t('tiktokTargetMode')}</label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-like-comment-target-${node.id}`}
              value="keyword"
              checked={isKeywordTarget}
              onChange={() => updateConfig({ target_mode: 'keyword' })}
            />
            <span className="text-sm font-medium">{t('tiktokReplyModeKeyword')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`tiktok-like-comment-target-${node.id}`}
              value="random"
              checked={!isKeywordTarget}
              onChange={() => updateConfig({ target_mode: 'random' })}
            />
            <span className="text-sm font-medium">{t('tiktokReplyModeRandom')}</span>
          </label>
        </div>
      </div>

      {/* Keyword list */}
      {isKeywordTarget && (
        <div className="space-y-2">
          <label className="block text-sm font-semibold">{t('tiktokReplyKeywords')}</label>
          <textarea
            key={`tiktok-keywords-${node.id}`}
            defaultValue={keywordsText}
            onBlur={(e) => {
              const keywords = e.target.value
                .split('\n')
                .map((l) => l.trim())
                .filter((l) => l.length > 0);
              updateConfig({ keywords });
            }}
            className="w-full p-2 border rounded text-sm"
            rows={3}
            placeholder={t('tiktokReplyKeywordsPlaceholder')}
          />
          <p className="text-xs text-gray-500">{t('tiktokReplyKeywordsHint')}</p>
        </div>
      )}

      {/* Max Likes */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">{t('tiktokMaxLikes')}</label>
        <input
          type="number"
          min={1}
          max={100}
          value={config.max_likes ?? 10}
          onChange={(e) => updateConfig({ max_likes: Math.max(1, parseInt(e.target.value) || 10) })}
          className="w-full px-3 py-2 border rounded"
        />
        <p className="text-xs text-gray-500">{t('tiktokMaxLikesHint')}</p>
      </div>

      {/* MODE: urls */}
      {isUrls && (
        <div className="space-y-2">
          <label className="block text-sm font-semibold">{t('tiktokVideoUrls')}</label>
          <textarea
            key={`tiktok-urls-${node.id}`}
            defaultValue={urlsText}
            onBlur={(e) => {
              const urls = e.target.value
                .split('\n')
                .map((l) => l.trim())
                .filter((l) => l.length > 0);
              updateConfig({ urls });
            }}
            className="w-full p-2 border rounded font-mono text-sm"
            rows={5}
            placeholder={t('tiktokVideoUrlsPlaceholder')}
          />
          <p className="text-xs text-gray-500">{t('tiktokCommentVideoUrlsHint')}</p>
        </div>
      )}

      {/* MODE: seeding_for_account */}
      {isSeeding && (
        <div className="space-y-3">
          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokSeedingIds')}</label>
            <textarea
              key={`tiktok-ids-${node.id}`}
              defaultValue={tiktokIdsText}
              onBlur={(e) => {
                const tiktok_ids = e.target.value
                  .split('\n')
                  .map((l) => l.trim())
                  .filter((l) => l.length > 0);
                updateConfig({ tiktok_ids });
              }}
              className="w-full p-2 border rounded font-mono text-sm"
              rows={4}
              placeholder={t('tiktokSeedingIdsPlaceholder')}
            />
            <p className="text-xs text-gray-500">{t('tiktokSeedingIdsHint')}</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokCommentCount')}</label>
            <input
              type="number"
              min={1}
              max={500}
              value={config.count ?? 10}
              onChange={(e) => updateConfig({ count: Math.max(1, parseInt(e.target.value) || 10) })}
              className="w-full px-3 py-2 border rounded"
            />
            <p className="text-xs text-gray-500">{t('tiktokCommentCountSeedingHint')}</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokOrder')}</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`tiktok-like-comment-order-${node.id}`}
                  value="newest"
                  checked={(config.order || 'newest') === 'newest'}
                  onChange={() => updateConfig({ order: 'newest' })}
                />
                <span className="text-sm">{t('tiktokOrderNewest')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`tiktok-like-comment-order-${node.id}`}
                  value="random"
                  checked={config.order === 'random'}
                  onChange={() => updateConfig({ order: 'random' })}
                />
                <span className="text-sm">{t('tiktokOrderRandom')}</span>
              </label>
            </div>
            <p className="text-xs text-gray-500">
              {config.order === 'random' ? t('tiktokOrderRandomHint') : t('tiktokOrderNewestHint')}
            </p>
          </div>
        </div>
      )}

      {/* MODE: keyword_search */}
      {isKeywordDispatch && (
        <div className="space-y-3">
          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokKeyword')}</label>
            <input
              type="text"
              value={config.keyword || ''}
              onChange={(e) => updateConfig({ keyword: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              placeholder={t('tiktokKeywordPlaceholder')}
            />
            <p className="text-xs text-gray-500">{t('tiktokKeywordHint')}</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokCommentCount')}</label>
            <input
              type="number"
              min={1}
              max={500}
              value={config.count ?? 10}
              onChange={(e) => updateConfig({ count: Math.max(1, parseInt(e.target.value) || 10) })}
              className="w-full px-3 py-2 border rounded"
            />
            <p className="text-xs text-gray-500">{t('tiktokCommentCountKeywordHint')}</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold">{t('tiktokKeywordOrder')}</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`tiktok-like-comment-kw-order-${node.id}`}
                  value="top"
                  checked={(config.order || 'top') === 'top'}
                  onChange={() => updateConfig({ order: 'top' })}
                />
                <span className="text-sm">{t('tiktokOrderTop')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`tiktok-like-comment-kw-order-${node.id}`}
                  value="random"
                  checked={config.order === 'random'}
                  onChange={() => updateConfig({ order: 'random' })}
                />
                <span className="text-sm">{t('tiktokOrderRandom')}</span>
              </label>
            </div>
            <p className="text-xs text-gray-500">
              {config.order === 'random' ? t('tiktokOrderRandomHint') : t('tiktokKeywordOrderTopHint')}
            </p>
          </div>
        </div>
      )}

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 space-y-1">
        <div className="font-semibold">ℹ️ {t('tiktokInfoTitle')}</div>
        <ul className="list-disc list-inside space-y-0.5">
          <li>{t('tiktokLikeCommentInfoScan')}</li>
          <li>{t('tiktokCommentInfoCaptcha')}</li>
          <li>{t('tiktokInfoMaxAvailable')}</li>
        </ul>
      </div>
    </div>
  );
};

// ============================================================================
// INTERACT PANEL
// ============================================================================

const InteractPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const config = node.data.interact_config || node.data.config || {
    type: 'all',
    duration_minutes: 10,
    max_likes: 5,
    max_shares: 2,
    max_comments: 3,
    keywords: [],
  };

  const updateConfig = (updates: any) => {
    onChange({
      ...node.data,
      interact_config: { ...config, ...updates },
    });
  };
  const { t } = useTranslation('workflow');

  const [keywordInput, setKeywordInput] = useState('');
  const addKeyword = () => {
    const kw = keywordInput.trim();
    if (kw && !(config.keywords || []).includes(kw)) {
      updateConfig({ keywords: [...(config.keywords || []), kw] });
    }
    setKeywordInput('');
  };
  const removeKeyword = (kw: string) =>
    updateConfig({ keywords: (config.keywords || []).filter((k: string) => k !== kw) });

  return (
    <div className="space-y-3">
      <SectionTitle>{t('interact')}</SectionTitle>

      <div className="space-y-2">
        <label className="text-sm font-semibold">{t('interactionType')}</label>
        <select
          value={config.type}
          onChange={(e) => updateConfig({ type: e.target.value })}
          className="w-full p-2 border rounded"
        >
          <option value="all">{t('allMode')}</option>
          <option value="newsfeed">{t('newsfeedOnly')}</option>
          <option value="reels">{t('reelsOnly')}</option>
          <option value="story">{t('storyOnly')}</option>
        </select>
      </div>

      <Input
        label={t('durationMinutes')}
        type="number"
        min={1}
        max={60}
        value={config.duration_minutes}
        onChange={(e) => updateConfig({ duration_minutes: parseInt(e.target.value) || 10 })}
      />

      <Input
        label={t('maxLikesInteract')}
        type="number"
        min={0}
        max={50}
        value={config.max_likes}
        onChange={(e) => updateConfig({ max_likes: parseInt(e.target.value) || 0 })}
      />

      <Input
        label={t('maxShares')}
        type="number"
        min={0}
        max={20}
        value={config.max_shares}
        onChange={(e) => updateConfig({ max_shares: parseInt(e.target.value) || 0 })}
      />

      <Input
        label={t('maxComments')}
        type="number"
        min={0}
        max={20}
        value={config.max_comments}
        onChange={(e) => updateConfig({ max_comments: parseInt(e.target.value) || 0 })}
      />

      {/* Algorithmic Warming Keywords */}
      <div>
        <label className="block text-sm font-semibold mb-1">
          {t('warmingKeywords', 'Từ khóa tạo vết thuật toán')} <span className="text-gray-400 font-normal text-xs">(tìm kiếm Reels/Video theo tệp trước khi lướt)</span>
        </label>
        <div className="flex gap-2 mb-2">
          <input
            className="flex-1 px-2 py-1 border rounded text-sm"
            placeholder={t('warmingKeywordsPlaceholder', 'Nhập keyword (vd: marketing, kinh doanh, thời trang)...')}
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }}
          />
          <button
            type="button"
            onClick={addKeyword}
            className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
          >
            Add
          </button>
        </div>
        {(config.keywords || []).length > 0 && (
          <div className="flex flex-wrap gap-1">
            {(config.keywords || []).map((kw: string) => (
              <span key={kw} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">
                {kw}
                <button type="button" onClick={() => removeKeyword(kw)} className="hover:text-red-600 font-bold">×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="mt-3 p-2 bg-blue-50 rounded text-xs">
        <p className="text-sm font-semibold mb-1">{t('interactModes')}</p>
        <ul className="text-gray-600 space-y-1">
          <li>{t('allModeDesc')}</li>
          <li>{t('newsfeedDesc')}</li>
          <li>{t('reelsDesc')}</li>
          <li>{t('storyDesc')}</li>
        </ul>
      </div>
    </div>
  );
};

// ============================================================================
// REPLY TO COMMENTS PANEL
// ============================================================================

const ReplyToCommentsPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.reply_to_comments_config || node.data.config || {};

  const config: ReplyToCommentsConfig = {
    post_url: rawConfig.post_url || '',
    max_replies: rawConfig.max_replies ?? 1,
    max_comments: rawConfig.max_comments ?? 20,
    content_hints: rawConfig.content_hints || '',
    link_url: rawConfig.link_url || '',
    comment_style: rawConfig.comment_style || {},
    brand_info_node_id: rawConfig.brand_info_node_id,
    dispatch_mode: (rawConfig.dispatch_mode || 'specific') as 'specific' | 'managed' | 'group_posts' | 'filter_posts' | 'from_top_posts',
    seeding_contents_manager_node_id: rawConfig.seeding_contents_manager_node_id,
    find_group_posts_node_id: rawConfig.find_group_posts_node_id,
    filter_post_node_id: rawConfig.filter_post_node_id,
    find_top_posts_node_id: rawConfig.find_top_posts_node_id,
    reply_mode: (rawConfig.reply_mode || 'random') as 'random' | 'keyword',
    reply_keywords: rawConfig.reply_keywords || [],
    reply_max_per_keyword: rawConfig.reply_max_per_keyword ?? 0,
  };

  const updateConfig = (updates: Partial<ReplyToCommentsConfig>) => {
    onChange({
      ...node.data,
      reply_to_comments_config: { ...config, ...updates },
    });
  };

  const [replyKeywordInput, setReplyKeywordInput] = React.useState('');
  const addReplyKeyword = () => {
    const kw = replyKeywordInput.trim();
    if (kw && !(config.reply_keywords || []).includes(kw)) {
      updateConfig({ reply_keywords: [...(config.reply_keywords || []), kw] });
    }
    setReplyKeywordInput('');
  };
  const removeReplyKeyword = (kw: string) =>
    updateConfig({ reply_keywords: (config.reply_keywords || []).filter((k) => k !== kw) });

  const isManaged = config.dispatch_mode === 'managed';
  const isGroupPosts = config.dispatch_mode === 'group_posts';
  const isFilterPosts = config.dispatch_mode === 'filter_posts';
  const isFromTopPosts = config.dispatch_mode === 'from_top_posts';
  const { t } = useTranslation('workflow');

  return (
    <div className="space-y-3">
      <SectionTitle>{t('replyToComments')}</SectionTitle>

      {/* Master Timeline Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <span className="text-lg">📅</span>
          <div className="flex-1">
            <div className="text-sm font-semibold text-blue-900">{t('timingBanner')}</div>
            <div className="text-xs text-blue-700 mt-1">
              {t('timingBannerHint')}
            </div>
          </div>
        </div>
      </div>

      {/* Dispatch Mode */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">{t('postUrlSource')}</label>
        <div className="flex gap-3 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`reply-dispatch-mode-${node.id}`}
              value="specific"
              checked={!isManaged && !isGroupPosts && !isFilterPosts && !isFromTopPosts}
              onChange={() =>
                updateConfig({ dispatch_mode: 'specific', seeding_contents_manager_node_id: undefined, find_group_posts_node_id: undefined, filter_post_node_id: undefined, find_top_posts_node_id: undefined })
              }
            />
            <span className="text-sm">{t('specificUrl')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`reply-dispatch-mode-${node.id}`}
              value="managed"
              checked={isManaged}
              onChange={() => updateConfig({ dispatch_mode: 'managed', post_url: '', find_group_posts_node_id: undefined, filter_post_node_id: undefined, find_top_posts_node_id: undefined })}
            />
            <span className="text-sm">{t('managedMode')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`reply-dispatch-mode-${node.id}`}
              value="group_posts"
              checked={isGroupPosts}
              onChange={() => updateConfig({ dispatch_mode: 'group_posts', post_url: '', seeding_contents_manager_node_id: undefined, filter_post_node_id: undefined, find_top_posts_node_id: undefined })}
            />
            <span className="text-sm">{t('groupPostsMode')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`reply-dispatch-mode-${node.id}`}
              value="filter_posts"
              checked={isFilterPosts}
              onChange={() => updateConfig({ dispatch_mode: 'filter_posts', post_url: '', seeding_contents_manager_node_id: undefined, find_group_posts_node_id: undefined, find_top_posts_node_id: undefined })}
            />
            <span className="text-sm">{t('filterPostsMode')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`reply-dispatch-mode-${node.id}`}
              value="from_top_posts"
              checked={isFromTopPosts}
              onChange={() => updateConfig({ dispatch_mode: 'from_top_posts', post_url: '', seeding_contents_manager_node_id: undefined, find_group_posts_node_id: undefined, filter_post_node_id: undefined })}
            />
            <span className="text-sm">{t('fromTopPostsMode')}</span>
          </label>
        </div>
        {isManaged && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
            {t('managedModeHint')}
          </div>
        )}
        {isGroupPosts && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
            {t('groupPostsModeHint')}
          </div>
        )}
        {isFilterPosts && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
            {t('filterPostsModeHint')}
          </div>
        )}
        {isFromTopPosts && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
            {t('fromTopPostsModeHint')}
          </div>
        )}
      </div>

      {/* Brand Info Selector */}
      <BrandInfoSelector
        value={config.brand_info_node_id}
        onChange={(brandInfoNodeId) => updateConfig({ brand_info_node_id: brandInfoNodeId })}
      />

      {/* SCM Selector (managed mode) */}
      {isManaged && (
        <SeedingContentsManagerSelector
          value={config.seeding_contents_manager_node_id}
          onChange={(id) => updateConfig({ seeding_contents_manager_node_id: id })}
        />
      )}

      {/* Find Group Posts Selector (group_posts mode) */}
      {isGroupPosts && (
        <FindGroupPostsSelector
          value={config.find_group_posts_node_id}
          onChange={(id) => updateConfig({ find_group_posts_node_id: id })}
        />
      )}

      {/* Filter Post Selector (filter_posts mode) */}
      {isFilterPosts && (
        <FilterPostSelector
          value={config.filter_post_node_id}
          onChange={(id) => updateConfig({ filter_post_node_id: id })}
        />
      )}

      {/* Find Top Posts Selector (from_top_posts mode) */}
      {isFromTopPosts && (
        <FindTopPostsSelector
          value={config.find_top_posts_node_id}
          onChange={(id) => updateConfig({ find_top_posts_node_id: id })}
        />
      )}

      {/* Post URL (specific mode only) */}
      {!isManaged && !isGroupPosts && !isFilterPosts && !isFromTopPosts && (
        <Input
          label={t('postUrlLabel')}
          type="url"
          placeholder="https://www.facebook.com/groups/{group_id}/permalink/{post_id}/"
          value={config.post_url}
          onChange={(e) => updateConfig({ post_url: e.target.value })}
        />
      )}

      <Input
        label={t('maxRepliesPerExecution')}
        type="number"
        min={1}
        max={10}
        value={config.max_replies}
        onChange={(e) => updateConfig({ max_replies: parseInt(e.target.value) || 1 })}
        placeholder="1"
      />

      {/* Reply Mode */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">Chế độ reply</label>
        <div className="flex gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`reply-mode-${node.id}`}
              value="random"
              checked={config.reply_mode !== 'keyword'}
              onChange={() => updateConfig({ reply_mode: 'random' })}
            />
            <span className="text-sm">Ngẫu Nhiên</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`reply-mode-${node.id}`}
              value="keyword"
              checked={config.reply_mode === 'keyword'}
              onChange={() => updateConfig({ reply_mode: 'keyword' })}
            />
            <span className="text-sm">Theo Keyword</span>
          </label>
        </div>
      </div>

      {/* Keyword Mode: keyword chip-input + max per keyword */}
      {config.reply_mode === 'keyword' && (
        <>
          <div>
            <label className="block text-sm font-semibold mb-1">
              Keywords <span className="text-gray-400 font-normal text-xs">(reply tất cả comment chứa keyword này)</span>
            </label>
            <div className="flex gap-2 mb-2">
              <input
                className="flex-1 px-2 py-1 border rounded text-sm"
                placeholder="Nhập keyword..."
                value={replyKeywordInput}
                onChange={(e) => setReplyKeywordInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addReplyKeyword(); } }}
              />
              <button
                type="button"
                onClick={addReplyKeyword}
                className="px-3 py-1 bg-purple-500 text-white rounded text-sm hover:bg-purple-600"
              >
                Add
              </button>
            </div>
            {(config.reply_keywords || []).length > 0 && (
              <div className="flex flex-wrap gap-1">
                {(config.reply_keywords || []).map((kw) => (
                  <span key={kw} className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full text-xs">
                    {kw}
                    <button type="button" onClick={() => removeReplyKeyword(kw)} className="hover:text-red-600 font-bold">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <Input
            label="Max replies per keyword (0 = tất cả matching)"
            type="number"
            min={0}
            max={50}
            value={config.reply_max_per_keyword ?? 0}
            onChange={(e) => updateConfig({ reply_max_per_keyword: parseInt(e.target.value) || 0 })}
            placeholder="0"
          />
        </>
      )}

      <Input
        label={t('maxCommentsToLoad')}
        type="number"
        min={5}
        max={100}
        value={config.max_comments}
        onChange={(e) => updateConfig({ max_comments: parseInt(e.target.value) || 20 })}
        placeholder="20"
      />

      <div>
        <label className="block text-sm font-semibold mb-1">{t('contentGuidanceOptional')}</label>
        <textarea
          className="w-full px-3 py-2 border rounded-md"
          rows={3}
          placeholder={t('replyGuidancePlaceholder')}
          value={config.content_hints}
          onChange={(e) => updateConfig({ content_hints: e.target.value })}
        />
      </div>

      <div>
        <label className="block text-sm mb-1">{t('linkUrl', 'Link URL')} <span className="text-gray-400 text-xs">({t('optional', 'optional')})</span></label>
        <Input
          type="url"
          placeholder="https://example.com"
          value={config.link_url || ''}
          onChange={(e) => updateConfig({ link_url: e.target.value })}
        />
      </div>
    </div>
  );
};

// ============================================================================
// VIEW NOTIFICATIONS PANEL
// ============================================================================

const ViewNotificationsPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.view_notifications_config || node.data.config || {};

  const config = {
    max_notifications: rawConfig.max_notifications || 10,
    mark_as_read: rawConfig.mark_as_read || false,
    brand_info_node_id: rawConfig.brand_info_node_id,
  };

  const updateConfig = (updates: any) => {
    onChange({
      ...node.data,
      view_notifications_config: { ...config, ...updates },
    });
  };
  const { t } = useTranslation('workflow');

  return (
    <div className="space-y-3">
      <SectionTitle>{t('viewNotifications')}</SectionTitle>

      {/* Brand Info Selector */}
      <BrandInfoSelector
        value={config.brand_info_node_id}
        onChange={(brandInfoNodeId) => updateConfig({ brand_info_node_id: brandInfoNodeId })}
      />

      <div>
        <label className="block text-sm mb-1">{t('maxNotifications')}</label>
        <input
          type="number"
          min="1"
          max="50"
          className="w-full px-2 py-1 border rounded"
          value={config.max_notifications}
          onChange={(e) => updateConfig({ max_notifications: parseInt(e.target.value) || 10 })}
        />
        <span className="text-xs text-gray-500">{t('maxNotificationsHint')}</span>
      </div>

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={config.mark_as_read}
          onChange={(e) => updateConfig({ mark_as_read: e.target.checked })}
        />
        <span className="text-sm">{t('markAsRead')}</span>
      </label>
    </div>
  );
};

// ============================================================================
// ACCEPT FRIEND REQUESTS PANEL (PASSIVE)
// ============================================================================

const AcceptFriendRequestsPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const config = node.data.accept_friend_requests_config || node.data.config || {
    max_accepts: 10,
  };

  const updateConfig = (updates: any) => {
    onChange({
      ...node.data,
      accept_friend_requests_config: { ...config, ...updates },
    });
  };
  const { t } = useTranslation('workflow');

  return (
    <div className="space-y-3">
      <SectionTitle>{t('acceptFriendRequests')}</SectionTitle>

      <div className="space-y-2">
        <label className="text-sm font-semibold">{t('maxAccepts')}</label>
        <input
          type="number"
          min="1"
          max="50"
          value={config.max_accepts}
          onChange={(e) => updateConfig({ max_accepts: parseInt(e.target.value) || 10 })}
          className="w-full p-2 border rounded"
          placeholder={t('maxAcceptsPlaceholder')}
        />
        <p className="text-xs text-gray-500">
          {t('maxAcceptsHint')}
        </p>
      </div>
    </div>
  );
};

// ============================================================================
// SEARCH ADD FRIEND PANEL (ACTIVE)
// ============================================================================

const SearchAddFriendPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.search_add_friend_config || node.data.config || {};
  const config = {
    type: rawConfig.type || 'keyword',
    keywords: rawConfig.keywords || [],
    max_add: rawConfig.max_add || 3,
    find_top_posts_node_id: rawConfig.find_top_posts_node_id,
  };

  const [keywordInput, setKeywordInput] = React.useState('');
  const { t } = useTranslation('workflow');

  const updateConfig = (updates: any) => {
    onChange({
      ...node.data,
      search_add_friend_config: { ...config, ...updates },
    });
  };

  const addKeyword = () => {
    const kw = keywordInput.trim();
    if (kw && !config.keywords.includes(kw)) {
      updateConfig({ keywords: [...config.keywords, kw] });
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword: string) => {
    updateConfig({ keywords: config.keywords.filter((k: string) => k !== keyword) });
  };

  const isFromTopPosts = config.type === 'from_top_posts';

  return (
    <div className="space-y-3">
      <SectionTitle>{t('searchAddFriend')}</SectionTitle>

      {/* Type toggle */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">Chế độ thêm bạn</label>
        <div className="flex gap-3 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`saf-type-${node.id}`}
              value="keyword"
              checked={!isFromTopPosts}
              onChange={() => updateConfig({ type: 'keyword', find_top_posts_node_id: undefined })}
            />
            <span className="text-sm">Theo từ khóa</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`saf-type-${node.id}`}
              value="from_top_posts"
              checked={isFromTopPosts}
              onChange={() => updateConfig({ type: 'from_top_posts', keywords: [] })}
            />
            <span className="text-sm">Từ danh sách Top Post</span>
          </label>
        </div>
      </div>

      {/* Keyword mode */}
      {!isFromTopPosts && (
        <div>
          <label className="block text-sm font-semibold mb-1">{t('searchAddFriendKeywords')}</label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              className="flex-1 px-2 py-1 border rounded text-sm"
              placeholder={t('searchAddFriendPlaceholder')}
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
            />
            <Button size="sm" variant="secondary" onClick={addKeyword}>
              Add
            </Button>
          </div>
          <div className="flex flex-wrap gap-1">
            {config.keywords.map((keyword: string) => (
              <Badge key={keyword} variant="primary">
                {keyword}
                <button className="ml-1" onClick={() => removeKeyword(keyword)}>&times;</button>
              </Badge>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {t('searchAddFriendKeywordsHint')}
          </p>
        </div>
      )}

      {/* From Top Posts mode */}
      {isFromTopPosts && (
        <FindTopPostsSelector
          value={config.find_top_posts_node_id}
          onChange={(id) => updateConfig({ find_top_posts_node_id: id })}
        />
      )}

      <div className="space-y-2">
        <label className="text-sm font-semibold">{t('maxFriendRequests')}</label>
        <input
          type="number"
          min="1"
          max="20"
          value={config.max_add}
          onChange={(e) => updateConfig({ max_add: parseInt(e.target.value) || 3 })}
          className="w-full p-2 border rounded"
          placeholder={t('maxFriendRequestsPlaceholder')}
        />
        <p className="text-xs text-gray-500">
          {t('maxFriendRequestsHint')}
        </p>
      </div>
    </div>
  );
};

// ============================================================================
// CHECK MESSAGE PANEL (PARALLEL INBOX SCANNER)
// ============================================================================

const CheckMessagePanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  // No config needed - this node scans all folders automatically
  const { t } = useTranslation('workflow');

  return (
    <div className="space-y-3">
      <SectionTitle>{t('checkMessage')}</SectionTitle>

      <div className="p-3 bg-blue-50 rounded text-sm">
        <p className="font-semibold mb-2">{t('checkMessageFolders')}</p>
        <ul className="space-y-1 ml-4">
          <li>{t('checkMessageFolder1')}</li>
          <li>{t('checkMessageFolder2')}</li>
          <li>{t('checkMessageFolder3')}</li>
        </ul>
      </div>

      <div className="p-3 bg-green-50 rounded text-sm">
        <p className="font-semibold mb-2">{t('checkMessageFiltering')}</p>
        <ul className="space-y-1 ml-4 text-gray-700">
          <li>• {t('checkMessageFilter1')}</li>
          <li>• {t('checkMessageFilter2')}</li>
          <li>• {t('checkMessageFilter3')}</li>
          <li>• {t('checkMessageFilter4')}</li>
        </ul>
      </div>

      <div className="p-3 bg-gray-50 rounded text-xs text-gray-600">
        {t('checkMessageNote')}
      </div>
    </div>
  );
};

// ============================================================================
// SEND DIRECT MESSAGE PANEL
// ============================================================================

const SendDirectMessagePanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const { t } = useTranslation('workflow');
  const config = (node.data.send_direct_message_config || node.data.config || {
    recipients: [],
    messages: [],
  }) as { recipients: string[]; messages: string[] };

  const updateConfig = (updates: Partial<{ recipients: string[]; messages: string[] }>) => {
    onChange({ ...node.data, send_direct_message_config: { ...config, ...updates } });
  };

  // Use local state for raw textarea text so empty lines / trailing spaces are preserved during editing
  const [recipientsText, setRecipientsText] = useState(() => (config.recipients || []).join('\n'));
  const [messagesText, setMessagesText] = useState(() => (config.messages || []).join('\n---\n'));

  // Reset local text when switching to a different node
  useEffect(() => {
    setRecipientsText((config.recipients || []).join('\n'));
    setMessagesText((config.messages || []).join('\n---\n'));
  }, [node.id]);

  // One recipient per line
  const handleRecipientsChange = (text: string) => {
    setRecipientsText(text);
    updateConfig({ recipients: text.split('\n').map(l => l.trim()).filter(l => l.length > 0) });
  };

  // Multiple messages separated by a line containing only ---
  const handleMessagesChange = (text: string) => {
    setMessagesText(text);
    updateConfig({ messages: text.split('\n---\n').map(l => l.trim()).filter(l => l.length > 0) });
  };

  const recipientCount = (config.recipients || []).length;
  const messageCount = (config.messages || []).length;

  return (
    <div className="space-y-4">
      <SectionTitle>{t('sendDirectMessage')}</SectionTitle>

      {/* Recipients */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">
          {t('dmRecipients')}
          {recipientCount > 0 && (
            <span className="ml-2 text-xs font-normal text-blue-600">{recipientCount} {t('dmRecipientsCount')}</span>
          )}
        </label>
        <textarea
          value={recipientsText}
          onChange={(e) => handleRecipientsChange(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          rows={5}
          placeholder={`100079106287255\nusername123\nhttps://facebook.com/someone`}
        />
        <p className="text-xs text-gray-500">{t('dmRecipientsHint')}</p>
      </div>

      {/* Messages */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">
          {t('dmMessages')}
          {messageCount > 0 && (
            <span className="ml-2 text-xs font-normal text-blue-600">{messageCount} {t('dmMessagesCount')}</span>
          )}
        </label>
        <textarea
          value={messagesText}
          onChange={(e) => handleMessagesChange(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          rows={7}
          placeholder={t('dmMessagesPlaceholder')}
        />
        <p className="text-xs text-gray-500">{t('dmMessagesHint')}</p>
      </div>

      {/* Summary */}
      {(recipientCount > 0 || messageCount > 0) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
          <span className="font-semibold">📨 {t('dmSummary', { recipients: recipientCount, messages: messageCount, total: recipientCount * messageCount })}</span>
        </div>
      )}

      {/* Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
        ⚠️ {t('dmWarning')}
      </div>
    </div>
  );
};

const PostAutoResponderPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const { t } = useTranslation('workflow');
  const config = (node.data.post_auto_responder_config || node.data.config || {
    post_url: '',
    keywords: [],
    message: '',
    reply_text: 'Mình đã inbox bạn nhé!',
  }) as {
    post_url: string;
    keywords: string[];
    message: string;
    reply_text: string;
    dispatch_mode?: 'specific' | 'managed' | 'group_posts' | 'filter_posts' | 'from_top_posts';
    seeding_contents_manager_node_id?: string;
    find_group_posts_node_id?: string;
    filter_post_node_id?: string;
    find_top_posts_node_id?: string;
  };

  const updateConfig = (updates: Partial<typeof config>) => {
    onChange({ ...node.data, post_auto_responder_config: { ...config, ...updates } });
  };

  const isManaged = config.dispatch_mode === 'managed';
  const isGroupPosts = config.dispatch_mode === 'group_posts';
  const isFilterPosts = config.dispatch_mode === 'filter_posts';
  const isFromTopPosts = config.dispatch_mode === 'from_top_posts';
  const isSpecific = !isManaged && !isGroupPosts && !isFilterPosts && !isFromTopPosts;

  const [keywordsText, setKeywordsText] = useState(() => (config.keywords || []).join(', '));

  useEffect(() => {
    setKeywordsText((config.keywords || []).join(', '));
  }, [node.id]);

  const handleKeywordsChange = (text: string) => {
    setKeywordsText(text);
    updateConfig({ keywords: text.split(',').map(k => k.trim()).filter(k => k.length > 0) });
  };

  const keywordCount = (config.keywords || []).length;

  return (
    <div className="space-y-4">
      <SectionTitle>{t('postAutoResponder')}</SectionTitle>

      {/* Dispatch Mode */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">{t('postUrlSource')}</label>
        <div className="flex gap-3 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`dispatch-mode-${node.id}`}
              value="specific"
              checked={isSpecific}
              onChange={() => updateConfig({ dispatch_mode: 'specific', seeding_contents_manager_node_id: undefined, find_group_posts_node_id: undefined, filter_post_node_id: undefined, find_top_posts_node_id: undefined })}
            />
            <span className="text-sm">{t('specificUrl')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`dispatch-mode-${node.id}`}
              value="managed"
              checked={isManaged}
              onChange={() => updateConfig({ dispatch_mode: 'managed', post_url: '', find_group_posts_node_id: undefined, filter_post_node_id: undefined, find_top_posts_node_id: undefined })}
            />
            <span className="text-sm">{t('managedMode')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`dispatch-mode-${node.id}`}
              value="group_posts"
              checked={isGroupPosts}
              onChange={() => updateConfig({ dispatch_mode: 'group_posts', post_url: '', seeding_contents_manager_node_id: undefined, filter_post_node_id: undefined, find_top_posts_node_id: undefined })}
            />
            <span className="text-sm">{t('groupPostsMode')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`dispatch-mode-${node.id}`}
              value="filter_posts"
              checked={isFilterPosts}
              onChange={() => updateConfig({ dispatch_mode: 'filter_posts', post_url: '', seeding_contents_manager_node_id: undefined, find_group_posts_node_id: undefined, find_top_posts_node_id: undefined })}
            />
            <span className="text-sm">{t('filterPostsMode')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`dispatch-mode-${node.id}`}
              value="from_top_posts"
              checked={isFromTopPosts}
              onChange={() => updateConfig({ dispatch_mode: 'from_top_posts', post_url: '', seeding_contents_manager_node_id: undefined, find_group_posts_node_id: undefined, filter_post_node_id: undefined })}
            />
            <span className="text-sm">{t('fromTopPostsMode')}</span>
          </label>
        </div>
        {isManaged && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">{t('managedModeHint')}</div>
        )}
        {isGroupPosts && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">{t('groupPostsModeHint')}</div>
        )}
        {isFilterPosts && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">{t('filterPostsModeHint')}</div>
        )}
        {isFromTopPosts && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">{t('fromTopPostsModeHint')}</div>
        )}
      </div>

      {/* SCM Selector (managed mode) */}
      {isManaged && (
        <SeedingContentsManagerSelector
          value={config.seeding_contents_manager_node_id}
          onChange={(id) => updateConfig({ seeding_contents_manager_node_id: id })}
        />
      )}

      {/* Find Group Posts Selector (group_posts mode) */}
      {isGroupPosts && (
        <FindGroupPostsSelector
          value={config.find_group_posts_node_id}
          onChange={(id) => updateConfig({ find_group_posts_node_id: id })}
        />
      )}

      {/* Filter Post Selector (filter_posts mode) */}
      {isFilterPosts && (
        <FilterPostSelector
          value={config.filter_post_node_id}
          onChange={(id) => updateConfig({ filter_post_node_id: id })}
        />
      )}

      {/* Find Top Posts Selector (from_top_posts mode) */}
      {isFromTopPosts && (
        <FindTopPostsSelector
          value={config.find_top_posts_node_id}
          onChange={(id) => updateConfig({ find_top_posts_node_id: id })}
        />
      )}

      {/* Post URL (specific mode only) */}
      {isSpecific && (
        <div className="space-y-2">
          <label className="block text-sm font-semibold">{t('parPostUrl')}</label>
          <input
            type="text"
            value={config.post_url || ''}
            onChange={(e) => updateConfig({ post_url: e.target.value.trim() })}
            className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="https://www.facebook.com/groups/.../posts/..."
          />
        </div>
      )}

      {/* Keywords */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">
          {t('parKeywords')}
          {keywordCount > 0 && (
            <span className="ml-2 text-xs font-normal text-blue-600">{keywordCount} {t('parKeywordsCount')}</span>
          )}
        </label>
        <input
          type="text"
          value={keywordsText}
          onChange={(e) => handleKeywordsChange(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder={t('parKeywordsPlaceholder')}
        />
        <p className="text-xs text-gray-500">{t('parKeywordsHint')}</p>
      </div>

      {/* DM Message */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">{t('parMessage')}</label>
        <textarea
          value={config.message || ''}
          onChange={(e) => updateConfig({ message: e.target.value })}
          className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          rows={4}
          placeholder={t('parMessagePlaceholder')}
        />
        <p className="text-xs text-gray-500">{t('parMessageHint')}</p>
      </div>

      {/* Reply text */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">{t('parReplyText')}</label>
        <input
          type="text"
          value={config.reply_text || ''}
          onChange={(e) => updateConfig({ reply_text: e.target.value })}
          className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Mình đã inbox bạn nhé!"
        />
        <p className="text-xs text-gray-500">{t('parReplyTextHint')}</p>
      </div>

      {/* Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
        ⚠️ {t('parWarning')}
      </div>
    </div>
  );
};

// ============================================================================
// GROUP RANDOM MESSENGER PANEL
// ============================================================================

const GroupRandomMessengerPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const { t } = useTranslation('workflow');
  const config = (node.data.group_random_messenger_config || node.data.config || {
    group_url: '',
    message: '',
    limit: 5,
  }) as { group_url: string; message: string; limit: number };

  const updateConfig = (updates: Partial<typeof config>) => {
    onChange({ ...node.data, group_random_messenger_config: { ...config, ...updates } });
  };

  return (
    <div className="space-y-4">
      <SectionTitle>{t('groupRandomMessenger')}</SectionTitle>

      {/* Group URL */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">{t('grmGroupUrl')}</label>
        <input
          type="url"
          value={config.group_url || ''}
          onChange={(e) => updateConfig({ group_url: e.target.value.trim() })}
          className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="https://www.facebook.com/groups/123456789"
        />
      </div>

      {/* Message */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold">{t('grmMessage')}</label>
        <textarea
          value={config.message || ''}
          onChange={(e) => updateConfig({ message: e.target.value })}
          className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          rows={5}
          placeholder={t('grmMessageHint')}
        />
      </div>

      {/* Limit */}
      <div className="space-y-1">
        <label className="block text-sm font-semibold">{t('grmLimit')}</label>
        <input
          type="number"
          min={1}
          max={50}
          value={config.limit ?? 5}
          onChange={(e) => updateConfig({ limit: Math.max(1, Math.min(50, parseInt(e.target.value) || 5)) })}
          className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <p className="text-xs text-gray-500">{t('grmLimitHint', { limit: config.limit ?? 5 })}</p>
      </div>

      {/* Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
        ⚠️ {t('grmWarning')}
      </div>
    </div>
  );
};

// ============================================================================
// WITH ZALO PANELS
// ============================================================================

const ZaloAddFriendPanel: React.FC<{ node: WorkflowNode; onChange: (config: any) => void }> = ({ node, onChange }) => {
  const { t } = useTranslation('workflow');
  const config = node.data.zalo_add_friend_config || node.data.config || {};
  const [phonesText, setPhonesText] = useState((config.phone_numbers || []).map((p: any) => typeof p === 'string' ? p : p.phoneNumber || '').join('\n'));

  const handleChange = (field: string, value: any) => {
    onChange({ ...node.data, zalo_add_friend_config: { ...config, [field]: value } });
  };

  const handlePhonesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setPhonesText(text);
    const phones = text.split('\n').map(p => p.trim()).filter(Boolean);
    handleChange('phone_numbers', phones);
  };

  return (
    <div className="space-y-4">
      <SectionTitle>Zalo Add Friend</SectionTitle>
      <div>
        <label className="block text-sm font-semibold mb-1">Phone Numbers (one per line)</label>
        <TextArea
          value={phonesText}
          onChange={handlePhonesChange}
          placeholder="0912345678\n0987654321"
          rows={5}
        />
      </div>
      <div>
        <label className="block text-sm font-semibold mb-1">Global Message</label>
        <Input
          value={config.global_message || ''}
          onChange={(e) => handleChange('global_message', e.target.value)}
          placeholder="Xin chào, mình kết bạn nhé!"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold mb-1">Min Delay (s)</label>
          <Input
            type="number"
            value={config.min_delay ?? 5}
            onChange={(e) => handleChange('min_delay', Number(e.target.value))}
          />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1">Max Delay (s)</label>
          <Input
            type="number"
            value={config.max_delay ?? 15}
            onChange={(e) => handleChange('max_delay', Number(e.target.value))}
          />
        </div>
      </div>
    </div>
  );
};

const ZaloJoinGroupPanel: React.FC<{ node: WorkflowNode; onChange: (config: any) => void }> = ({ node, onChange }) => {
  const { t } = useTranslation('workflow');
  const config = node.data.zalo_join_group_config || node.data.config || {};

  return (
    <div className="space-y-4">
      <SectionTitle>Zalo Join Group</SectionTitle>
      <div>
        <label className="block text-sm font-semibold mb-1">Invite Link</label>
        <Input
          value={config.invite_link || ''}
          onChange={(e) => onChange({ ...node.data, zalo_join_group_config: { ...config, invite_link: e.target.value } })}
          placeholder="https://zalo.me/g/..."
        />
      </div>
    </div>
  );
};

const ZaloGroupMembersPanel: React.FC<{ node: WorkflowNode; onChange: (config: any) => void }> = ({ node, onChange }) => {
  const { t } = useTranslation('workflow');
  const config = node.data.zalo_group_members_config || node.data.config || {};

  return (
    <div className="space-y-4">
      <SectionTitle>Zalo Group Members</SectionTitle>
      <div>
        <label className="block text-sm font-semibold mb-1">Group ID</label>
        <Input
          value={config.group_id || ''}
          onChange={(e) => onChange({ ...node.data, zalo_group_members_config: { ...config, group_id: e.target.value } })}
          placeholder="Enter group ID..."
        />
      </div>
      <div>
        <label className="block text-sm font-semibold mb-1">Group Name (Fallback)</label>
        <Input
          value={config.group_name || ''}
          onChange={(e) => onChange({ ...node.data, zalo_group_members_config: { ...config, group_name: e.target.value } })}
          placeholder="Enter exact group name..."
        />
      </div>
    </div>
  );
};

const ZaloProactiveSenderPanel: React.FC<{ node: WorkflowNode; onChange: (config: any) => void }> = ({ node, onChange }) => {
  const { t } = useTranslation('workflow');
  const config = node.data.zalo_proactive_sender_config || node.data.config || {};
  const [recipientsText, setRecipientsText] = useState((config.recipients || []).map((r: any) => typeof r === 'string' ? r : r.phoneNumber || r.userId || r.displayName || '').join('\n'));

  const handleChange = (field: string, value: any) => {
    onChange({ ...node.data, zalo_proactive_sender_config: { ...config, [field]: value } });
  };

  const handleRecipientsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setRecipientsText(text);
    const recs = text.split('\n').map(p => p.trim()).filter(Boolean);
    handleChange('recipients', recs);
  };

  return (
    <div className="space-y-4">
      <SectionTitle>Zalo Proactive Sender</SectionTitle>
      <div>
        <label className="block text-sm font-semibold mb-1">Recipients (Phones/Names, one per line)</label>
        <TextArea
          value={recipientsText}
          onChange={handleRecipientsChange}
          placeholder="0912345678\nNguyen Van A"
          rows={5}
        />
      </div>
      <div>
        <label className="block text-sm font-semibold mb-1">Global Message Text</label>
        <TextArea
          value={config.global_text || ''}
          onChange={(e) => handleChange('global_text', e.target.value)}
          placeholder="Chao {{name}}, day la tin nhan..."
          rows={3}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold mb-1">Min Delay (s)</label>
          <Input
            type="number"
            value={config.min_delay ?? 8}
            onChange={(e) => handleChange('min_delay', Number(e.target.value))}
          />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1">Max Delay (s)</label>
          <Input
            type="number"
            value={config.max_delay ?? 20}
            onChange={(e) => handleChange('max_delay', Number(e.target.value))}
          />
        </div>
      </div>
    </div>
  );
};
// ============================================================================
// CREATE POST THREADS PANEL
// ============================================================================

function CreatePostThreadsPanel({ node, onChange }: { node: any; onChange: (n: any) => void }) {
  const { t } = useTranslation('workflow');
  const rawConfig = node.data.create_post_threads_config || node.data.config || {};

  const config = {
    content_type: rawConfig.content_type || 'both',
    content_hints: rawConfig.content_hints || '',
    image_source_type: rawConfig.image_source_type || 'none',
    image_asset_id: rawConfig.image_asset_id || '',
    brand_info_node_id: rawConfig.brand_info_node_id,
  };

  const updateConfig = (updates: any) => {
    onChange({
      ...node.data,
      create_post_threads_config: { ...config, ...updates },
    });
  };

  return (
    <div className="space-y-4">
      <SectionTitle>Create Post on Threads</SectionTitle>

      <BrandInfoSelector
        value={config.brand_info_node_id}
        onChange={(brandInfoNodeId) => updateConfig({ brand_info_node_id: brandInfoNodeId })}
      />

      <div>
        <label className="block text-sm mb-1">{t('contentType')}</label>
        <select
          className="w-full px-2 py-1 border rounded"
          value={config.content_type}
          onChange={(e) => updateConfig({ content_type: e.target.value })}
        >
          <option value="both">{t('textAndImage', 'Text and Image')}</option>
          <option value="image">{t('imageOnly', 'Image Only')}</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1.5">{t('imageAttachment')}</label>
        <select
          className="w-full px-2 py-1 border rounded text-sm"
          value={config.image_source_type}
          onChange={(e) =>
            updateConfig({
              image_source_type: e.target.value,
              image_asset_id: e.target.value !== 'vault_asset' ? '' : config.image_asset_id,
            })
          }
        >
          <option value="none">{t('noImage')}</option>
          <option value="context">{t('fromPreviousNode')}</option>
          <option value="character_context">{t('characterContext', 'Character Context (Image2Image)')}</option>
          <option value="vault_asset">{t('pickFromVault')}</option>
        </select>

        {config.image_source_type === 'context' && (
          <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
            {t('contextImageHint')}
          </div>
        )}

        {config.image_source_type === 'vault_asset' && (
          <div className="mt-2">
            <div className="text-xs text-gray-600 mb-1">
              {t('selectImageFromVault')}
            </div>
            <VaultAssetPicker
              mediaType="image"
              selectedId={config.image_asset_id || undefined}
              onSelect={(id) => updateConfig({ image_asset_id: id })}
            />
            {config.image_asset_id && (
              <p className="text-xs text-gray-500 mt-1 truncate">
                {t('selectedImage', { id: config.image_asset_id })}
              </p>
            )}
          </div>
        )}
      </div>

      {config.content_type !== 'image' && (
        <div>
          <label className="block text-sm mb-1">{t('contentHints')}</label>
          <textarea
            className="w-full px-2 py-1 border rounded text-sm"
            rows={3}
            placeholder={t('contentHintsPlaceholder')}
            value={config.content_hints}
            onChange={(e) => updateConfig({ content_hints: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}

// ============================================================================
// LIKE POST THREADS PANEL
// ============================================================================

const LikePostThreadsPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.like_post_threads_config || node.data.config || {};
  const config = { urls: rawConfig.urls || [] };

  const updateConfig = (updates: any) => {
    onChange({ ...node.data, like_post_threads_config: { ...config, ...updates } });
  };

  const urlsText = (config.urls || []).join('\n');

  return (
    <div className="space-y-4">
      <SectionTitle>Like Post on Threads</SectionTitle>
      <div className="space-y-2">
        <label className="block text-sm font-semibold">Threads Post URLs</label>
        <textarea
          key={`th-like-urls-${node.id}`}
          defaultValue={urlsText}
          onBlur={(e) => {
            const urls = e.target.value.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);
            updateConfig({ urls });
          }}
          className="w-full p-2 border rounded font-mono text-sm"
          rows={5}
          placeholder="https://www.threads.net/..."
        />
        <p className="text-xs text-gray-500">Each URL on a new line.</p>
      </div>
    </div>
  );
};

// ============================================================================
// SHARE POST THREADS PANEL
// ============================================================================

const SharePostThreadsPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.share_post_threads_config || node.data.config || {};
  const config = { 
    urls: rawConfig.urls || [],
    share_type: rawConfig.share_type || 'repost'
  };

  const updateConfig = (updates: any) => {
    onChange({ ...node.data, share_post_threads_config: { ...config, ...updates } });
  };

  const urlsText = (config.urls || []).join('\n');

  return (
    <div className="space-y-4">
      <SectionTitle>Share Post on Threads</SectionTitle>
      
      <div>
        <label className="block text-sm mb-1">Share Type</label>
        <select
          className="w-full px-2 py-1 border rounded text-sm"
          value={config.share_type}
          onChange={(e) => updateConfig({ share_type: e.target.value })}
        >
          <option value="repost">Repost</option>
          <option value="quote">Quote</option>
        </select>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold">Threads Post URLs</label>
        <textarea
          key={`th-share-urls-${node.id}`}
          defaultValue={urlsText}
          onBlur={(e) => {
            const urls = e.target.value.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);
            updateConfig({ urls });
          }}
          className="w-full p-2 border rounded font-mono text-sm"
          rows={5}
          placeholder="https://www.threads.net/..."
        />
        <p className="text-xs text-gray-500">Each URL on a new line.</p>
      </div>
    </div>
  );
};

// ============================================================================
// COMMENT POST THREADS PANEL
// ============================================================================

const CommentPostThreadsPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.comment_post_threads_config || node.data.config || {};
  const config = { urls: rawConfig.urls || [] };

  const updateConfig = (updates: any) => {
    onChange({ ...node.data, comment_post_threads_config: { ...config, ...updates } });
  };

  const urlsText = (config.urls || []).join('\n');

  return (
    <div className="space-y-4">
      <SectionTitle>Comment Post on Threads</SectionTitle>
      <div className="space-y-2">
        <label className="block text-sm font-semibold">Threads Post URLs</label>
        <textarea
          key={`th-comment-urls-${node.id}`}
          defaultValue={urlsText}
          onBlur={(e) => {
            const urls = e.target.value.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);
            updateConfig({ urls });
          }}
          className="w-full p-2 border rounded font-mono text-sm"
          rows={5}
          placeholder="https://www.threads.net/..."
        />
        <p className="text-xs text-gray-500">Each URL on a new line.</p>
      </div>
    </div>
  );
};

// ============================================================================
// FOLLOW THREADS PANEL
// ============================================================================

const FollowThreadsPanel: React.FC<{
  node: WorkflowNode;
  onChange: (config: any) => void;
}> = ({ node, onChange }) => {
  const rawConfig = node.data.follow_threads_config || node.data.config || {};
  const config = { urls: rawConfig.urls || [] };

  const updateConfig = (updates: any) => {
    onChange({ ...node.data, follow_threads_config: { ...config, ...updates } });
  };

  const urlsText = (config.urls || []).join('\n');

  return (
    <div className="space-y-4">
      <SectionTitle>Follow on Threads</SectionTitle>
      <div className="space-y-2">
        <label className="block text-sm font-semibold">Threads Profile URLs</label>
        <textarea
          key={`th-follow-urls-${node.id}`}
          defaultValue={urlsText}
          onBlur={(e) => {
            const urls = e.target.value.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);
            updateConfig({ urls });
          }}
          className="w-full p-2 border rounded font-mono text-sm"
          rows={5}
          placeholder="https://www.threads.net/@username"
        />
        <p className="text-xs text-gray-500">Each URL on a new line.</p>
      </div>
    </div>
  );
};

// ============ LINKEDIN ============
const LinkedinConnectMessagePanel: React.FC<{ node: any; onChange: (cfg: any) => void }> = ({ node, onChange }) => {
  const config = node.data?.linkedin_connect_message_config || node.data?.config || {};
  const updateConfig = (updates: any) => {
    const updated = { ...config, ...updates };
    onChange({
      ...node.data,
      linkedin_connect_message_config: updated,
      config: updated,
    });
  };

  const actionType = config.action_type || 'auto';
  const maxLimit = typeof config.max_limit === 'number' ? config.max_limit : 5;

  return (
    <div className="space-y-4">
      <SectionTitle>LinkedIn Connect & Message</SectionTitle>
      
      <div>
        <label className="block text-sm font-semibold mb-1">Phương thức gửi (Action Mode)</label>
        <select
          className="w-full px-2 py-1.5 border rounded text-sm bg-white"
          value={actionType}
          onChange={(e) => updateConfig({ action_type: e.target.value })}
        >
          <option value="auto">🌟 Tự động tối ưu (Ưu tiên Nhắn tin, dự phòng Kết nối)</option>
          <option value="direct_message">💬 Gửi tin nhắn trực tiếp vào Hộp thư (Direct Message)</option>
          <option value="connect_note">🤝 Gửi lời mời kết nối kèm thông điệp (Connect + Add Note)</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          {actionType === 'direct_message' && 'Chỉ nhắn tin trực tiếp tới Profile (1st-degree connection hoặc Open InMail).'}
          {actionType === 'connect_note' && 'Gửi lời mời kết nối kèm lời nhắn ngắn chào hỏi.'}
          {actionType === 'auto' && 'Hệ thống tự động thử nhắn tin trực tiếp trước, nếu chưa kết nối sẽ tự chuyển sang gửi lời mời kết nối kèm tin nhắn.'}
        </p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold">Đối tượng mục tiêu (URL Profile hoặc Từ khóa tìm kiếm)</label>
        <input
          type="text"
          defaultValue={config.target_identifier || ''}
          onBlur={(e) => updateConfig({ target_identifier: e.target.value })}
          className="w-full p-2 border rounded text-sm"
          placeholder="VD: 'Giám đốc Marketing', 'CEO Fintech' hoặc https://linkedin.com/in/username"
        />
        <p className="text-xs text-gray-500">
          Nhập từ khóa tìm kiếm để tiếp cận hàng loạt hoặc dán link profile cá nhân cụ thể.
        </p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold">Nội dung thông điệp (Message Content)</label>
        <textarea
          defaultValue={config.message_content || ''}
          onBlur={(e) => updateConfig({ message_content: e.target.value })}
          className="w-full p-2 border rounded font-mono text-sm"
          rows={5}
          placeholder="Chào bạn, mình thấy profile của bạn rất ấn tượng và muốn kết nối trao đổi về..."
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold">Số lượng tối đa mỗi lượt chạy (Max Profiles)</label>
        <input
          type="number"
          min={1}
          max={20}
          defaultValue={maxLimit}
          onBlur={(e) => updateConfig({ max_limit: parseInt(e.target.value, 10) || 5 })}
          className="w-full p-2 border rounded text-sm"
        />
        <p className="text-xs text-gray-500">
          Khống chế tối đa số lượng người nhận trong một đợt chạy (Mặc định: 5 người).
        </p>
      </div>
    </div>
  );
};

const LinkedinJoinGroupPanel: React.FC<{ node: any; onChange: (cfg: any) => void }> = ({ node, onChange }) => {
  const config = node.data?.linkedin_join_group_config || node.data?.config || {};
  const updateConfig = (updates: any) => {
    const updated = { ...config, ...updates };
    onChange({
      ...node.data,
      linkedin_join_group_config: updated,
      config: updated,
    });
  };

  const maxLimit = typeof config.max_limit === 'number' ? config.max_limit : 5;
  const autoJoin = config.auto_join !== false;

  return (
    <div className="space-y-4">
      <SectionTitle>LinkedIn Search & Join Groups</SectionTitle>
      
      <div className="space-y-2">
        <label className="block text-sm font-semibold">Mục tiêu tìm kiếm (Từ khóa hoặc URL Nhóm)</label>
        <input
          type="text"
          defaultValue={config.target_identifier || ''}
          onBlur={(e) => updateConfig({ target_identifier: e.target.value })}
          className="w-full p-2 border rounded text-sm"
          placeholder="VD: 'Nhân sự', 'Giáo dục số', 'Kỹ sư phần mềm' hoặc https://linkedin.com/groups/10485815/"
        />
        <p className="text-xs text-gray-500">
          Nhập từ khóa để tự động quét tìm các cộng đồng phù hợp hoặc dán đường link nhóm cụ thể.
        </p>
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={autoJoin}
            onChange={(e) => updateConfig({ auto_join: e.target.checked })}
            className="rounded border-gray-300 text-tg-primary focus:ring-tg-primary"
          />
          <span className="text-sm font-medium">Tự động bấm Tham gia (Auto Join)</span>
        </label>
        <p className="text-xs text-gray-500">
          Khi bật, hệ thống sẽ tự động click nút "Tham gia" (Join) sau khi quét và trích xuất dữ liệu của nhóm.
        </p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold">Số lượng nhóm tối đa mỗi lượt chạy (Max Groups)</label>
        <input
          type="number"
          min={1}
          max={20}
          defaultValue={maxLimit}
          onBlur={(e) => updateConfig({ max_limit: parseInt(e.target.value, 10) || 5 })}
          className="w-full p-2 border rounded text-sm"
        />
        <p className="text-xs text-gray-500">
          Khống chế tối đa số lượng nhóm xử lý trong một đợt chạy (Mặc định: 5 nhóm).
        </p>
      </div>
    </div>
  );
};

// ============ VIDEO PRODUCTION & TRENDS PANELS ============

const FindTrendOmnichannelPanel: React.FC<{ node: any; onChange: (cfg: any) => void }> = ({ node, onChange }) => {
  const config = node.data?.find_trend_omnichannel_config || node.data?.config || {};
  const updateConfig = (updates: any) => {
    const updated = { ...config, ...updates };
    onChange({
      ...node.data,
      find_trend_omnichannel_config: updated,
      config: updated,
    });
  };

  const platform = config.platform || 'tiktok';
  const keywordsText = (config.keywords || []).join('\n');
  const maxResults = typeof config.max_results === 'number' ? config.max_results : 10;
  const timeWindow = typeof config.time_window_hours === 'number' ? config.time_window_hours : 72;
  const minVelocity = typeof config.min_velocity_score === 'number' ? config.min_velocity_score : 50.0;

  return (
    <div className="space-y-4">
      <SectionTitle>Tìm kiếm xu hướng đa nền tảng (Omnichannel Trend Discovery)</SectionTitle>

      <div>
        <label className="block text-sm font-semibold mb-1">Nền tảng tìm kiếm (Platform)</label>
        <select
          className="w-full px-2 py-1.5 border rounded text-sm bg-white"
          value={platform}
          onChange={(e) => updateConfig({ platform: e.target.value })}
        >
          <option value="tiktok">🎵 TikTok Trending</option>
          <option value="youtube_shorts">▶️ YouTube Shorts</option>
          <option value="douyin">🐼 Douyin (Tik Tok Trung Quốc)</option>
          <option value="facebook">📘 Facebook Reels</option>
          <option value="instagram">📸 Instagram Reels</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Hệ thống quét các video ngắn có tốc độ tăng trưởng tương tác đột biến trên nền tảng đã chọn.
        </p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold">Từ khoá hoặc Hashtag tìm kiếm (Mỗi dòng một từ khoá)</label>
        <textarea
          key={`trend-kws-${node.id}`}
          defaultValue={keywordsText}
          onBlur={(e) => {
            const keywords = e.target.value.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);
            updateConfig({ keywords });
          }}
          className="w-full p-2 border rounded font-mono text-sm"
          rows={4}
          placeholder="#viral&#10;kinh doanh&#10;cong nghe&#10;lam dep"
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold">Ngành hàng / Danh mục (Category)</label>
        <input
          type="text"
          defaultValue={config.category || 'entertainment'}
          onBlur={(e) => updateConfig({ category: e.target.value.trim() })}
          className="w-full p-2 border rounded text-sm"
          placeholder="VD: entertainment, tech, beauty, finance, ecommerce"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-semibold mb-1">Số video tối đa</label>
          <input
            type="number"
            min={1}
            max={50}
            defaultValue={maxResults}
            onBlur={(e) => updateConfig({ max_results: parseInt(e.target.value, 10) || 10 })}
            className="w-full p-2 border rounded text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1">Khung giờ quét (Giờ)</label>
          <input
            type="number"
            min={6}
            max={720}
            defaultValue={timeWindow}
            onBlur={(e) => updateConfig({ time_window_hours: parseInt(e.target.value, 10) || 72 })}
            className="w-full p-2 border rounded text-sm"
          />
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold">Điểm bứt phá tối thiểu (Min Velocity Score)</label>
        <input
          type="number"
          step="5"
          min={0}
          max={100}
          defaultValue={minVelocity}
          onBlur={(e) => updateConfig({ min_velocity_score: parseFloat(e.target.value) || 50.0 })}
          className="w-full p-2 border rounded text-sm"
        />
        <p className="text-xs text-gray-500">
          Chỉ giữ lại các nội dung có tốc độ lan truyền (likes/shares/views theo giờ) đạt ngưỡng lọc.
        </p>
      </div>

      <div className="space-y-3 pt-2 border-t">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1"
            checked={config.auto_expand_hashtags !== false}
            onChange={(e) => updateConfig({ auto_expand_hashtags: e.target.checked })}
          />
          <div>
            <span className="text-sm font-semibold">Tự động mở rộng Hashtag xu hướng (Trending Hashtags)</span>
            <p className="text-xs text-gray-500">
              Tự động gom thêm các hashtag thịnh hành của ngành hàng ({config.category || 'marketing'}) để radar quét rộng và trúng xu hướng hơn.
            </p>
          </div>
        </label>

        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1"
            checked={Boolean(config.early_viral_only)}
            onChange={(e) => updateConfig({ early_viral_only: e.target.checked })}
          />
          <div>
            <span className="text-sm font-semibold">Chỉ lọc video bùng nổ sớm (Early Breakout Detection)</span>
            <p className="text-xs text-gray-500">
              Chỉ đón đầu các video đang tăng tốc mạnh (&lt;48h phát hành, Engagement Velocity cao) thay vì video đã bão hòa hàng triệu views cũ.
            </p>
          </div>
        </label>
      </div>
    </div>
  );
};

const DownloadVideoPanel: React.FC<{ node: any; onChange: (cfg: any) => void }> = ({ node, onChange }) => {
  const config = node.data?.download_video_config || node.data?.config || {};
  const updateConfig = (updates: any) => {
    const updated = { ...config, ...updates };
    onChange({
      ...node.data,
      download_video_config: updated,
      config: updated,
    });
  };

  const urlsText = (config.urls || []).join('\n');
  const maxDuration = typeof config.max_duration === 'number' ? config.max_duration : 180;
  const resolution = config.resolution || '1080p';

  return (
    <div className="space-y-4">
      <SectionTitle>Tải Video Nguồn Không Watermark (yt-dlp)</SectionTitle>

      <div className="space-y-2">
        <label className="block text-sm font-semibold">Danh sách URLs trực tiếp (Tuỳ chọn)</label>
        <textarea
          key={`dl-urls-${node.id}`}
          defaultValue={urlsText}
          onBlur={(e) => {
            const urls = e.target.value.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);
            updateConfig({ urls });
          }}
          className="w-full p-2 border rounded font-mono text-sm"
          rows={4}
          placeholder="Để trống để tự động lấy video trending từ node upstream (find_trend_omnichannel)"
        />
        <p className="text-xs text-gray-500">
          Nếu để trống, node sẽ tự động nhận danh sách video được tìm thấy bởi node tìm trend phía trước.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-semibold mb-1">Độ phân giải mong muốn</label>
          <select
            className="w-full px-2 py-1.5 border rounded text-sm bg-white"
            value={resolution}
            onChange={(e) => updateConfig({ resolution: e.target.value })}
          >
            <option value="1080p">1080p (FHD - Đề xuất)</option>
            <option value="720p">720p (HD)</option>
            <option value="best">Chất lượng cao nhất (Best)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1">Thời lượng tối đa (Giây)</label>
          <input
            type="number"
            min={10}
            max={600}
            defaultValue={maxDuration}
            onBlur={(e) => updateConfig({ max_duration: parseInt(e.target.value, 10) || 180 })}
            className="w-full p-2 border rounded text-sm"
          />
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold">Thư mục lưu trữ tạm thời (Output Dir)</label>
        <input
          type="text"
          defaultValue={config.output_dir || ''}
          onBlur={(e) => updateConfig({ output_dir: e.target.value.trim() })}
          className="w-full p-2 border rounded text-sm"
          placeholder="Để trống để hệ thống tự quản lý trong thư mục tạm worker"
        />
      </div>
    </div>
  );
};

const AnalyzeViralContentPanel: React.FC<{ node: any; onChange: (cfg: any) => void }> = ({ node, onChange }) => {
  const config = node.data?.analyze_viral_content_config || node.data?.config || {};
  const updateConfig = (updates: any) => {
    const updated = { ...config, ...updates };
    onChange({
      ...node.data,
      analyze_viral_content_config: updated,
      config: updated,
    });
  };

  const targetLanguage = config.target_language || 'Tiếng Việt';

  return (
    <div className="space-y-4">
      <SectionTitle>Phân Tích Video Viral & Tạo Kịch Bản AI</SectionTitle>

      <div className="space-y-2">
        <label className="block text-sm font-semibold">Tên Thương hiệu (Brand Name)</label>
        <input
          type="text"
          defaultValue={config.brand_name || ''}
          onBlur={(e) => updateConfig({ brand_name: e.target.value.trim() })}
          className="w-full p-2 border rounded text-sm"
          placeholder="VD: BizTada Marketing, FPT Shop, Vinamilk..."
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold">Giọng điệu thương hiệu (Brand Tone)</label>
        <input
          type="text"
          defaultValue={config.brand_tone || 'friendly'}
          onBlur={(e) => updateConfig({ brand_tone: e.target.value.trim() })}
          className="w-full p-2 border rounded text-sm"
          placeholder="VD: Thân thiện, hài hước, chuyên nghiệp, truyền cảm hứng..."
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold">Khách hàng mục tiêu (Target Audience Persona)</label>
        <input
          type="text"
          defaultValue={config.target_audience || 'mass'}
          onBlur={(e) => updateConfig({ target_audience: e.target.value.trim() })}
          className="w-full p-2 border rounded text-sm"
          placeholder="VD: Gen Z đam mê công nghệ, chủ doanh nghiệp SME, người nội trợ..."
        />
      </div>

      <div>
        <label className="block text-sm font-semibold mb-1">Ngôn ngữ kịch bản đầu ra</label>
        <select
          className="w-full px-2 py-1.5 border rounded text-sm bg-white"
          value={targetLanguage}
          onChange={(e) => updateConfig({ target_language: e.target.value })}
        >
          <option value="Tiếng Việt">🇻🇳 Tiếng Việt</option>
          <option value="English">🇺🇸 English</option>
        </select>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold">Chỉ dẫn bổ sung cho AI (Custom Prompt)</label>
        <textarea
          key={`analyze-prompt-${node.id}`}
          defaultValue={config.custom_prompt || ''}
          onBlur={(e) => updateConfig({ custom_prompt: e.target.value.trim() })}
          className="w-full p-2 border rounded font-mono text-sm"
          rows={3}
          placeholder="VD: Nhấn mạnh vào yếu tố tiện ích, giữ hook mở đầu dưới 3 giây, kêu gọi comment..."
        />
      </div>
    </div>
  );
};

const AutoRepurposeVideoPanel: React.FC<{ node: any; onChange: (cfg: any) => void }> = ({ node, onChange }) => {
  const config = node.data?.auto_repurpose_video_config || node.data?.config || {};
  const updateConfig = (updates: any) => {
    const updated = { ...config, ...updates };
    onChange({
      ...node.data,
      auto_repurpose_video_config: updated,
      config: updated,
    });
  };

  const targetRatio = config.target_aspect_ratio || '9:16';
  const voice = config.voice || 'vi-VN-HoaiMyNeural';
  const burnSubtitles = config.burn_subtitles !== false;
  const bgMusicVolume = typeof config.bg_music_volume === 'number' ? config.bg_music_volume : 0.15;
  const maxDurationSec = typeof config.max_duration_sec === 'number' ? config.max_duration_sec : 90;

  return (
    <div className="space-y-4">
      <SectionTitle>Tự Động Sản Xuất Video (Auto Repurpose Video)</SectionTitle>

      <div>
        <label className="block text-sm font-semibold mb-1">Tỷ lệ khung hình (Aspect Ratio)</label>
        <select
          className="w-full px-2 py-1.5 border rounded text-sm bg-white"
          value={targetRatio}
          onChange={(e) => updateConfig({ target_aspect_ratio: e.target.value })}
        >
          <option value="9:16">📱 9:16 (TikTok, Reels, Shorts - Đề xuất)</option>
          <option value="1:1">⏹️ 1:1 (Vuông Feed)</option>
          <option value="16:9">🖥️ 16:9 (Ngang chuẩn Youtube)</option>
          <option value="original">🔄 Giữ nguyên tỷ lệ gốc</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-semibold mb-1">Giọng đọc lồng tiếng AI (Edge-TTS)</label>
        <select
          className="w-full px-2 py-1.5 border rounded text-sm bg-white"
          value={voice}
          onChange={(e) => updateConfig({ voice: e.target.value })}
        >
          <option value="vi-VN-HoaiMyNeural">Hoài My (Nữ miền Nam tự nhiên, trong trẻo)</option>
          <option value="vi-VN-NamMinhNeural">Nam Minh (Nam miền Nam ấm áp, truyền cảm)</option>
          <option value="vi-VN-PhuongNhiNeural">Phương Nhi (Nữ miền Bắc trẻ trung, năng động)</option>
          <option value="vi-VN-TuanKietNeural">Tuấn Kiệt (Nam miền Bắc đĩnh đạc, hiện đại)</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Kịch bản được đọc tự động lấy từ node phân tích viral (`analyze_viral_content`).
        </p>
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={burnSubtitles}
            onChange={(e) => updateConfig({ burn_subtitles: e.target.checked })}
            className="rounded border-gray-300 text-tg-primary focus:ring-tg-primary"
          />
          <span className="text-sm font-medium">Bật phụ đề động nổi bật (Kinetic Subtitles)</span>
        </label>
        <p className="text-xs text-gray-500">
          Tự động đồng bộ giọng nói và gắn phụ đề màu sắc bắt mắt ngay trên video.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-semibold mb-1">Âm lượng nhạc nền (Ducking)</label>
          <input
            type="number"
            step="0.05"
            min={0}
            max={1}
            defaultValue={bgMusicVolume}
            onBlur={(e) => updateConfig({ bg_music_volume: parseFloat(e.target.value) || 0.15 })}
            className="w-full p-2 border rounded text-sm"
          />
          <p className="text-xs text-gray-500 mt-1">Mặc định: 0.15 (15%)</p>
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1">Thời lượng tối đa (Giây)</label>
          <input
            type="number"
            min={15}
            max={300}
            defaultValue={maxDurationSec}
            onBlur={(e) => updateConfig({ max_duration_sec: parseInt(e.target.value, 10) || 90 })}
            className="w-full p-2 border rounded text-sm"
          />
        </div>
      </div>
    </div>
  );
};

const VaultUploadFilePanel: React.FC<{ node: any; onChange: (cfg: any) => void }> = ({ node, onChange }) => {
  const config = node.data?.vault_upload_file_config || node.data?.config || {};
  const updateConfig = (updates: any) => {
    const updated = { ...config, ...updates };
    onChange({
      ...node.data,
      vault_upload_file_config: updated,
      config: updated,
    });
  };

  const goal = config.goal || 'social';

  return (
    <div className="space-y-4">
      <SectionTitle>Tải Lên Thư Viện Vault Telegram (Vault Upload File)</SectionTitle>

      <div>
        <label className="block text-sm font-semibold mb-1">Mục tiêu tài nguyên (Goal)</label>
        <select
          className="w-full px-2 py-1.5 border rounded text-sm bg-white"
          value={goal}
          onChange={(e) => updateConfig({ goal: e.target.value })}
        >
          <option value="social">Nội dung mạng xã hội (Social Media)</option>
          <option value="marketing">Chiến dịch tiếp thị (Marketing)</option>
          <option value="business">Tài nguyên doanh nghiệp (Business)</option>
          <option value="game">Trò chơi / Nhân vật (Game/Character)</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Giúp phân loại chính xác tài sản khi xuất hiện trong Thư viện Content Vault của Brandlabs.
        </p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold">Tiêu đề tài sản (Asset Title)</label>
        <input
          type="text"
          defaultValue={config.title || ''}
          onBlur={(e) => updateConfig({ title: e.target.value.trim() })}
          className="w-full p-2 border rounded text-sm"
          placeholder="Để trống để tự động lấy tên video hoặc kịch bản"
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold">Mô tả tài sản (Description)</label>
        <textarea
          key={`vault-desc-${node.id}`}
          defaultValue={config.description || ''}
          onBlur={(e) => updateConfig({ description: e.target.value.trim() })}
          className="w-full p-2 border rounded font-mono text-sm"
          rows={3}
          placeholder="Ghi chú về nội dung video, nguồn gốc trend, chiến dịch sử dụng..."
        />
      </div>

      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
        💡 <strong>Tự động kết nối luồng:</strong> Node này tự động nhận file video thành phẩm từ node <code>auto_repurpose_video</code> đứng trước, upload qua MTProto và index trực tiếp vào Telegram Vault.
      </div>
    </div>
  );
};



